<template>
  <div class="project-compliance">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link to="/projects" class="breadcrumb-item">
        Projects
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">{{ project?.name || 'Project' }}</span>
    </div>

    <!-- Project Details Section -->
    <div class="project-header">
      <div class="project-info">
        <h2 class="page-title">{{ project?.name || 'Compliance' }}</h2>
        <div class="project-metadata">
          <span v-if="project?.default_comprehensive_check_name" class="check-badge">
            <i class="fas fa-clipboard-check"></i> {{ project.default_comprehensive_check_name }}
          </span>
          <span class="project-description">{{ project?.description }}</span>
        </div>
      </div>
      <div class="project-actions">
        <router-link :to="`/projects/${projectId}/settings`" class="btn btn-outline-primary">
          <i class="fas fa-cog"></i> Settings
        </router-link>
        <button @click="showUploadModal = true" class="btn btn-primary">
          <i class="fas fa-upload"></i> Upload Documents
        </button>
      </div>
    </div>

    <!-- Loading and Error States -->
    <div v-if="loading" class="loading">Loading compliance data...</div>
    <div v-else-if="error" class="error">{{ error }}</div>

    <!-- Compliance Intelligence Hub -->
    <div v-if="!loading && !error" class="intelligence-hub">
      
      <!-- Intelligence Overview Section -->
      <div class="intelligence-overview">
        <div class="overview-header">
          <h2><i class="fas fa-clipboard-check"></i> Compliance Overview</h2>
          <p class="overview-description">Track compliance status for all {{ entityTerm.plural.toLowerCase() }} in this project</p>
        </div>
        
        <div class="compliance-stats">
          <div class="stat-card total-subjects" :class="{ 'active': complianceSubjects.length > 0 }">
            <div class="stat-icon">
              <i class="fas fa-users"></i>
            </div>
            <div class="stat-content">
              <h3>{{ complianceSubjects.length }}</h3>
              <p>Total {{ entityTerm.plural }}</p>
              <div class="stat-status">
                <span v-if="complianceSubjects.length === 0" class="status pending">Ready to add subjects</span>
                <span v-else class="status active">Active monitoring</span>
              </div>
            </div>
          </div>
          
          <div class="stat-card compliant-count" :class="{ 'active': compliantCount > 0 }">
            <div class="stat-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="stat-content">
              <h3>{{ compliantCount }}</h3>
              <p>Compliant Entities</p>
              <div class="stat-status">
                <span v-if="complianceSubjects.length === 0" class="status disabled">No subjects yet</span>
                <span v-else class="status active">{{ Math.round((compliantCount / complianceSubjects.length) * 100) }}% compliance rate</span>
              </div>
            </div>
          </div>
          
          <div class="stat-card non-compliant-count" :class="{ 'active': nonCompliantCount > 0 }">
            <div class="stat-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="stat-content">
              <h3>{{ nonCompliantCount }}</h3>
              <p>Non-Compliant Entities</p>
              <div class="stat-status">
                <span v-if="nonCompliantCount === 0" class="status success">All compliant</span>
                <span v-else class="status warning">Requires attention</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Compliance Management Section -->
      <div class="intelligence-card compliance-management">
        <div class="section-header">
          <h2><i class="fas fa-users-cog"></i> {{ entityTerm.plural }} Management</h2>
          <p class="section-description">Track and manage compliance for all entities in your project</p>
        </div>

        <!-- Management Controls -->
        <div class="management-controls">
          <div class="controls-left">
            <!-- Search & Filter -->
            <div class="search-filter-group">
              <div class="search-box">
                <i class="fas fa-search"></i>
                <input 
                  type="text" 
                  v-model="searchQuery" 
                  :placeholder="`Search ${entityTerm.plural.toLowerCase()}...`" 
                  class="search-input"
                  @input="handleSearch"
                >
              </div>
              <select v-model="statusFilter" class="filter-select" @change="applyFilters">
                <option value="">All Active</option>
                <option value="COMPLIANT">Compliant</option>
                <option value="NON_COMPLIANT">Non-Compliant</option>
                <option value="PENDING">Pending</option>
                <option value="REVIEWING">Under Review</option>
                <option value="ARCHIVED">Archived</option>
                <option value="ALL">Show All (incl. Archived)</option>
              </select>
              <button @click="clearFilters" class="btn btn-outline-secondary btn-sm">
                <i class="fas fa-times"></i> Clear
              </button>
            </div>
          </div>
          
          <div class="controls-right">
            <!-- Bulk Actions -->
            <div class="bulk-actions" v-if="selectedSubjects.length > 0">
              <span class="selection-count">{{ selectedSubjects.length }} selected</span>
              <button @click="bulkRequestDocuments" class="btn btn-outline-primary btn-sm">
                <i class="fas fa-envelope"></i> Request Docs
              </button>
              <button @click="bulkAnalyze" class="btn btn-outline-success btn-sm">
                <i class="fas fa-play"></i> Run Analysis
              </button>
            </div>
            
            <!-- Add Subject Actions -->
            <div class="add-subject-actions">
              <button @click="openCreateFromCoiModal" class="btn btn-outline-secondary btn-sm" title="Create from COI">
                <i class="fas fa-certificate"></i> From COI
              </button>
              <button @click="openAddSubjectModal" class="btn btn-primary btn-sm">
                <i class="fas fa-plus"></i> Add {{ entityTerm.singular }}
              </button>
            </div>
          </div>
        </div>

        <!-- Results Summary -->
        <div class="results-summary" v-if="filteredSubjects.length !== complianceSubjects.length">
          <span class="summary-text">
            Showing {{ paginatedSubjects.length }} of {{ filteredSubjects.length }} subjects
            <span v-if="searchQuery || statusFilter">(filtered from {{ complianceSubjects.length }} total)</span>
          </span>
        </div>
        <!-- Subjects Table -->
        <div class="subjects-table-container">
          <!-- Loading Check -->
          <div v-if="loadingSubjects" class="loading-inline">
            <div class="spinner-sm"></div>
            <span>Loading subjects...</span>
          </div>
          
          <!-- Empty State Check -->
          <div v-else-if="!complianceSubjects || complianceSubjects.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-users"></i>
            </div>
            <h3>No {{ entityTerm.plural }} Yet</h3>
            <p>Get started by adding your first compliance subject to track their compliance status.</p>
            <button @click="openAddSubjectModal" class="btn btn-primary">
              <i class="fas fa-plus"></i> Add First Subject
            </button>
          </div>
          
          <!-- Scalable Table Rendering -->
          <div v-else class="table-wrapper">
            <table class="subjects-table modern-table">
                <thead>
                    <tr>
                        <th class="select-column">
                          <input 
                            type="checkbox" 
                            :checked="allSubjectsSelected" 
                            @change="toggleAllSubjects"
                            class="bulk-select-checkbox"
                          >
                        </th>
                        <th class="sortable" @click="sortBy('subject_entity_name')">
                          Subject Name
                          <i :class="getSortIcon('subject_entity_name')"></i>
                        </th>
                        <th class="sortable" @click="sortBy('current_status')">
                          Status
                          <i :class="getSortIcon('current_status')"></i>
                        </th>
                        <th class="sortable" @click="sortBy('first_analysis_date')">
                          Tracking Started
                          <i :class="getSortIcon('first_analysis_date')"></i>
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="subject in paginatedSubjects" :key="subject.id">
                        <tr class="subject-row" :class="{ 'selected': selectedSubjects.includes(subject.id) }">
                            <td class="select-column">
                              <input 
                                type="checkbox" 
                                :value="subject.id" 
                                v-model="selectedSubjects"
                                class="subject-select-checkbox"
                              >
                            </td>
                            <td class="subject-name">
                                <div class="subject-info">
                                  <i class="fas fa-user-tie subject-icon"></i>
                                  <span class="name">{{ subject.subject_entity_name }}</span>
                                </div>
                            </td>
                            <td class="status-column">
                                <span :class="['status-badge', `status-${subject.current_status.toLowerCase()}`]">
                                    {{ subject.current_status_display }}
                                </span>
                            </td>
                            <td class="date-column">{{ formatDateOnly(subject.first_analysis_date) || 'N/A' }}</td>
                            <td class="actions-column">
                                <div class="action-buttons">
                                    <button class="btn btn-xs btn-link text-secondary p-0 me-1" @click="viewSubjectDetails(subject)" title="View Subject Details">
                                        <i class="fas fa-eye"></i>
                                    </button>
                                    <button class="btn btn-xs btn-link text-secondary p-0 me-1" @click="openRequestDocsModal(subject)" title="Request Documents">
                                        <i class="fas fa-envelope"></i>
                                    </button>
                                    <button class="btn btn-xs btn-link text-secondary p-0 me-1" @click="openUploadModalForSubject(subject)" title="Upload Document for Subject">
                                        <i class="fas fa-upload"></i>
                                    </button>
                                    <!-- RENAMED Button and updated title - keeping its original btn-light for now, can adjust if needed -->
                                    <button class="btn btn-xs btn-light me-1" @click="viewSubjectDetails(subject, 'parties')" title="Manage Linked Agents / Parties">
                                        <i class="fas fa-briefcase"></i> <!-- Example icon -->
                                    </button>
                                    <!-- ADDED Toggle Contacts Button - keeping its original btn-light for now, can adjust if needed -->
                                    <button class="btn btn-xs btn-light" @click="toggleContacts(subject.subject_entity)" title="Show/Hide Contacts for Subject">
                                       <i :class="['fas', visibleContacts[subject.subject_entity] ? 'fa-users-slash' : 'fa-users']"></i>
                                    </button>
                                    <!-- Archive/Delete Subject Button -->
                                    <button class="btn btn-xs btn-link text-danger p-0 ms-1" @click="confirmArchiveSubject(subject)" title="Archive Subject">
                                       <i class="fas fa-archive"></i>
                                    </button>
                                 </div>
                            </td>
                        </tr>
                         <!-- Nested row for contacts (kept for backwards compatibility) -->
                         <tr v-if="visibleContacts[subject.subject_entity]" class="contacts-row-dashboard">
                            <td colspan="4">
                               <div class="contacts-section p-3 bg-light border rounded">
                                  <h5 class="mb-3">Contacts for {{ subject.subject_entity_name }}</h5>
                                  <div v-if="loadingContactsFor === subject.subject_entity">
                                      <span class="spinner-sm me-1"></span> Loading contacts...
                                  </div>
                                  <div v-else-if="!entityContacts[subject.subject_entity] || entityContacts[subject.subject_entity].length === 0">
                                      <p class="mb-2 text-muted">No contacts found for this entity.</p>
                                  </div>
                                  <ul v-else class="list-unstyled mb-3">
                                      <!-- Apply more specific key -->
                                      <li v-for="contact in entityContacts[subject.subject_entity]" :key="`subject-contact-${contact.id}`" class="contact-item d-flex justify-content-between align-items-center mb-1">
                                          <span>
                                              <i class="fas fa-user me-1"></i> 
                                              <strong>{{ contact.name }}</strong> ({{ contact.email }}) - {{ contact.title || 'No Title' }}
                                              <span v-if="contact.is_primary" class="badge bg-secondary ms-1">Primary</span>
                                          </span>
                                          <span>
                                              <!-- TODO: Add Edit/Delete contact buttons -->
                                              <button class="btn btn-xs btn-link text-secondary" title="Edit Contact"><i class="fas fa-edit"></i></button>
                                              <button @click="deleteContact(contact.id, subject.subject_entity)" class="btn btn-xs btn-link text-danger" title="Delete Contact"><i class="fas fa-trash"></i></button>
                                          </span>
                                      </li>
                                  </ul>
                                  
                                  <!-- Add New Contact Form/Toggle -->
                                  <div class="add-contact-section mt-2">
                                      <button v-if="!showAddContactForm[subject.subject_entity]" @click="openAddContactForm(subject.subject_entity)" class="btn btn-sm btn-outline-primary">
                                          <i class="fas fa-plus"></i> Add New Contact
                                      </button>
                                      <form v-if="showAddContactForm[subject.subject_entity]" @submit.prevent="addContact(subject.subject_entity)" class="mt-3 border p-3 rounded bg-white shadow-sm">
                                          <h6>New Contact for {{ subject.subject_entity_name }}</h6>
                                           <div v-if="addContactError[subject.subject_entity]" class="alert alert-danger alert-sm p-2 mb-2">
                                               {{ addContactError[subject.subject_entity] }}
                                           </div>
                                          <div class="row gx-2">
                                              <div class="col-md-6 form-group mb-2">
                                                  <label :for="`contact-name-${subject.subject_entity}`" class="form-label form-label-sm">Name *</label>
                                                  <input type="text" :id="`contact-name-${subject.subject_entity}`" v-model="newContactFormData[subject.subject_entity].name" class="form-control form-control-sm" required>
                                              </div>
                                              <div class="col-md-6 form-group mb-2">
                                                  <label :for="`contact-email-${subject.subject_entity}`" class="form-label form-label-sm">Email *</label>
                                                  <input type="email" :id="`contact-email-${subject.subject_entity}`" v-model="newContactFormData[subject.subject_entity].email" class="form-control form-control-sm" required>
                                              </div>
                                          </div>
                                           <div class="row gx-2">
                                              <div class="col-md-6 form-group mb-2">
                                                  <label :for="`contact-phone-${subject.subject_entity}`" class="form-label form-label-sm">Phone</label>
                                                  <input type="tel" :id="`contact-phone-${subject.subject_entity}`" v-model="newContactFormData[subject.subject_entity].phone" class="form-control form-control-sm">
                                              </div>
                                               <div class="col-md-6 form-group mb-2">
                                                  <label :for="`contact-title-${subject.subject_entity}`" class="form-label form-label-sm">Title</label>
                                                  <input type="text" :id="`contact-title-${subject.subject_entity}`" v-model="newContactFormData[subject.subject_entity].title" class="form-control form-control-sm">
                                              </div>
                                          </div>
                                          <div class="form-check mb-2">
                                              <input type="checkbox" :id="`contact-primary-${subject.subject_entity}`" v-model="newContactFormData[subject.subject_entity].is_primary" class="form-check-input">
                                              <label :for="`contact-primary-${subject.subject_entity}`" class="form-check-label form-check-label-sm">Set as Primary Contact</label>
                                          </div>
                                          <div>
                                              <button type="submit" class="btn btn-sm btn-success" :disabled="isSavingContact[subject.subject_entity]">
                                                 <span v-if="isSavingContact[subject.subject_entity]" class="spinner-sm me-1"></span>
                                                 Save Contact
                                              </button>
                                              <button type="button" @click="cancelAddContact(subject.subject_entity)" class="btn btn-sm btn-secondary ms-2">Cancel</button>
                                          </div>
                                      </form>
                                  </div>
                               </div>
                            </td>
                         </tr>
                        <!-- ADDED: Subject Detail Row -->
                        <tr v-if="visibleSubjectDetails[subject.id]" class="subject-detail-row">
                            <td colspan="5" class="p-0"> <!-- Full width across all columns -->
                                <!-- Use a card structure for better visual separation -->
                                <div class="subject-detail-card card card-body p-3 bg-light border-top-0 rounded-0">

                                    <!-- Section 1: Subject Info & Context (Remains above tabs) -->
                                    <div class="subject-context mb-2"> 
                                        <h5 class="mb-1">Details for: {{ subject.subject_entity_name }}</h5>
                                        <div class="summary-details d-flex flex-wrap align-items-baseline text-muted small mt-1" style="gap: 1rem;">
                                             <div class="summary-item me-4 mb-1"> 
                                                 <i class="fas fa-play-circle fa-fw me-1" title="Tracking Started"></i>
                                                 <span class="summary-label">Started:</span>
                                                 <span class="summary-value">{{ formatDateOnly(subject.first_analysis_date) || 'N/A' }}</span> 
                                             </div>
                                             <div class="summary-item me-4 mb-1">
                                                 <i class="fas fa-tasks fa-fw me-1" title="Next Steps/Gaps"></i>
                                                 <span class="summary-label">Gaps:</span>
                                                  <!-- Placeholder - Use a more descriptive badge later -->
                                                 <span class="summary-value badge bg-warning text-dark">Review Failures</span>
                                             </div>
                                             <div class="summary-item mb-1"> 
                                                 <i class="fas fa-history fa-fw me-1" title="Last Status Change"></i>
                                                 <span class="summary-label">Updated:</span>
                                                 <!-- Will reflect status changes later -->
                                                 <span class="summary-value">{{ formatDateOnly(subject.status_last_updated) }}</span> 
                                             </div>
                                        </div>
                                    </div>
                                    <hr class="mt-1 mb-3">

                                    <!-- Internal Tabs Navigation - Changed classes -->
                                    <ul class="subject-detail-tabs nav mb-3"> <!-- Use custom class, keep nav for structure -->
                                        <li class="nav-item">
                                            <button :class="['nav-link', { active: activeDetailTab[subject.id] === 'status' }]" @click="activeDetailTab[subject.id] = 'status'">Compliance Status</button>
                                        </li>
                                        <li class="nav-item">
                                            <button :class="['nav-link', { active: activeDetailTab[subject.id] === 'parties' }]" @click="openPartiesTab(subject.id)">Associated Parties</button>
                                        </li>
                                        <li class="nav-item">
                                            <button :class="['nav-link', { active: activeDetailTab[subject.id] === 'history' }]" @click="activeDetailTab[subject.id] = 'history'">Analysis History</button>
                                        </li>
                                        <li class="nav-item">
                                            <button :class="['nav-link', { active: activeDetailTab[subject.id] === 'agent' }]" @click="activeDetailTab[subject.id] = 'agent'; loadAgentEvaluation(subject.id)">Agent Intelligence</button>
                                        </li>
                                        <li class="nav-item">
                                            <button :class="['nav-link', { active: activeDetailTab[subject.id] === 'coverage' }]" @click="activeDetailTab[subject.id] = 'coverage'; loadCoverageSummary(subject.id)">Coverage Matrix</button>
                                            <button :class="['nav-link', { active: activeDetailTab[subject.id] === 'response' }]" @click="activeDetailTab[subject.id] = 'response'; loadResponseIntelligence(subject.id)">Response Intelligence</button>
                                        </li>
                                    </ul>

                                    <!-- Tab Content -->
                                    <div class="subject-detail-tab-content">
                                        <!-- Compliance Status Tab -->
                                        <div v-if="activeDetailTab[subject.id] === 'status'">
                                            <!-- Section 2: Requirements Status -->
                                            <h5>Compliance Requirements Status</h5> <!-- Changed to H5 -->
                                            <div v-if="loadingSubjectDetailedStatus[subject.id]" class="loading-inline small">
                                                <span class="spinner-sm me-1"></span> Loading results...
                                            </div>
                                            <div v-else-if="subjectDetailedStatus[subject.id] && subjectDetailedStatus[subject.id].length > 0" class="requirements-results mt-2">
                                                <table class="requirements-table requirements-table-inline">
                                                    <thead>
                                                        <tr>
                                                            <th>Requirement</th>
                                                            <th>Severity</th>
                                                            <th>Result</th>
                                                            <th>Evidence</th>
                                                            <th>Feedback</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody v-if="Array.isArray(subjectDetailedStatus[subject.id])"> 
                                                      <!-- Iterate over SORTED detailed status -->
                                                      <tr v-for="item in sortedRequirementStatus(subject.id)" :key="item.requirement.id" :class="{ 'failed-row': item.latest_result && !getResultPassed(item.latest_result) }">
                                                        <td>
                                                          <div class="requirement-identifier">
                                                            <strong>{{ item.requirement.name }}</strong>
                                                            <div class="req-details">
                                                              <span class="req-code">{{ item.requirement.code || 'req_' + item.requirement.id }}</span>
                                                              <span class="req-description">{{ item.requirement.description?.substring(0, 80) }}{{ item.requirement.description?.length > 80 ? '...' : '' }}</span>
                                                            </div>
                                                          </div>
                                                        </td>
                                                        <td>
                                                          <!-- Display severity from definition -->
                                                          <span :class="['severity-badge', `severity-${(item.requirement.severity?.toLowerCase()) ?? 'unknown'}`]">
                                                            {{ item.requirement.severity || 'N/A' }}
                                                          </span>
                                                        </td>
                                                        <td>
                                                          <!-- Enhanced result display logic -->
                                                          <span v-if="item.latest_result" 
                                                                :class="['result-badge', getResultBadgeClass(item.latest_result)]"
                                                                :title="getResultTooltip(item.latest_result)">
                                                            {{ getResultDisplayText(item.latest_result) }}
                                                          </span>
                                                          <span v-else class="result-badge result-pending">
                                                            PENDING
                                                          </span>
                                                        </td>
                                                        <td class="evidence-cell">
                                                            <!-- Enhanced evidence display -->
                                                            <div v-if="item.latest_result" class="evidence-container">
                                                              <div class="evidence-text" :title="getFullEvidence(item.latest_result)">
                                                                {{ getEvidenceText(item.latest_result) }}
                                                              </div>
                                                              <!-- Show enhanced information if available -->
                                                              <div v-if="hasEnhancedData(item.latest_result)" class="enhanced-details mt-1">
                                                                <small class="text-muted">
                                                                  <i class="fas fa-info-circle me-1"></i>
                                                                  <span v-if="getConfidence(item.latest_result)">
                                                                    Confidence: {{ Math.round(getConfidence(item.latest_result) * 100) }}%
                                                                  </span>
                                                                  <button v-if="hasAdditionalInfo(item.latest_result)" 
                                                                          @click="toggleResultDetails(item.latest_result.id)"
                                                                          class="btn btn-xs btn-link p-0 ms-1"
                                                                          title="Show detailed analysis">
                                                                    <i :class="['fas', expandedResults[item.latest_result.id] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                                  </button>
                                                                </small>
                                                              </div>
                                                              <!-- Expanded details -->
                                                              <div v-if="expandedResults[item.latest_result.id]" class="expanded-result-details mt-2 p-2 bg-light border rounded">
                                                                <div v-if="getReasoning(item.latest_result)" class="reasoning mb-2">
                                                                  <strong>Analysis:</strong> {{ getReasoning(item.latest_result) }}
                                                                </div>
                                                                <div v-if="getDeficiencies(item.latest_result).length > 0" class="deficiencies mb-2">
                                                                  <strong>Issues:</strong>
                                                                  <ul class="mb-0 mt-1">
                                                                    <li v-for="deficiency in getDeficiencies(item.latest_result)" :key="deficiency">
                                                                      {{ deficiency }}
                                                                    </li>
                                                                  </ul>
                                                                </div>
                                                                <div v-if="getSuggestions(item.latest_result).length > 0" class="suggestions">
                                                                  <strong>Recommendations:</strong>
                                                                  <ul class="mb-0 mt-1">
                                                                    <li v-for="suggestion in getSuggestions(item.latest_result)" :key="suggestion">
                                                                      {{ suggestion }}
                                                                    </li>
                                                                  </ul>
                                                                </div>
                                                              </div>
                                                            </div>
                                                            <span v-else>--</span>
                                                        </td>
                                                        <td class="feedback-cell">
                                                           <!-- Show buttons only if a latest_result exists -->
                                                           <template v-if="item.latest_result">
                                                             <button 
                                                               @click="submitRequirementFeedback(item.latest_result.id, subject.id, 'ACCURATE')" 
                                                               :class="['feedback-btn', { active: item.latest_result.user_feedback_accuracy === 'ACCURATE' }]" 
                                                               :disabled="item.latest_result.user_feedback_accuracy && item.latest_result.user_feedback_accuracy !== 'NONE'" 
                                                               title="Mark as Accurate">
                                                               <i class="fas fa-thumbs-up"></i>
                                                             </button>
                                                             <button 
                                                               @click="submitRequirementFeedback(item.latest_result.id, subject.id, 'INACCURATE')" 
                                                               :class="['feedback-btn', { active: item.latest_result.user_feedback_accuracy === 'INACCURATE' }]" 
                                                               :disabled="item.latest_result.user_feedback_accuracy && item.latest_result.user_feedback_accuracy !== 'NONE'" 
                                                               title="Mark as Inaccurate">
                                                               <i class="fas fa-thumbs-down"></i>
                                                             </button>
                                                             
                                                             <!-- Override Buttons - Only show if feedback is INACCURATE -->
                                                             <div v-if="item.latest_result.user_feedback_accuracy === 'INACCURATE'" class="override-buttons mt-1">
                                                               <button 
                                                                 @click="openOverrideModal(item.latest_result, subject.id)"
                                                                 class="btn btn-xs btn-warning override-btn"
                                                                 title="Override Result">
                                                                 <i class="fas fa-edit"></i> Override
                                                               </button>
                                                             </div>
                                                           </template>
                                                           <span v-else class="hint">--</span>
                                                        </td>
                                                      </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div v-else class="empty-state-small mt-2 text-muted fst-italic">
                                               (No completed analysis results found for this subject)
                                            </div>
                                        </div>

                                        <!-- Parties Tab -->
                                        <div v-if="activeDetailTab[subject.id] === 'parties'">
                                            <!-- Section 3: Associated Parties & Relationships -->
                                            <h5>Associated Parties & Relationships</h5>
                                            <div class="parties-relationships-summary mt-2 mb-3"> 
                                                <table class="table table-sm requirements-table requirements-table-inline parties-compliance-table mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Entity</th>
                                                            <th>Relationships</th>
                                                            <th>Status Summary</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <!-- Loading state -->
                                                        <tr v-if="loadingFormalAgents[subject.id] || loadingSubjectParties[subject.id]">
                                                             <td colspan="4" class="text-center text-muted small py-2">
                                                                  <span class="spinner-sm me-1"></span> Loading entity relationships...
                                                             </td>
                                                        </tr>
                                                        
                                                        <!-- Iterate through all entities grouped by entity -->
                                                        <template v-for="entity in getGroupedEntities(subject.id)" :key="entity.entityId">
                                                            <!-- Entity Row -->
                                                            <tr>
                                                                <td>
                                                                    <div class="entity-info">
                                                                        <div class="entity-main">
                                                                            <i :class="['me-2', entity.icon]"></i>
                                                                            <strong>{{ entity.name }}</strong>
                                                                            <span v-if="entity.isPrimary" class="badge bg-primary ms-2">Primary Subject</span>
                                                                        </div>
                                                                        <div v-if="entity.primaryContact" class="entity-contact text-muted small mt-1">
                                                                            <i class="fas fa-user me-1"></i>
                                                                            <strong>{{ entity.primaryContact.name }}</strong>
                                                                            <span v-if="entity.primaryContact.email"> ({{ entity.primaryContact.email }})</span>
                                                                            <span v-if="entity.primaryContact.title"> - {{ entity.primaryContact.title }}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div class="relationships-list">
                                                                        <span v-for="(relationship, index) in entity.relationships" 
                                                                              :key="index"
                                                                              :class="['relationship-badge', `relationship-${relationship.type.toLowerCase()}`]">
                                                                            {{ relationship.label }}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <!-- Status indicators based on entity role -->
                                                                    <div class="status-indicators">
                                                                        <span v-if="entity.isPrimary" :class="['result-badge', subject.current_status === 'COMPLIANT' ? 'result-passed' : 'result-failed']" title="Overall Subject Status">
                                                                            {{ subject.current_status_display }}
                                                                        </span>
                                                                        <span v-if="entity.documentCount > 0" class="badge bg-light text-dark me-1" :title="`${entity.documentCount} Documents`">
                                                                            <i class="fas fa-file-alt"></i> {{ entity.documentCount }}
                                                                        </span>
                                                                        <span v-if="!entity.isPrimary" class="badge bg-light text-dark" title="Status tracking not available for this entity type">
                                                                            Status N/A
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div class="entity-actions">
                                                                        <!-- Remove formal agent link (only for formal agents) -->
                                                                        <button v-if="entity.isFormalAgent"
                                                                            @click="removeFormalAgent(subject, entity.entityId)"
                                                                            class="btn btn-xs btn-link text-danger p-0 me-1"
                                                                            :disabled="isSavingAgent[subject.id]"
                                                                            title="Remove Agent Link">
                                                                             <i class="fas fa-unlink"></i>
                                                                         </button>
                                                                         <!-- View entity details -->
                                                                         <button @click="viewPartyDetails(entity.entityId)" 
                                                                                 class="btn btn-xs btn-link text-secondary p-0 me-1" 
                                                                                 title="View Entity Details">
                                                                             <i class="fas fa-eye"></i>
                                                                         </button>
                                                                         <!-- Manage contacts -->
                                                                         <button @click="toggleContacts(entity.entityId)" 
                                                                                 class="btn btn-xs btn-link text-primary p-0" 
                                                                                 title="Manage Contacts">
                                                                            <i class="fas fa-address-book"></i>
                                                                          </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            
                                                            <!-- Nested row for contacts (when expanded) -->
                                                             <tr v-if="visibleContacts[entity.entityId]" class="contacts-row-dashboard">
                                                                 <td colspan="4">
                                                                     <div class="contacts-section p-3 bg-light border rounded mt-n1 mb-2">
                                                                         <h6 class="mb-3">Contacts for {{ entity.name }}</h6>
                                                                         <div v-if="loadingContactsFor === entity.entityId" class="loading-inline small">
                                                                             <span class="spinner-sm me-1"></span> Loading contacts...
                                                                         </div>
                                                                         <template v-else>
                                                                             <div v-if="!entityContacts[entity.entityId] || entityContacts[entity.entityId].length === 0" class="empty-state-small text-muted fst-italic">
                                                                                 No contacts found for this entity.
                                                                             </div>
                                                                             <ul v-else class="list-unstyled mb-3">
                                                                                 <li v-for="contact in entityContacts[entity.entityId]" :key="`entity-${entity.entityId}-contact-${contact.id}`" class="contact-item d-flex justify-content-between align-items-center mb-1">
                                                                                     <span>
                                                                                         <i class="fas fa-user me-1"></i>
                                                                                         <strong>{{ contact.name }}</strong> ({{ contact.email }}) - {{ contact.title || 'No Title' }}
                                                                                         <span v-if="contact.is_primary" class="badge bg-secondary ms-1">Primary</span>
                                                                                     </span>
                                                                                     <span>
                                                                                         <button class="btn btn-xs btn-link text-secondary" title="Edit Contact"><i class="fas fa-edit"></i></button>
                                                                                         <button @click="deleteContact(contact.id, entity.entityId)" class="btn btn-xs btn-link text-danger" title="Delete Contact"><i class="fas fa-trash"></i></button>
                                                                                     </span>
                                                                                 </li>
                                                                             </ul>
                                                                             <div class="add-contact-section mt-2">
                                                                                 <button v-if="!showAddContactForm[entity.entityId]" @click="openAddContactForm(entity.entityId)" class="btn btn-sm btn-outline-primary">
                                                                                     <i class="fas fa-plus"></i> Add New Contact for {{ entity.name }}
                                                                                 </button>
                                                                                 <form v-if="showAddContactForm[entity.entityId]" @submit.prevent="addContact(entity.entityId)" class="mt-3 border p-3 rounded bg-white shadow-sm">
                                                                                     <h6>New Contact for {{ entity.name }}</h6>
                                                                                     <div v-if="addContactError[entity.entityId]" class="alert alert-danger alert-sm p-2 mb-2">
                                                                                         {{ addContactError[entity.entityId] }}
                                                                                     </div>
                                                                                     <div class="row gx-2">
                                                                                         <div class="col-md-6 form-group mb-2">
                                                                                             <label :for="`contact-name-${entity.entityId}`" class="form-label form-label-sm">Name *</label>
                                                                                             <input type="text" :id="`contact-name-${entity.entityId}`" v-model="newContactFormData[entity.entityId].name" class="form-control form-control-sm" required>
                                                                                         </div>
                                                                                         <div class="col-md-6 form-group mb-2">
                                                                                             <label :for="`contact-email-${entity.entityId}`" class="form-label form-label-sm">Email *</label>
                                                                                             <input type="email" :id="`contact-email-${entity.entityId}`" v-model="newContactFormData[entity.entityId].email" class="form-control form-control-sm" required>
                                                                                         </div>
                                                                                     </div>
                                                                                     <div class="row gx-2">
                                                                                         <div class="col-md-6 form-group mb-2">
                                                                                             <label :for="`contact-phone-${entity.entityId}`" class="form-label form-label-sm">Phone</label>
                                                                                             <input type="tel" :id="`contact-phone-${entity.entityId}`" v-model="newContactFormData[entity.entityId].phone" class="form-control form-control-sm">
                                                                                         </div>
                                                                                         <div class="col-md-6 form-group mb-2">
                                                                                             <label :for="`contact-title-${entity.entityId}`" class="form-label form-label-sm">Title</label>
                                                                                             <input type="text" :id="`contact-title-${entity.entityId}`" v-model="newContactFormData[entity.entityId].title" class="form-control form-control-sm">
                                                                                         </div>
                                                                                     </div>
                                                                                     <div class="form-check mb-2">
                                                                                         <input type="checkbox" :id="`contact-primary-${entity.entityId}`" v-model="newContactFormData[entity.entityId].is_primary" class="form-check-input">
                                                                                         <label :for="`contact-primary-${entity.entityId}`" class="form-check-label form-check-label-sm">Set as Primary Contact</label>
                                                                                     </div>
                                                                                     <div>
                                                                                         <button type="submit" class="btn btn-sm btn-success" :disabled="isSavingContact[entity.entityId]">
                                                                                            <span v-if="isSavingContact[entity.entityId]" class="spinner-sm me-1"></span>
                                                                                            Save Contact
                                                                                         </button>
                                                                                         <button type="button" @click="cancelAddContact(entity.entityId)" class="btn btn-sm btn-secondary ms-2">Cancel</button>
                                                                                     </div>
                                                                                 </form>
                                                                             </div>
                                                                         </template>
                                                                     </div>
                                                                 </td>
                                                             </tr>
                                                        </template>
                                                        
                                                        <!-- Row for adding a new agent -->
                                                         <tr class="add-agent-row" style="background-color: #f8f9fa; border-top: 2px solid #dee2e6;">
                                                              <td colspan="2" class="pt-2 pb-2 align-middle">
                                                                   <select v-model="selectedAgentToAdd[subject.id]" class="form-select form-select-sm" :disabled="isSavingAgent[subject.id]">
                                                                       <option value="" disabled>-- Select Existing Agency/Broker --</option>
                                                                       <option v-for="entity in availableEntitiesForAgentSelection(subject.id)" :key="entity.id" :value="entity.id">
                                                                           {{ entity.name }} ({{ entity.entity_type }})
                                                                       </option>
                                                                   </select>
                                                                   <div v-if="availableEntitiesForAgentSelection(subject.id).length === 0 && !loadingFormalAgents[subject.id]" class="form-text small text-muted mt-1">
                                                                       No available Agency/Broker entities to add.
                                                                   </div>
                                                              </td>
                                                              <td class="pt-2 pb-2 align-middle">
                                                                   <button
                                                                       @click="addFormalAgent(subject)"
                                                                       class="btn btn-sm btn-outline-primary w-100"
                                                                       :disabled="!selectedAgentToAdd[subject.id] || isSavingAgent[subject.id]">
                                                                       <span v-if="isSavingAgent[subject.id]" class="spinner-sm me-1"></span>
                                                                       Add Selected
                                                                   </button>
                                                              </td>
                                                              <td class="pt-2 pb-2 align-middle text-center">
                                                                  <span class="text-muted small me-1">OR</span>
                                                                   <button
                                                                      @click="toggleAddAgentForm(subject.id)"
                                                                      class="btn btn-sm btn-outline-success"
                                                                      :disabled="isSavingNewAgent[subject.id]"
                                                                      title="Create a new Agency/Broker entity and link it">
                                                                       <i class="fas fa-plus me-1"></i> Create & Add New Agency
                                                                   </button>
                                                              </td>
                                                         </tr>
                                                         
                                                         <!-- Inline Form for New Agent -->
                                                         <tr v-if="showAddAgentForm[subject.id]" class="new-agent-form-row">
                                                              <td colspan="4" class="p-2">
                                                                  <div class="border rounded p-3 bg-white shadow-sm">
                                                                    <h6 class="mb-3">Create New Agency/Broker</h6>
                                                                     <div v-if="newAgentError[subject.id]" class="alert alert-danger alert-sm p-2 mb-2">
                                                                         {{ newAgentError[subject.id] }}
                                                                     </div>
                                                                    <div class="row gx-2">
                                                                        <div class="col-md-5 form-group mb-2">
                                                                            <label :for="`new-agent-name-${subject.id}`" class="form-label form-label-sm">Agency Name *</label>
                                                                            <input type="text" :id="`new-agent-name-${subject.id}`" v-model="newAgentFormData[subject.id].name" class="form-control form-control-sm" required>
                                                                        </div>
                                                                        <div class="col-md-4 form-group mb-2">
                                                                            <label :for="`new-agent-email-${subject.id}`" class="form-label form-label-sm">Primary Email</label>
                                                                            <input type="email" :id="`new-agent-email-${subject.id}`" v-model="newAgentFormData[subject.id].primary_email" class="form-control form-control-sm">
                                                                        </div>
                                                                        <div class="col-md-3 form-group mb-2">
                                                                            <label :for="`new-agent-phone-${subject.id}`" class="form-label form-label-sm">Primary Phone</label>
                                                                            <input type="tel" :id="`new-agent-phone-${subject.id}`" v-model="newAgentFormData[subject.id].primary_phone" class="form-control form-control-sm">
                                                                        </div>
                                                                    </div>
                                                                    <div class="mt-2 d-flex justify-content-end">
                                                                        <button 
                                                                            type="button" 
                                                                            @click="saveNewAgent(subject)" 
                                                                            class="btn btn-sm btn-success" 
                                                                            :disabled="isSavingNewAgent[subject.id] || !newAgentFormData[subject.id]?.name">
                                                                            <span v-if="isSavingNewAgent[subject.id]" class="spinner-sm me-1"></span>
                                                                            Save New Agency
                                                                        </button>
                                                                        <button 
                                                                            type="button" 
                                                                            @click="toggleAddAgentForm(subject.id)" 
                                                                            class="btn btn-sm btn-secondary ms-2">Cancel</button>
                                                                    </div>
                                                                  </div>
                                                              </td>
                                                         </tr>
                                                        <!-- END Other Parties -->
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <!-- History Tab -->
                                        <div v-if="activeDetailTab[subject.id] === 'history'">
                                            <!-- Section 4: Analysis History -->
                                            <h5 class="mt-2">Analysis History</h5> <!-- Changed to H5 -->
                                             <div v-if="loadingSubjectCheckRuns[subject.id]" class="loading-inline small">
                                                 <span class="spinner-sm me-1"></span> Loading analysis history...
                                             </div>
                                             <div v-else-if="subjectCheckRuns[subject.id] && subjectCheckRuns[subject.id].length > 0" class="analysis-history-table-container mt-2">
                                                 <table class="table table-sm analysis-history-table">
                                                    <thead>
                                                      <tr>
                                                        <!-- Removed Document Column -->
                                                        <th>Template / Document</th> <!-- Combined Column -->
                                                        <th>Date</th>
                                                        <th>Status</th>
                                                        <th>Outcome</th>
                                                        <th>Actions</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      <tr v-for="run in subjectCheckRuns[subject.id]" :key="run.id">
                                                        <td>
                                                            <div>
                                                              {{ run.check_definition_name }}
                                                              <span v-if="run.run_metadata?.document_types?.length" class="run-doc-type-badge">
                                                                {{ run.run_metadata.document_types[0] }}
                                                              </span>
                                                            </div>
                                                            <!-- Show Document Name underneath -->
                                                            <div class="text-muted small">
                                                                 <i class="fas fa-file-alt me-1"></i>
                                                                {{ documentNames[run.document] || 'Unknown Doc' }}
                                                                <!-- Add View/Download Buttons Here -->
                                                                <span class="document-actions-inline">
                                                                  <button @click="handleDownload(documentObjects[run.document] || { id: run.document, file: documentFilePaths[run.document], name: documentNames[run.document] })" class="doc-action-btn" v-if="documentObjects[run.document] || documentFilePaths[run.document]" title="Download Document" :disabled="downloading">
                                                                    <i :class="downloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                                                                  </button>
                                                                  <button @click="viewDocument(documentObjects[run.document] || { id: run.document, file: documentFilePaths[run.document] })" class="doc-action-btn" v-if="documentObjects[run.document] || documentFilePaths[run.document]" title="View Document">
                                                                    <i class="fas fa-eye"></i>
                                                                  </button>
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>{{ formatDate(run.created_at) }}</td>
                                                        <td>
                                                          <span :class="['status-badge', `status-${run.status.toLowerCase()}`]">
                                                            {{ formatStatus(run.status) }}
                                                          </span>
                                                        </td>
                                                        <td>
                                                           <span v-if="run.outcome" :class="['status-badge', `outcome-${run.outcome.toLowerCase()}`]">
                                                             {{ formatOutcome(run.outcome) }}
                                                           </span>
                                                           <span v-else class="hint">--</span>
                                                        </td>
                                                        <td class="actions-cell">
                                                          <button 
                                                            v-if="run.status === 'COMPLETED'" 
                                                            @click="viewResults(run, subject.id)" 
                                                            class="btn btn-xs btn-primary"
                                                          >
                                                            View Results
                                                          </button>
                                                          <span v-else-if="run.status === 'IN_PROGRESS'" class="hint">Processing...</span>
                                                          <span v-else-if="run.status === 'FAILED'" class="hint">Failed</span>
                                                          <!-- Add other statuses if needed -->
                                                        </td>
                                                      </tr>
                                                    </tbody>
                                                 </table>
                                             </div>
                                             <div v-else class="empty-state-small mt-2 text-muted fst-italic">
                                                No analysis history found for this subject.
                                             </div>
                                        </div>

                                        <!-- Agent Intelligence Tab -->
                                        <div v-if="activeDetailTab[subject.id] === 'agent'">
                                            <h5 class="mt-2">Intelligent Compliance Agent Evaluation</h5>
                                            
                                            <div v-if="loadingAgentEvaluation[subject.id]" class="loading-inline small">
                                                <span class="spinner-sm me-1"></span> Loading agent evaluation...
                                            </div>
                                            
                                            <div v-else-if="agentEvaluations[subject.id] && agentEvaluations[subject.id].action && agentEvaluations[subject.id].action !== 'ERROR'" class="agent-evaluation-content mt-2">
                                                <!-- Agent Recommendation Header -->
                                                <div class="agent-recommendation-header">
                                                    <div class="recommendation-summary">
                                                        <i class="fas fa-robot me-2"></i>
                                                        <span class="action-label">Recommended Action:</span>
                                                        <span class="action-value">{{ formatActionName(agentEvaluations[subject.id].action) }}</span>
                                                        
                                                        <div class="urgency-badge" :class="getUrgencyClass(agentEvaluations[subject.id].urgency)">
                                                            <i class="fas fa-exclamation-triangle me-1"></i>
                                                            {{ agentEvaluations[subject.id].urgency }} PRIORITY
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Agent Reasoning -->
                                                <div class="agent-reasoning mt-3">
                                                    <h6><i class="fas fa-lightbulb me-2"></i>Intelligence Analysis</h6>
                                                    <p class="reasoning-text">{{ agentEvaluations[subject.id].reasoning }}</p>
                                                </div>

                                                <!-- Confidence Metrics -->
                                                <div class="confidence-metrics mt-3">
                                                    <h6><i class="fas fa-chart-line me-2"></i>Confidence Assessment</h6>
                                                    <div class="confidence-bar">
                                                        <div class="confidence-fill" :style="{width: (agentEvaluations[subject.id].overall_confidence * 100) + '%'}" :class="getAgentConfidenceClass(agentEvaluations[subject.id].overall_confidence)"></div>
                                                    </div>
                                                    <span class="confidence-percentage">{{ Math.round(agentEvaluations[subject.id].overall_confidence * 100) }}%</span>
                                                    
                                                    <div v-if="agentEvaluations[subject.id].estimated_resolution_days" class="resolution-timeline mt-2">
                                                        <i class="fas fa-calendar-alt me-2"></i>
                                                        <span class="timeline-label">Est. Resolution:</span>
                                                        <span class="timeline-value">{{ agentEvaluations[subject.id].estimated_resolution_days }} days</span>
                                                    </div>
                                                </div>

                                                <!-- Specific Actions -->
                                                <div v-if="agentEvaluations[subject.id].specific_actions && agentEvaluations[subject.id].specific_actions.length > 0" class="specific-actions mt-3">
                                                    <h6><i class="fas fa-tasks me-2"></i>Recommended Steps</h6>
                                                    <ul class="actions-list">
                                                        <li v-for="(action, index) in agentEvaluations[subject.id].specific_actions" :key="index" class="action-item">
                                                            <i class="fas fa-arrow-right me-2"></i>
                                                            <span class="action-name">{{ formatActionName(action.action) }}:</span>
                                                            <span class="action-description">{{ formatActionDescription(action.description) }}</span>
                                                        </li>
                                                    </ul>
                                                </div>

                                                <!-- Risk Factors -->
                                                <div v-if="agentEvaluations[subject.id].risk_factors && agentEvaluations[subject.id].risk_factors.length > 0" class="agent-risk-factors mt-3">
                                                    <h6><i class="fas fa-exclamation-triangle me-2"></i>Identified Risk Factors</h6>
                                                    <div class="risk-factors-grid">
                                                        <div v-for="(factor, index) in agentEvaluations[subject.id].risk_factors" :key="index" class="risk-factor-item">
                                                            <i class="fas fa-shield-alt me-2"></i>
                                                            <span>{{ factor }}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Agent Metadata -->
                                                <div v-if="agentEvaluations[subject.id].evaluation_timestamp" class="agent-metadata mt-3">
                                                    <h6><i class="fas fa-info-circle me-2"></i>Evaluation Details</h6>
                                                    <div class="metadata-grid">
                                                        <div class="metadata-item">
                                                            <span class="metadata-label">Evaluated:</span>
                                                            <span class="metadata-value">{{ formatDateTime(agentEvaluations[subject.id].evaluation_timestamp) }}</span>
                                                        </div>
                                                        <div v-if="agentEvaluations[subject.id].agent_version" class="metadata-item">
                                                            <span class="metadata-label">Agent Version:</span>
                                                            <span class="metadata-value">{{ agentEvaluations[subject.id].agent_version }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <!-- Agent Error State -->
                                            <div v-else-if="agentEvaluations[subject.id] && agentEvaluations[subject.id].action === 'ERROR'" class="agent-error mt-2">
                                                <div class="alert alert-warning">
                                                    <i class="fas fa-exclamation-triangle me-2"></i>
                                                    <strong>Agent Evaluation Error:</strong> {{ agentEvaluations[subject.id].reasoning }}
                                                </div>
                                            </div>
                                            
                                            <!-- No Agent Data -->
                                            <div v-else class="empty-state-small mt-2 text-muted fst-italic">
                                                No agent evaluation available for this subject. Agent analysis is triggered during document analysis.
                                            </div>
                                        </div>

                                        <!-- Coverage Matrix Tab -->
                                        <div v-if="activeDetailTab[subject.id] === 'coverage'">
                                            <h5 class="mt-2">Coverage Matrix</h5>
                                            <div v-if="loadingCoverage[subject.id]" class="loading-inline small">
                                                <span class="spinner-sm me-1"></span> Loading coverage...
                                            </div>
                                            <div v-else-if="coverageSummary[subject.id]" class="mt-2">
                                                <table class="table table-sm requirements-table requirements-table-inline">
                                                    <thead>
                                                        <tr>
                                                            <th>Coverage</th>
                                                            <th>Status</th>
                                                            <th>Best Expiration</th>
                                                            <th>Details</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="req in coverageSummary[subject.id].required_coverages" :key="req.code">
                                                            <td>{{ req.name }}</td>
                                                            <td>
                                                                <span :class="['status-badge', coverageStateBadgeClass(subject.id, req.code)]">
                                                                    {{ coverageStateDisplay(subject.id, req.code) }}
                                                                </span>
                                                            </td>
                                                            <td>{{ bestExpiration(subject.id, req.code) || '—' }}</td>
                                                            <td>
                                                                <span v-for="p in coveragePolicies(subject.id, req.code)" :key="p.policy_id" class="badge bg-light text-dark me-1">
                                                                    {{ p.policy_name }}<span v-if="p.expiration"> (exp {{ formatDateOnly(p.expiration) }})</span>
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div v-else class="empty-state-small mt-2 text-muted fst-italic">No coverage data.</div>
                                        </div>

                                        <!-- Response Intelligence Tab -->
                                        <div v-if="activeDetailTab[subject.id] === 'response'">
                                            <h5 class="mt-2">Response Intelligence</h5>
                                            <div v-if="loadingResponseIntelligence[subject.id]" class="loading-inline small">
                                                <span class="spinner-sm me-1"></span> Loading response analytics...
                                            </div>
                                            <div v-else-if="responseIntelligence[subject.id]" class="mt-2">
                                                <div class="row">
                                                    <!-- Response Metrics -->
                                                    <div class="col-md-6">
                                                        <div class="metrics-card">
                                                            <h6>📊 Response Metrics (90 days)</h6>
                                                            <div class="metric-grid">
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Total Requests</span>
                                                                    <span class="metric-value">{{ responseIntelligence[subject.id].analytics.total_requests_90_days }}</span>
                                                                </div>
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Response Rate</span>
                                                                    <span class="metric-value">{{ (responseIntelligence[subject.id].analytics.response_rate * 100).toFixed(1) }}%</span>
                                                                </div>
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Avg Response Time</span>
                                                                    <span class="metric-value">{{ responseIntelligence[subject.id].analytics.average_response_time_hours.toFixed(1) }}h</span>
                                                                </div>
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Reliability Score</span>
                                                                    <span :class="['metric-value', getReliabilityClass(responseIntelligence[subject.id].analytics.reliability_score)]">
                                                                        {{ (responseIntelligence[subject.id].analytics.reliability_score * 100).toFixed(0) }}%
                                                                    </span>
                                                                </div>
                                                                <div class="metric-item" v-if="responseIntelligence[subject.id].analytics.overdue_count > 0">
                                                                    <span class="metric-label">Overdue Requests</span>
                                                                    <span class="metric-value text-danger">{{ responseIntelligence[subject.id].analytics.overdue_count }}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <!-- Prediction & Recommendations -->
                                                    <div class="col-md-6">
                                                        <div class="metrics-card">
                                                            <h6>🔮 Response Prediction</h6>
                                                            <div class="metric-grid">
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Next Response Time</span>
                                                                    <span class="metric-value">{{ responseIntelligence[subject.id].prediction.predicted_response_hours.toFixed(1) }}h</span>
                                                                </div>
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Confidence</span>
                                                                    <span class="metric-value">{{ (responseIntelligence[subject.id].prediction.confidence_score * 100).toFixed(0) }}%</span>
                                                                </div>
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Preferred Method</span>
                                                                    <span class="metric-value">{{ responseIntelligence[subject.id].prediction.recommendation.recommended_method }}</span>
                                                                </div>
                                                                <div class="metric-item">
                                                                    <span class="metric-label">Best Time</span>
                                                                    <span class="metric-value">{{ responseIntelligence[subject.id].prediction.recommendation.recommended_timing }}</span>
                                                                </div>
                                                            </div>
                                                            
                                                            <!-- Strategy Recommendations -->
                                                            <div v-if="responseIntelligence[subject.id].prediction.recommendation.strategy_notes?.length" class="mt-3">
                                                                <h6 class="small">💡 Communication Strategy</h6>
                                                                <ul class="small mb-0">
                                                                    <li v-for="note in responseIntelligence[subject.id].prediction.recommendation.strategy_notes" :key="note">
                                                                        {{ note }}
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <!-- Recent Requests -->
                                                <div v-if="responseIntelligence[subject.id].analytics.recent_requests?.length" class="mt-3">
                                                    <h6>📋 Recent Requests</h6>
                                                    <div class="table-responsive">
                                                        <table class="table table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th>Type</th>
                                                                    <th>Sent</th>
                                                                    <th>Status</th>
                                                                    <th>Response Time</th>
                                                                    <th>Escalation</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr v-for="req in responseIntelligence[subject.id].analytics.recent_requests" :key="req.id">
                                                                    <td>{{ req.type }}</td>
                                                                    <td>{{ formatDateTime(req.sent_at) }}</td>
                                                                    <td>
                                                                        <span :class="['badge', getRequestStatusClass(req)]">
                                                                            {{ req.status }}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span v-if="req.response_time_hours">
                                                                            {{ req.response_time_hours.toFixed(1) }}h
                                                                        </span>
                                                                        <span v-else-if="req.is_overdue" class="text-danger">
                                                                            Overdue
                                                                        </span>
                                                                        <span v-else class="text-muted">
                                                                            Pending
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span v-if="req.escalation_level > 0" class="badge bg-warning">
                                                                            Level {{ req.escalation_level }}
                                                                        </span>
                                                                        <span v-else class="text-muted">—</span>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-else class="empty-state-small mt-2 text-muted fst-italic">No response intelligence data available.</div>
                                        </div>
                                    </div> 

                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            
            <!-- Pagination Controls -->
            <div class="pagination-controls" v-if="totalPages > 1">
              <div class="pagination-info">
                Showing {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, filteredSubjects.length) }} 
                of {{ filteredSubjects.length }} subjects
              </div>
              <div class="pagination-buttons">
                <button 
                  @click="goToPage(1)" 
                  :disabled="currentPage === 1"
                  class="btn btn-outline-secondary btn-sm"
                  title="First page"
                >
                  <i class="fas fa-angle-double-left"></i>
                </button>
                <button 
                  @click="goToPage(currentPage - 1)" 
                  :disabled="currentPage === 1"
                  class="btn btn-outline-secondary btn-sm"
                  title="Previous page"
                >
                  <i class="fas fa-angle-left"></i>
                </button>
                
                <span class="page-numbers">
                  <button 
                    v-for="page in visiblePages" 
                    :key="page"
                    @click="goToPage(page)"
                    :class="['btn', 'btn-sm', page === currentPage ? 'btn-primary' : 'btn-outline-secondary']"
                  >
                    {{ page }}
                  </button>
                </span>
                
                <button 
                  @click="goToPage(currentPage + 1)" 
                  :disabled="currentPage === totalPages"
                  class="btn btn-outline-secondary btn-sm"
                  title="Next page"
                >
                  <i class="fas fa-angle-right"></i>
                </button>
                <button 
                  @click="goToPage(totalPages)" 
                  :disabled="currentPage === totalPages"
                  class="btn btn-outline-secondary btn-sm"
                  title="Last page"
                >
                  <i class="fas fa-angle-double-right"></i>
                </button>
              </div>
              <div class="page-size-controls">
                <label>
                  Show:
                  <select v-model="pageSize" @change="handlePageSizeChange" class="form-select form-select-sm">
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                    <option :value="250">250</option>
                  </select>
                  per page
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Analysis Tools Section -->
        <div class="intelligence-card analysis-tools">
          <div class="section-header">
            <h2><i class="fas fa-cogs"></i> Analysis Tools</h2>
            <p class="section-description">Run analysis and manage templates</p>
          </div>
          
          <div class="tools-grid">
            <div class="tool-card">
              <h4><i class="fas fa-upload"></i> Upload Document</h4>
              <p>Upload compliance documents for analysis</p>
              <button @click="openUploadModal" class="btn btn-primary">
                Upload
              </button>
            </div>
            
            <div class="tool-card">
              <h4><i class="fas fa-users-cog"></i> Manage Entities</h4>
              <p>Add and manage project entities</p>
              <button @click="openEntityManagerModal" class="btn btn-primary">
                Manage
              </button>
            </div>
            
            <div class="tool-card">
              <h4><i class="fas fa-play"></i> Run Analysis</h4>
              <p>Start new compliance analysis</p>
              <button @click="openNewAnalysisModal" class="btn btn-primary">
                Analyze
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- New Analysis Modal -->
    <div v-if="showRunDialog" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h2>Run {{ selectedCheck?.name }}</h2>
          <button @click="closeRunDialog" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <p>Select documents to analyze:</p>
          
          <div v-if="loadingDocuments" class="loading-inline">
            <div class="spinner-sm"></div>
            <span>Loading documents...</span>
          </div>
          
          <div v-else-if="documents.length === 0" class="empty-state">
            <p>No documents available for analysis. Please upload documents first using the "Upload Document" button.</p>
            <button @click="closeRunDialog; openUploadModal()" class="btn btn-primary mt-3">
              <i class="fas fa-upload"></i> Upload Now
            </button>
          </div>
          
          <div v-else class="document-selection">
            <div v-for="doc in documents" 
                 :key="doc.id" 
                 :class="['document-item', { selected: selectedDocuments.includes(doc.id) }]"
                 >
              <input 
                type="checkbox" 
                :value="doc.id" 
                v-model="selectedDocuments" 
                :id="'doc-' + doc.id"
                >
              <label :for="'doc-' + doc.id" class="document-details">
                <div class="document-name">{{ doc.name }}</div>
                <div class="document-meta">
                  <span class="document-type">{{ formatDocumentType(doc.document_type) }}</span>
                  <span class="document-date" v-if="doc.created_at">{{ formatDate(doc.created_at).split(',')[0] }}</span>
                </div>
              </label>
            </div>
            
            <div v-if="selectedDocuments.length > 0" class="selection-summary">
              {{ selectedDocuments.length }} document{{ selectedDocuments.length !== 1 ? 's' : '' }} selected
            </div>
          </div>
          
          <!-- Temporal Policy Context Options -->
          <div class="policy-context-options">
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-calendar-alt"></i> Policy Reference Date
                <span class="help-tooltip" title="Analyze documents against policies that were effective on this date. Useful for historical compliance analysis.">?</span>
              </label>
              <div class="reference-date-input">
                <input 
                  type="date" 
                  v-model="analysisReferenceDate" 
                  class="form-control"
                  :max="todayDate"
                />
                <button 
                  @click="analysisReferenceDate = ''" 
                  class="btn btn-sm btn-outline clear-date"
                  v-if="analysisReferenceDate"
                  title="Clear date (use document dates)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <small class="form-text text-muted">
                {{ analysisReferenceDate 
                   ? `Analysis will use policies effective on ${formatDate(analysisReferenceDate + 'T00:00:00').split(',')[0]}`
                   : 'Leave empty to use each document\'s date automatically' }}
              </small>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeRunDialog" class="btn btn-secondary">Cancel</button>
          <button 
            @click="confirmRunCheck" 
            class="btn btn-primary"
            :disabled="selectedDocuments.length === 0 || runningCheck"
          >
            <span v-if="runningCheck" class="spinner-sm"></span>
            {{ runningCheck ? 'Processing...' : 'Run Analysis' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Results Dialog -->
    <div v-if="showResultsDialog" class="modal-overlay">
      <div class="modal-container modal-extra-large">
        <div class="modal-header">
          <h2>Analysis Results</h2>
          <button @click="closeResultsDialog" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <div v-if="loadingResults" class="loading-inline">
            <div class="spinner-sm"></div>
            <span>Loading results...</span>
          </div>
          
          <div v-else class="split-pane-container">
            <div class="split-pane-layout">
              <!-- Left Panel: Document Viewer -->
              <div class="document-panel">
                <div class="panel-header">
                  <h4><i class="fas fa-file-alt"></i> Source Document</h4>
                  <p class="panel-subtitle">Review the analyzed document to validate findings</p>
                </div>
                <div class="document-viewer">
                  <div v-if="getSelectedRunDocument()" class="document-content">
                    <div class="document-info">
                      <h5>{{ documentNames[selectedRun?.document] || 'Unknown document' }}</h5>
                      <span class="document-meta">Analysis Date: {{ formatDate(selectedRun?.created_at) }}</span>
                    </div>
                    <div class="document-actions">
                      <a :href="getDocumentUrl(getSelectedRunDocument())" 
                         target="_blank" 
                         class="btn btn-outline btn-sm">
                        <i class="fas fa-external-link-alt"></i> View Full Size
                      </a>
                      <button @click="handleDownload(getSelectedRunDocument())" 
                         :disabled="downloading"
                         class="btn btn-outline btn-sm">
                        <i :class="downloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                        {{ downloading ? 'Downloading...' : 'Download' }}
                      </button>
                    </div>
                    <!-- Document preview area -->
                    <div class="document-preview">
                      <div v-if="isPDF(getSelectedRunDocument())" class="pdf-container">
                        <!-- PDF iframe with better browser support -->
                        <iframe :src="getDocumentUrl(getSelectedRunDocument())" 
                                type="application/pdf"
                                class="pdf-preview"
                                title="Analysis Document Preview">
                        </iframe>
                        <div class="pdf-fallback">
                          <p><i class="fas fa-file-pdf"></i> PDF Viewer</p>
                          <p class="small text-muted">If the PDF doesn't display above, <a :href="getDocumentUrl(getSelectedRunDocument())" target="_blank">open in new tab</a></p>
                        </div>
                      </div>
                      <div v-else class="preview-placeholder">
                        <i class="fas fa-file-alt"></i>
                        <p>Document preview not available for this file type</p>
                        <p class="small text-muted">Click "View Full Size" to open in new tab</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-document">
                    <i class="fas fa-file-exclamation"></i>
                    <p>Document not available for preview</p>
                  </div>
                </div>
              </div>

              <!-- Right Panel: Analysis Results -->
              <div class="results-panel">
                <div class="panel-header">
                  <h4><i class="fas fa-clipboard-check"></i> Analysis Results</h4>
                  <p class="panel-subtitle">Comprehensive check findings and evidence</p>
                </div>
                
                <div class="results-header">
                  <div class="result-info">
                    <div><strong>Analysis:</strong> {{ selectedRun?.check_definition_name }}</div>
                    <div><strong>Status:</strong> <span :class="['run-status', selectedRun?.status?.toLowerCase()]">{{ selectedRun?.status }}</span></div>
                  </div>
                  <div class="result-summary">
                    <div class="summary-card">
                      <div class="summary-title">Overall Outcome</div>
                      <div :class="['summary-value', selectedRun?.outcome ? `outcome-${selectedRun.outcome.toLowerCase()}` : '']">
                        {{ formatOutcome(selectedRun?.outcome) }}
                      </div>
                    </div>
                    <div class="summary-card">
                      <div class="summary-title">Passed</div>
                      <div class="summary-value passed">{{ selectedRun?.results_summary?.passed || 0 }}</div>
                    </div>
                    <div class="summary-card">
                      <div class="summary-title">Failed</div>
                      <div class="summary-value failed">{{ selectedRun?.results_summary?.failed || 0 }}</div>
                    </div>
                  </div>
                  <button
                    v-if="selectedRun?.status === 'COMPLETED' && !comparisonRun"
                    type="button"
                    class="btn btn-primary btn-sm"
                    :disabled="comparisonLoading"
                    @click="startComparison"
                  >
                    <i :class="comparisonLoading ? 'fas fa-spinner fa-spin' : 'fas fa-redo'"></i>
                    Re-run and compare
                  </button>
                </div>

                <section v-if="comparisonRun || comparisonLoading" class="notification-panel">
                  <div class="notification-header">
                    <div class="notification-title">
                      <i class="fas fa-columns"></i>
                      <span>Comparison</span>
                    </div>
                    <span v-if="comparisonLoading" class="notification-status pending">
                      Running the current check. The original result stays on file.
                    </span>
                    <span v-else-if="comparisonRun?.run_metadata?.comparison_of" class="notification-status info">
                      Review only. The agent was not emailed, and the franchisee count did not change.
                    </span>
                    <span v-else class="notification-status sent">
                      This result is now the live one. The earlier run is still on file.
                    </span>
                  </div>
                  <p v-if="comparisonError" class="notification-recipient no-recipient">{{ comparisonError }}</p>
                  <template v-if="comparisonRun && !comparisonLoading">
                    <p><strong>New agent summary</strong></p>
                    <p>{{ comparisonOverview }}</p>
                    <ul v-if="comparisonInstructions.length">
                      <li v-for="(item, idx) in comparisonInstructions" :key="'cmp-' + idx">
                        {{ item.action }}
                      </li>
                    </ul>
                    <ul>
                      <li v-for="row in comparisonRows" :key="row.code">
                        <strong>{{ row.name }}</strong>
                        — was {{ row.before }}, now {{ row.after }}
                      </li>
                    </ul>
                    <p>
                      Sending the request emails the new summary. The franchisee count stays on the earlier result until you use this result.
                    </p>
                    <div class="purpose-actions">
                      <button
                        v-if="comparisonHasGap && comparisonRun.run_metadata?.comparison_of"
                        type="button"
                        class="btn btn-primary btn-sm"
                        @click="openComparisonSend"
                      >
                        Send this request
                      </button>
                      <button
                        v-if="comparisonRun.run_metadata?.comparison_of"
                        type="button"
                        class="btn btn-outline btn-sm"
                        :disabled="adoptingComparison"
                        @click="adoptComparison"
                      >
                        Use this result
                      </button>
                    </div>
                  </template>
                </section>

                <!-- Notification transparency + recipient management (PR1 + PR2) -->
                <div v-if="notificationPreview" class="notification-panel" :class="notificationPanelClass">
                  <div class="notification-header">
                    <div class="notification-title">
                      <i class="fas fa-envelope"></i>
                      <span>Gap Notification Email</span>
                    </div>
                    <span v-if="notificationPreview.auto_sent" class="notification-status sent">
                      <i class="fas fa-check-circle"></i> Auto-sent on completion
                    </span>
                    <span v-else-if="notificationPreview.failed_count === 0" class="notification-status info">
                      <i class="fas fa-info-circle"></i> No gaps — no notification needed
                    </span>
                    <span v-else class="notification-status pending">
                      <i class="fas fa-pause-circle"></i> Not sent automatically
                    </span>
                  </div>

                  <div v-if="notificationPreview.recipient" class="notification-recipient">
                    <div class="recipient-row">
                      <strong>Suggested recipient:</strong>
                      <span class="recipient-email">{{ notificationPreview.recipient.email }}</span>
                      <span class="recipient-source" :title="`Resolved via: ${notificationPreview.recipient.source}`">
                        ({{ formatNotificationSource(notificationPreview.recipient.source) }})
                      </span>
                    </div>
                    <div v-if="notificationPreview.recipient.contact_name" class="recipient-contact">
                      <i class="fas fa-user"></i>
                      {{ notificationPreview.recipient.contact_name }}
                    </div>
                  </div>
                  <div v-else class="notification-recipient no-recipient">
                    <i class="fas fa-exclamation-triangle"></i>
                    No contact email could be auto-resolved. You can still pick a contact or
                    type one in below.
                  </div>

                  <div v-if="notificationBlockingExplanations.length > 0" class="notification-reasons">
                    <strong>Why it wasn't auto-sent:</strong>
                    <ul>
                      <li v-for="(reason, idx) in notificationBlockingExplanations" :key="idx">
                        {{ reason }}
                      </li>
                    </ul>
                  </div>

                  <div v-if="notificationHistory.length > 0" class="notification-history">
                    <strong>Send history:</strong>
                    <ul>
                      <li v-for="(entry, idx) in notificationHistory" :key="entry.id || idx">
                        <i :class="entry.success ? 'fas fa-check text-success' : 'fas fa-times text-danger'"></i>
                        <span class="history-trigger" :class="`history-trigger--${(entry.trigger || '').toLowerCase()}`">
                          {{ entry.trigger || 'MANUAL' }}
                        </span>
                        <span>{{ formatDate(entry.sent_at) }}</span>
                        <span v-if="entry.sent_by_user_email"> — by {{ entry.sent_by_user_email }}</span>
                        <span v-if="entry.recipient"> → {{ entry.recipient }}</span>
                        <span v-if="entry.cc && entry.cc.length > 0">
                          (cc: {{ entry.cc.join(', ') }})
                        </span>
                        <span v-if="!entry.success && entry.reason" class="text-danger">
                          ({{ entry.reason }})
                        </span>
                      </li>
                    </ul>
                  </div>

                  <!-- Inline send-drawer (PR2): override To/CC, pick a contact -->
                  <div v-if="showSendDrawer" class="send-drawer">
                    <div class="drawer-header">
                      <strong><i class="fas fa-paper-plane"></i> Send gap notification</strong>
                    </div>

                    <div class="drawer-row">
                      <label>To <span class="req">*</span></label>
                      <input
                        type="email"
                        v-model.trim="sendForm.to"
                        placeholder="recipient@example.com"
                        class="form-control"
                      />
                    </div>

                    <div v-if="candidateContacts.length > 0" class="drawer-row">
                      <label>Pick from known contacts</label>
                      <select
                        v-model="sendForm.contact_id"
                        @change="onCandidateChosen"
                        class="form-control"
                      >
                        <option :value="null">— Select a contact —</option>
                        <option
                          v-for="c in candidateContacts"
                          :key="`${c.relation}-${c.contact_id || c.email}`"
                          :value="c.contact_id"
                        >
                          {{ c.name }} &lt;{{ c.email }}&gt;
                          <template v-if="c.is_primary"> · primary</template>
                          <template v-if="c.entity_name"> · {{ c.entity_name }}</template>
                        </option>
                      </select>
                    </div>

                    <div class="drawer-row">
                      <label>CC (optional)</label>
                      <div class="cc-input-row">
                          <input
                            type="email"
                            v-model.trim="ccInput"
                            @keydown="onCcKeydown"
                            placeholder="Type email + Enter"
                            class="form-control"
                          />
                        <button @click="addCc" class="btn btn-outline btn-sm" type="button">
                          <i class="fas fa-plus"></i> Add
                        </button>
                      </div>
                      <div v-if="sendForm.cc.length > 0" class="cc-tags">
                        <span v-for="(addr, idx) in sendForm.cc" :key="addr" class="cc-tag">
                          {{ addr }}
                          <button @click="removeCc(idx)" class="cc-remove" type="button" title="Remove">
                            <i class="fas fa-times"></i>
                          </button>
                        </span>
                      </div>
                      <small class="cc-hint">
                        No one is CC'd by default. Add yourself if you want a copy of the outgoing email.
                      </small>
                    </div>

                    <div class="drawer-actions">
                      <button @click="closeSendDrawer" class="btn btn-outline btn-sm" :disabled="sendingNotification" type="button">
                        Cancel
                      </button>
                      <button
                        @click="confirmSend"
                        :disabled="!sendForm.to || sendingNotification"
                        class="btn btn-primary btn-sm"
                        type="button"
                      >
                        <i :class="sendingNotification ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
                        {{ sendingNotification ? 'Sending...' : `Send to ${sendForm.to || '...'}` }}
                      </button>
                    </div>
                  </div>

                  <div v-else class="notification-actions">
                    <button
                      @click="loadNotificationPreview"
                      :disabled="notificationLoading"
                      class="btn btn-outline btn-sm"
                      title="Refresh recipient lookup"
                    >
                      <i :class="notificationLoading ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
                      Refresh
                    </button>
                    <button
                      @click="openSendDrawer"
                      :disabled="!notificationPreview.can_send"
                      class="btn btn-primary btn-sm"
                      :title="sendButtonTooltip"
                    >
                      <i class="fas fa-paper-plane"></i>
                      Configure &amp; Send
                    </button>
                  </div>
                </div>
                
                <div v-if="requirementResults.length > 0" class="requirements-results">
                  <h3>Compliance Analysis</h3>
                  
                  <!-- Streamlined Results List -->
                  <div class="streamlined-results">
                    <!-- Failed Results First (Most Important) -->
                    <div v-if="failedResults.length > 0" class="result-section failed-section">
                      <h4 class="section-header failed-header">
                        <i class="fas fa-times-circle"></i> Issues Found ({{ failedResults.length }})
                      </h4>
                      <div 
                        v-for="result in failedResults" 
                        :key="result.id" 
                        class="result-item failed-item"
                      >
                        <div class="result-content">
                          <div class="result-header-row">
                            <div class="result-badges">
                              <span class="req-code-badge" :title="`Requirement Code: ${result.requirement_code}`">
                                {{ result.requirement_code || 'N/A' }}
                              </span>
                              <span class="severity-badge" :class="`severity-${(result.requirement_severity?.toLowerCase()) ?? 'unknown'}`">
                                {{ result.requirement_severity || 'HIGH' }}
                              </span>
                              <span v-if="getConfidenceScore(result)" 
                                    class="confidence-badge" 
                                    :class="getConfidenceClass(getConfidenceScore(result))"
                                    :title="`AI Confidence: ${Math.round(getConfidenceScore(result) * 100)}%`">
                                <i class="fas fa-brain"></i> {{ Math.round(getConfidenceScore(result) * 100) }}%
                              </span>
                            </div>
                            <div class="quick-actions">
                              <button 
                                @click="editRequirement(result)" 
                                class="btn-icon btn-edit"
                                title="Edit this requirement's prompt">
                                <i class="fas fa-edit"></i>
                              </button>
                              <button 
                                @click="goToPromptEnhancement(result.requirement_code)" 
                                class="btn-icon btn-enhance"
                                title="Enhance prompt for this requirement">
                                <i class="fas fa-magic"></i>
                              </button>
                            </div>
                          </div>
                          <div class="result-message">
                            <span class="result-icon">❌</span>
                            <div class="result-details">
                              <!-- Evidence Section -->
                              <div class="result-text evidence">
                                <div class="evidence-header">
                                  <strong>Evidence:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getFullEvidence(result))"
                                    @click="toggleExpanded('evidence', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('evidence', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('evidence', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <div class="evidence-content" :class="{ 'expanded': isExpanded('evidence', result.id) }">
                                  <!-- DEBUG: {{ typeof result.evidence }} - {{ result.evidence ? result.evidence.substring(0, 50) : 'null' }} -->
                                  {{ getDisplayText(getFullEvidence(result), 'evidence', result.id) || 'No evidence found' }}
                                </div>
                              </div>

                              <!-- Reasoning Section (if available) -->
                              <div v-if="getReasoning(result)" class="result-text reasoning">
                                <div class="reasoning-header">
                                  <strong>Analysis:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getReasoning(result))"
                                    @click="toggleExpanded('reasoning', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('reasoning', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('reasoning', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <div class="reasoning-content" :class="{ 'expanded': isExpanded('reasoning', result.id) }">
                                  {{ getDisplayText(getReasoning(result), 'reasoning', result.id) }}
                                </div>
                              </div>

                              <!-- Issues Section -->
                              <div v-if="getDeficiencies(result).length > 0" class="result-text deficiencies">
                                <div class="issues-header">
                                  <strong>Issues:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getDeficienciesText(result))"
                                    @click="toggleExpanded('issues', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('issues', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('issues', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <ul class="issues-list" :class="{ 'expanded': isExpanded('issues', result.id) }">
                                  <li v-for="(deficiency, index) in getDeficiencies(result)" :key="index">
                                    {{ deficiency }}
                                  </li>
                                </ul>
                              </div>

                              <!-- Actions Section -->
                              <div v-if="getSuggestions(result).length > 0" class="result-text actions">
                                <div class="actions-header">
                                  <strong>Recommendations:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getSuggestionsText(result))"
                                    @click="toggleExpanded('actions', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('actions', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('actions', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <ul class="actions-list" :class="{ 'expanded': isExpanded('actions', result.id) }">
                                  <li v-for="(suggestion, index) in getSuggestions(result)" :key="index">
                                    {{ suggestion }}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                          
                          <!-- HITL Override Section for Failed Results -->
                          <div class="result-actions mt-2">
                            <div class="feedback-actions">
                              <button 
                                @click="submitRequirementFeedback(result.id, selectedRun.subject_id, 'ACCURATE')" 
                                :class="['feedback-btn', { active: result.user_feedback_accuracy === 'ACCURATE' }]"
                                title="Mark as Accurate - AI correctly identified this as failed">
                                <i class="fas fa-thumbs-up"></i>
                              </button>
                              <button 
                                @click="submitRequirementFeedback(result.id, selectedRun.subject_id, 'PARTIALLY_CORRECT')" 
                                :class="['feedback-btn', { active: result.user_feedback_accuracy === 'PARTIALLY_CORRECT' }]"
                                title="Mark as Partially Correct - AI got some parts right but missed details">
                                <i class="fas fa-adjust"></i>
                              </button>
                              <button 
                                @click="submitRequirementFeedback(result.id, selectedRun.subject_id, 'INACCURATE')" 
                                :class="['feedback-btn', { active: result.user_feedback_accuracy === 'INACCURATE' }]"
                                title="Mark as Inaccurate - AI got this wrong">
                                <i class="fas fa-thumbs-down"></i>
                              </button>
                              
                              <!-- Enhanced Correction Button - Shows for any feedback -->
                              <button 
                                v-if="result.user_feedback_accuracy && result.user_feedback_accuracy !== 'NONE'"
                                @click="openCorrectionModal(result)"
                                :class="['btn btn-sm ms-2', getCorrectionButtonClass(result)]">
                                <i class="fas fa-edit-medical"></i> 
                                {{ getCorrectionButtonText(result) }}
                              </button>
                              
                              <!-- Show correction summary if available -->
                              <div v-if="result.correct_result_should_be" class="correction-summary mt-2">
                                <small class="text-muted">
                                  <i class="fas fa-info-circle"></i>
                                  Correction: Should be <span :class="['badge', 'badge-sm', getCorrectionResultBadgeClass(result.correct_result_should_be)]">{{ result.correct_result_should_be }}</span>
                                  <span v-if="result.correction_categories && result.correction_categories.length > 0">
                                    - Issues: {{ result.correction_categories.join(', ') }}
                                  </span>
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Passed Results -->
                    <div v-if="passedResults.length > 0" class="result-section passed-section">
                      <h4 class="section-header passed-header">
                        <i class="fas fa-check-circle"></i> Requirements Met ({{ passedResults.length }})
                      </h4>
                      <div 
                        v-for="result in passedResults" 
                        :key="result.id" 
                        class="result-item passed-item"
                      >
                        <div class="result-content">
                          <div class="result-header-row">
                            <div class="result-badges">
                              <span class="req-code-badge" :title="`Requirement Code: ${result.requirement_code}`">
                                {{ result.requirement_code || 'N/A' }}
                              </span>
                              <span class="severity-badge" :class="`severity-${(result.requirement_severity?.toLowerCase()) ?? 'unknown'}`">
                                {{ result.requirement_severity || 'MED' }}
                              </span>
                              <span v-if="getConfidenceScore(result)" 
                                    class="confidence-badge" 
                                    :class="getConfidenceClass(getConfidenceScore(result))"
                                    :title="`AI Confidence: ${Math.round(getConfidenceScore(result) * 100)}%`">
                                <i class="fas fa-brain"></i> {{ Math.round(getConfidenceScore(result) * 100) }}%
                              </span>
                            </div>
                            <div class="quick-actions">
                              <button 
                                @click="editRequirement(result)" 
                                class="btn-icon btn-edit"
                                title="Edit this requirement's prompt">
                                <i class="fas fa-edit"></i>
                              </button>
                              <button 
                                @click="goToPromptEnhancement(result.requirement_code)" 
                                class="btn-icon btn-enhance"
                                title="Enhance prompt for this requirement">
                                <i class="fas fa-magic"></i>
                              </button>
                            </div>
                          </div>
                          <div class="result-message">
                            <span class="result-icon">✅</span>
                            <div class="result-details">
                              <!-- Confirmation Section -->
                              <div class="result-text response">
                                <div class="confirmation-header">
                                  <strong>Confirmation:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getReasoning(result) || getFullEvidence(result))"
                                    @click="toggleExpanded('confirmation', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('confirmation', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('confirmation', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <div class="confirmation-content" :class="{ 'expanded': isExpanded('confirmation', result.id) }">
                                  <div v-if="hasEnhancedData(result)" class="enhanced-confirmation">
                                    <div class="enhanced-response-display">
                                      <div class="evidence-section">
                                        <strong>Evidence:</strong> {{ getDisplayText(getFullEvidence(result), 'confirmation', result.id) }}
                                      </div>
                                      <p v-if="result.details && result.details.internal_review" class="internal-review-note">
                                        {{ result.details.internal_review.message }}
                                      </p>
                                      <div v-if="getReasoning(result)" class="reasoning-section">
                                        <strong>Analysis:</strong> {{ getDisplayText(getReasoning(result), 'reasoning', result.id) }}
                                      </div>
                                      <div v-if="getConfidenceScore(result)" class="confidence-section">
                                        <strong>Confidence:</strong> 
                                        <span :class="getConfidenceClass(getConfidenceScore(result))">
                                          {{ Math.round(getConfidenceScore(result) * 100) }}%
                                        </span>
                                      </div>
                                      <div v-if="getDeficiencies(result).length > 0" class="deficiencies-section">
                                        <strong>Issues:</strong>
                                        <ul class="deficiency-list">
                                          <li v-for="deficiency in getDeficiencies(result)" :key="deficiency">{{ deficiency }}</li>
                                        </ul>
                                      </div>
                                      <div v-if="getSuggestions(result).length > 0" class="suggestions-section">
                                        <strong>Suggestions:</strong>
                                        <ul class="suggestion-list">
                                          <li v-for="suggestion in getSuggestions(result)" :key="suggestion">{{ suggestion }}</li>
                                        </ul>
                                      </div>
                                    </div>
                                  </div>
                                  <div v-else class="legacy-confirmation">
                                    {{ getDisplayText(getReasoning(result) || getFullEvidence(result), 'confirmation', result.id) || 'Requirement met' }}
                                  </div>
                                </div>
                              </div>

                              <!-- Evidence Section (if separate from reasoning) -->
                              <div v-if="getFullEvidence(result) && getReasoning(result)" class="result-text evidence">
                                <div class="evidence-header">
                                  <strong>Evidence:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getFullEvidence(result))"
                                    @click="toggleExpanded('evidence', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('evidence', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('evidence', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <div class="evidence-content" :class="{ 'expanded': isExpanded('evidence', result.id) }">
                                  {{ getDisplayText(getFullEvidence(result), 'evidence', result.id) }}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- HITL Override Section for Passed Results -->
                          <div class="result-actions mt-2">
                            <div class="feedback-actions">
                              <button 
                                @click="submitRequirementFeedback(result.id, selectedRun.subject_id, 'ACCURATE')" 
                                :class="['feedback-btn', { active: result.user_feedback_accuracy === 'ACCURATE' }]"
                                title="Mark as Accurate - AI correctly identified this as passed">
                                <i class="fas fa-thumbs-up"></i>
                              </button>
                              <button 
                                @click="submitRequirementFeedback(result.id, selectedRun.subject_id, 'PARTIALLY_CORRECT')" 
                                :class="['feedback-btn', { active: result.user_feedback_accuracy === 'PARTIALLY_CORRECT' }]"
                                title="Mark as Partially Correct - AI got some parts right but missed details">
                                <i class="fas fa-adjust"></i>
                              </button>
                              <button 
                                @click="submitRequirementFeedback(result.id, selectedRun.subject_id, 'INACCURATE')" 
                                :class="['feedback-btn', { active: result.user_feedback_accuracy === 'INACCURATE' }]"
                                title="Mark as Inaccurate - AI got this wrong">
                                <i class="fas fa-thumbs-down"></i>
                              </button>
                              
                              <!-- Enhanced Correction Button - Shows for any feedback -->
                              <button 
                                v-if="result.user_feedback_accuracy && result.user_feedback_accuracy !== 'NONE'"
                                @click="openCorrectionModal(result)"
                                :class="['btn btn-sm ms-2', getCorrectionButtonClass(result)]">
                                <i class="fas fa-edit-medical"></i> 
                                {{ getCorrectionButtonText(result) }}
                              </button>
                              
                              <!-- Show correction summary if available -->
                              <div v-if="result.correct_result_should_be" class="correction-summary mt-2">
                                <small class="text-muted">
                                  <i class="fas fa-info-circle"></i>
                                  Correction: Should be <span :class="['badge', 'badge-sm', getCorrectionResultBadgeClass(result.correct_result_should_be)]">{{ result.correct_result_should_be }}</span>
                                  <span v-if="result.correction_categories && result.correction_categories.length > 0">
                                    - Issues: {{ result.correction_categories.join(', ') }}
                                  </span>
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Unclear/Pending Results -->
                    <div v-if="unclearResults.length > 0" class="result-section unclear-section">
                      <h4 class="section-header unclear-header">
                        <i class="fas fa-question-circle"></i> Needs Review ({{ unclearResults.length }})
                      </h4>
                      <div 
                        v-for="result in unclearResults" 
                        :key="result.id" 
                        class="result-item unclear-item"
                      >
                        <div class="result-content">
                          <div class="result-badges">
                            <span class="severity-badge" :class="`severity-${(result.requirement_severity?.toLowerCase()) ?? 'unknown'}`">
                              {{ result.requirement_severity || 'MED' }}
                            </span>
                            <span v-if="getConfidenceScore(result)" 
                                  class="confidence-badge" 
                                  :class="getConfidenceClass(getConfidenceScore(result))"
                                  :title="`AI Confidence: ${Math.round(getConfidenceScore(result) * 100)}%`">
                              <i class="fas fa-brain"></i> {{ Math.round(getConfidenceScore(result) * 100) }}%
                            </span>
                          </div>
                          <div class="result-message">
                            <span class="result-icon">⚠️</span>
                            <div class="result-details">
                              <!-- Review Status Section -->
                              <div class="result-text review">
                                <div class="review-header">
                                  <strong>Status:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getReasoning(result) || getFullEvidence(result))"
                                    @click="toggleExpanded('review', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('review', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('review', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <div class="review-content" :class="{ 'expanded': isExpanded('review', result.id) }">
                                  {{ getDisplayText(getReasoning(result) || getFullEvidence(result), 'review', result.id) || 'Manual review required' }}
                                </div>
                              </div>

                              <!-- Evidence Section (if separate from reasoning) -->
                              <div v-if="getFullEvidence(result) && getReasoning(result)" class="result-text evidence">
                                <div class="evidence-header">
                                  <strong>Evidence:</strong>
                                  <button 
                                    v-if="shouldShowExpandButton(getFullEvidence(result))"
                                    @click="toggleExpanded('evidence', result.id)" 
                                    class="expand-btn"
                                    :title="isExpanded('evidence', result.id) ? 'Show less' : 'Show more'">
                                    <i :class="isExpanded('evidence', result.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                  </button>
                                </div>
                                <div class="evidence-content" :class="{ 'expanded': isExpanded('evidence', result.id) }">
                                  {{ getDisplayText(getFullEvidence(result), 'evidence', result.id) }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            
            <!-- Added Action Log Section -->
            <div v-if="selectedRun?.action_log && (selectedRun.action_log.actions_executed?.length || selectedRun.action_log.status_changes_applied?.length)" class="action-log-results">
              <h3>Actions Log</h3>
              <div v-if="selectedRun.action_log.actions_executed?.length">
                <h4>Executed Actions</h4>
                <table class="action-log-table">
                   <thead><tr><th>Action</th><th>Type</th><th>Status</th><th>Details</th><th>Error</th><th>Duration</th></tr></thead>
                   <tbody>
                      <tr v-for="(action, idx) in selectedRun.action_log.actions_executed" :key="'action-'+idx">
                         <td>{{ action.action_name }}</td>
                         <td>{{ action.action_type }}</td>
                         <td :class="action.status">{{ action.status }}</td>
                         <td>
                            <div v-if="action.details && action.action_type === 'NOTIFICATION'">
                                <small>To: {{ action.details.recipients?.join(', ') || 'N/A' }}</small><br>
                                <small>Subject: {{ action.details.subject || 'N/A' }}</small>
                            </div>
                            <div v-else-if="action.details">
                                <small>{{ JSON.stringify(action.details) }}</small>
                            </div>
                            <span v-else class="hint">--</span>
                         </td>
                         <td>{{ action.error || '--' }}</td>
                         <td>{{ action.duration ? action.duration.toFixed(2) + 's' : '--' }}</td>
                      </tr>
                   </tbody>
                </table>
              </div>
              <div v-if="selectedRun.action_log.status_changes_applied?.length">
                <h4>Applied Status Changes</h4>
                 <table class="action-log-table">
                   <thead><tr><th>Change</th><th>Target</th><th>New Status</th><th>Prop.</th><th>Status</th><th>Error</th><th>Duration</th></tr></thead>
                   <tbody>
                      <tr v-for="(sc, idx) in selectedRun.action_log.status_changes_applied" :key="'sc-'+idx">
                         <td>{{ sc.name }}</td>
                         <td>{{ sc.target }}</td>
                         <td>{{ sc.new_status }}</td>
                         <td>{{ sc.propagated ? 'Yes' : 'No' }}</td>
                         <td :class="sc.status">{{ sc.status }}</td>
                         <td>{{ sc.error || '--' }}</td>
                         <td>{{ sc.duration ? sc.duration.toFixed(2) + 's' : '--' }}</td>
                      </tr>
                   </tbody>
                </table>
              </div>
            </div>
            <!-- End Action Log Section -->

            <!-- Policy Exclusion Scan Results -->
            <div v-if="selectedRun?.run_metadata?.policy_exclusion_scan" class="exclusion-scan-results">
              <h3><i class="fas fa-search-minus"></i> Policy Exclusion Scan</h3>
              <div class="exclusion-scan-meta">
                <span class="scan-doc-badge">
                  <i class="fas fa-file-alt"></i> {{ selectedRun.run_metadata.policy_exclusion_scan.document_type || 'Policy Document' }}
                </span>
                <span v-if="selectedRun.run_metadata.policy_exclusion_scan.status" 
                      :class="['scan-status-badge', `scan-${selectedRun.run_metadata.policy_exclusion_scan.status.toLowerCase()}`]">
                  {{ selectedRun.run_metadata.policy_exclusion_scan.status }}
                </span>
              </div>

              <!-- Exclusions Found -->
              <div v-if="selectedRun.run_metadata.policy_exclusion_scan.exclusions_found?.length" class="exclusion-group">
                <h4 class="exclusion-group-title warn"><i class="fas fa-ban"></i> Exclusions Found</h4>
                <div class="exclusion-chips">
                  <span v-for="excl in selectedRun.run_metadata.policy_exclusion_scan.exclusions_found"
                        :key="excl" class="exclusion-chip warn">{{ excl }}</span>
                </div>
              </div>
              <div v-else class="exclusion-none-found">
                <i class="fas fa-check-circle"></i> No material exclusions detected
              </div>

              <!-- Endorsements Found -->
              <div v-if="selectedRun.run_metadata.policy_exclusion_scan.endorsements_found?.length" class="exclusion-group">
                <h4 class="exclusion-group-title info"><i class="fas fa-paperclip"></i> Endorsements Found</h4>
                <div class="exclusion-chips">
                  <span v-for="end in selectedRun.run_metadata.policy_exclusion_scan.endorsements_found"
                        :key="end" class="exclusion-chip info">{{ end }}</span>
                </div>
              </div>

              <!-- Flags / Alerts -->
              <div v-if="selectedRun.run_metadata.policy_exclusion_scan.flags?.length" class="exclusion-group">
                <h4 class="exclusion-group-title danger"><i class="fas fa-flag"></i> Flags Requiring Review</h4>
                <ul class="exclusion-flag-list">
                  <li v-for="flag in selectedRun.run_metadata.policy_exclusion_scan.flags" :key="flag">{{ flag }}</li>
                </ul>
              </div>

              <!-- Raw Summary -->
              <div v-if="selectedRun.run_metadata.policy_exclusion_scan.summary" class="exclusion-summary">
                <label>Analysis Summary</label>
                <p>{{ selectedRun.run_metadata.policy_exclusion_scan.summary }}</p>
              </div>
            </div>
            <!-- End Exclusion Scan Section -->

            <div v-else class="empty-state">
              <p>No requirement results available.</p>
            </div>
                </div>
              </div>
            </div>
          </div>
        
        <div class="modal-footer">
          <button @click="closeResultsDialog" class="btn btn-secondary">Close</button>
          <button @click="exportResults(selectedRun)" class="btn btn-primary">Export PDF</button>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <div v-if="showUploadModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h2>Upload Document</h2>
          <button @click="closeUploadModal" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <p>Select a file to upload - AI will automatically classify the document type:</p>
          
          <div class="form-group">
            <label for="file">File:</label>
            <div class="file-upload-container">
              <input type="file" id="file" @change="handleFileUpload" required>
              <div class="file-upload-label">
                <i class="fas fa-upload"></i>
                <span>{{ uploadData.file ? uploadData.file.name : 'Click to select file' }}</span>
              </div>
            </div>
          </div>
          
          <!-- Classification Progress -->
          <div v-if="uploadStatus.classifying" class="classification-progress">
            <i class="fas fa-robot fa-spin"></i>
            <span>AI is analyzing document type...</span>
          </div>
          
          <!-- Classification Result -->
          <div v-if="uploadStatus.classificationResult" class="classification-result">
            <div class="classification-header">
              <i class="fas fa-check-circle"></i>
              <strong>Document Classified</strong>
            </div>
            <div class="classification-info">
              <span class="doc-type-badge">{{ formatDocumentType(uploadStatus.classificationResult.primary_type) }}</span>
              <span class="confidence-badge" :class="getConfidenceClass(uploadStatus.classificationResult.confidence)">
                {{ Math.round(uploadStatus.classificationResult.confidence * 100) }}%
              </span>
              <div v-if="uploadStatus.classificationResult.coverage_types && uploadStatus.classificationResult.coverage_types.length" class="coverage-info">
                Coverage: {{ uploadStatus.classificationResult.coverage_types.map(formatCoverageType).join(', ') }}
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeUploadModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="uploadDocument" 
            class="btn btn-primary"
            :disabled="!canUpload"
          >
            {{ uploadStatus.uploading ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ADDED: Add Compliance Subject Modal -->
    <AddComplianceSubjectModal
      :show="showAddSubjectModal"
      :project-id="projectId"
      :existing-subject-entity-ids="complianceSubjectEntityIds"
      :entity-term-singular="entityTerm.singular"
      :entity-term-plural="entityTerm.plural"
      @close="showAddSubjectModal = false"
      @subject-added="handleSubjectAdded"
    />

    <!-- ADDED Entity Manager Modal -->
    <EntityManagerModal
       :show="showEntityManagerModal"
       :project-id="projectId"
       @close="showEntityManagerModal = false"
       @entities-updated="refreshData"
    />
    
    <!-- NEW: Requirement Editor Modal -->
    <RequirementEditorModal
      :show="showRequirementEditorModal"
      :requirement-id="editingRequirementId"
      @close="closeRequirementEditor"
      @saved="handleRequirementSaved"
    />

    <!-- ADDED Upload Request Modal/Form (as a modal overlay) -->
    <div v-if="showRequestDocsModal" class="modal-overlay" @click.self="closeRequestDocsModal">
        <div class="modal-container">
            <!-- Using UploadRequestForm directly -->
            <UploadRequestForm
              :project-id="projectId"
              :document-types="[]" 
              :workflows="[]" 
              :subject-context="subjectForRequestModal"
              @request-sent="handleRequestSent"
              @close="closeRequestDocsModal"
            />
             <!-- Optional: Add explicit close button if form doesn't have one -->
             <button @click="closeRequestDocsModal" style="position: absolute; top: 10px; right: 10px; background:none; border:none; font-size: 1.5rem; cursor:pointer;">&times;</button>
        </div>
    </div>

    <!-- ADDED: Create Subject from COI Modal -->
    <div v-if="showCreateFromCoiModal" class="modal-overlay">
      <div class="modal-container modern-coi-modal">
        <div class="modal-header modern-coi-header">
          <!-- Dynamically change title based on step -->
          <div class="header-content">
            <div class="step-indicator" v-if="showCoiReviewStep">
              <span class="step-number">2</span>
              <span class="step-text">Review & Validate</span>
            </div>
            <div class="step-indicator" v-else>
              <span class="step-number">1</span>
              <span class="step-text">Select Document</span>
            </div>
            <h2 v-if="!showCoiReviewStep">Create Subject from Certificate of Insurance</h2>
            <h2 v-else>Review Extracted Data</h2>
          </div>
          <button @click="closeCreateFromCoiModal" class="modal-close modern-close">&times;</button>
        </div>
        
        <!-- Modal Body: Show Form OR Confirmation -->
        <div class="modal-body modern-coi-body">
        
          <!-- Step 1: Form (if confirmation not shown) -->
          <div v-if="!showCoiReviewStep" class="upload-form-container">
              <!-- Option 1: Select Existing Document -->
              <div class="form-section">
                 <h3><i class="fas fa-file-pdf"></i> Option 1: Select Existing COI Document</h3>
                 <div class="input-group">
                   <label for="coi-document-select" class="form-label">Choose from uploaded documents:</label>
                   <select 
                      id="coi-document-select" 
                      v-model="selectedCoiDocumentId" 
                      class="form-select modern-select" 
                      :disabled="!!newCoiFile || loadingDocumentsForCoi" 
                      @change="clearNewCoiFile">
                     <option value="" disabled>-- Please select a COI --</option>
                     <option v-for="doc in coiDocuments" :key="doc.id" :value="doc.id">
                       {{ doc.name }} ({{ formatDateOnly(doc.created_at) }})
                     </option>
                   </select>
                   <div v-if="loadingDocumentsForCoi" class="loading-inline">
                     <div class="spinner-sm"></div>
                     <span>Loading COI documents...</span>
                   </div>
                   <div v-else-if="coiDocuments.length === 0 && !newCoiFile" class="empty-state-small">
                     <i class="fas fa-info-circle"></i>
                     <span>No Certificate of Insurance documents found in this project.</span>
                   </div>
                 </div>
              </div>
    
              <!-- Separator -->
              <div class="form-separator">
                <span class="separator-text">OR</span>
              </div>
    
              <!-- Option 2: Upload New Document -->
              <div class="form-section">
                <h3><i class="fas fa-upload"></i> Option 2: Upload New COI File</h3>
                <div class="input-group">
                  <label for="new-coi-file" class="form-label">Upload a new document:</label>
                  <input 
                      type="file" 
                      id="new-coi-file" 
                      @change="handleNewCoiUpload" 
                      class="form-control modern-file-input" 
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" 
                      :disabled="!!selectedCoiDocumentId">
                  <div v-if="newCoiFile" class="file-selected">
                    <i class="fas fa-check-circle"></i>
                    <span>Selected: {{ newCoiFile.name }}</span>
                  </div>
                </div>
              </div>
              
              <!-- Loading/Error state during SUBMISSION -->
              <div v-if="isUploadingNewCoi" class="status-message loading">
                  <span class="spinner-sm"></span> 
                  <span>Uploading new COI file...</span>
              </div>
              <div v-if="isCreatingSubjectFromCoi" class="status-message loading">
                  <span class="spinner-sm"></span> 
                  <span>Processing COI and creating subject...</span>
              </div>
              <div v-if="createFromCoiError" class="status-message error">
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>{{ createFromCoiError }}</span>
              </div>
          </div>
          
          <!-- Step 2: Split-Pane Review Layout -->
          <div v-else-if="parsedCoiData" class="split-pane-container">
            <div class="split-pane-layout">
              <!-- Left Panel: Document Viewer -->
              <div class="document-panel">
                <div class="panel-header">
                  <h4><i class="fas fa-file-alt"></i> Source Document</h4>
                  <p class="panel-subtitle">Review the original COI to validate extracted data</p>
                </div>
                <div class="document-viewer">
                  <div v-if="currentCoiDocument" class="document-content">
                    <div class="document-info">
                      <h5>{{ currentCoiDocument.name }}</h5>
                      <span class="document-meta">Uploaded: {{ formatDateOnly(currentCoiDocument.created_at) }}</span>
                      <!-- Debug info can be removed once PDF viewing is confirmed working -->
                      <!-- <div style="font-size: 10px; color: #666; margin-top: 5px;">
                        DEBUG: {{ JSON.stringify(currentCoiDocument, null, 2) }}<br>
                        CONSTRUCTED URL: {{ getDocumentUrl(currentCoiDocument) }}<br>
                        <a :href="getDocumentUrl(currentCoiDocument)" target="_blank" style="color: blue;">🔗 Test Direct Link</a>
                      </div> -->
                    </div>
                    <div class="document-actions">
                      <a :href="getDocumentUrl(currentCoiDocument)" 
                         target="_blank" 
                         class="btn btn-outline btn-sm">
                        <i class="fas fa-external-link-alt"></i> View Full Size
                      </a>
                      <button @click="handleDownload(currentCoiDocument)" 
                         :disabled="downloading"
                         class="btn btn-outline btn-sm">
                        <i :class="downloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
                        {{ downloading ? 'Downloading...' : 'Download' }}
                      </button>
                    </div>
                    <!-- Document preview area -->
                    <div class="document-preview">
                      <div v-if="isPDF(currentCoiDocument)" class="pdf-container">
                        <!-- PDF embed with same-origin support -->
                        <embed :src="getDocumentUrl(currentCoiDocument)" 
                               type="application/pdf"
                               class="pdf-preview"
                               title="COI Document Preview" />
                        <div class="pdf-fallback">
                          <p><i class="fas fa-file-pdf"></i> PDF Viewer</p>
                          <p class="small text-muted">If the PDF doesn't display above, <a :href="getDocumentUrl(currentCoiDocument)" target="_blank">open in new tab</a></p>
                        </div>
                      </div>
                      <div v-else class="preview-placeholder">
                        <i class="fas fa-file-alt"></i>
                        <p>Document preview not available for this file type</p>
                        <p class="small text-muted">Click "View Full Size" to open in new tab</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-document">
                    <i class="fas fa-exclamation-triangle"></i>
                    <p>Could not load document information</p>
                    <!-- DEBUG: Show why no document -->
                    <div style="font-size: 10px; color: #666; text-align: left; margin-top: 10px;">
                      DEBUG INFO:<br>
                      selectedCoiDocumentId: {{ selectedCoiDocumentId }}<br>
                      newCoiFile: {{ newCoiFile ? newCoiFile.name : 'null' }}<br>
                      parsedCoiData?.document_id: {{ parsedCoiData?.document_id }}<br>
                      uploadedDocumentInfo: {{ uploadedDocumentInfo ? uploadedDocumentInfo.id : 'null' }}<br>
                      coiDocuments.length: {{ coiDocuments.length }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Right Panel: Extracted Data -->
              <div class="extracted-data-panel">
                <div class="panel-header">
                  <h4><i class="fas fa-robot"></i> Extracted Information</h4>
                  <p class="panel-subtitle">Verify and edit the information extracted by AI</p>
                </div>
                <div class="extracted-data-content">
                  <div class="review-intro">
                    <p>Please confirm the primary <strong>Compliance Subject</strong> was correctly identified below. Edit any incorrect information, then confirm to create the records.</p>
                  </div>
              
              <!-- 1. Subject Selection -->
              <div class="subject-selection-card">
                  <label class="form-label">Which entity should be tracked as the Compliance Subject?</label> 
                  <!-- Radio buttons remain the same -->
                  <div class="subject-options-container">
                     <div :class="['form-check', 'mb-1', {'recommended-option': selectedSubjectSourceKey === 'policyholder'}]" 
                          v-if="parsedCoiData.policyholder && parsedCoiData.policyholder.name">
                         <input class="form-check-input" type="radio" name="subjectSource" id="subjectPolicyholder" value="policyholder" v-model="selectedSubjectSourceKey">
                         <label class="form-check-label" for="subjectPolicyholder" title="Entity identified by LLM as the Policyholder">
                           <!-- UPDATED Label & Add Recommended Badge -->
                           <span class="badge bg-success me-1" v-if="selectedSubjectSourceKey === 'policyholder'">Recommended</span> 
                           LLM "Policyholder": <i>{{ parsedCoiData.policyholder.name }}</i>
                         </label>
                     </div>
                     <div :class="['form-check', 'mb-1', {'recommended-option': selectedSubjectSourceKey === 'certificate_recipient'}]" 
                          v-if="parsedCoiData.certificate_recipient && parsedCoiData.certificate_recipient.name">
                         <input class="form-check-input" type="radio" name="subjectSource" id="subjectRecipient" value="certificate_recipient" v-model="selectedSubjectSourceKey">
                         <label class="form-check-label" for="subjectRecipient" title="Entity identified by LLM as the Certificate Recipient/Holder"> 
                            <!-- UPDATED Label & Add Recommended Badge -->
                           <span class="badge bg-success me-1" v-if="selectedSubjectSourceKey === 'certificate_recipient'">Selected</span> 
                           LLM "Recipient/Holder": <i>{{ parsedCoiData.certificate_recipient.name }}</i>
                         </label>
                     </div>
                      <div :class="['form-check', 'mb-1', {'recommended-option': selectedSubjectSourceKey === 'insured_box_entity'}]" 
                           v-if="parsedCoiData.insured_box_entity && parsedCoiData.insured_box_entity.name">
                         <input class="form-check-input" type="radio" name="subjectSource" id="subjectInsuredBox" value="insured_box_entity" v-model="selectedSubjectSourceKey">
                         <label class="form-check-label" for="subjectInsuredBox" title="Entity found directly in the box labeled 'INSURED'"> 
                            <!-- UPDATED Label & Add Recommended Badge -->
                           <span class="badge bg-success me-1" v-if="selectedSubjectSourceKey === 'insured_box_entity'">Selected</span> 
                           Entity in "INSURED" Box: <i>{{ parsedCoiData.insured_box_entity.name }}</i>
                         </label>
                     </div>
                  </div>
                  <div v-if="!isValidSubjectSelectionPossible" class="text-danger small mt-1">
                      Error: Could not identify a valid entity to select as the subject.
                  </div>
              </div>
              
              <hr>

              <!-- 2. Editable Details Sections -->
              <div class="entity-cards-grid">
                  <!-- Compliance Subject Card -->
                  <div class="entity-card">
                      <div class="entity-card-header">
                        <i class="fas fa-user-check text-primary"></i>
                        <h6>Compliance Subject</h6>
                      </div>
                      <div class="entity-card-content" v-if="selectedSubjectEntityData">
                         <div class="form-field">
                            <label :for="'edit-subject-name'" class="form-label">Subject Name:</label>
                            <input type="text" :id="'edit-subject-name'" v-model="selectedSubjectEntityData.name" class="form-control">
                         </div>
                         <div class="form-field">
                            <label :for="'edit-subject-address'" class="form-label">Subject Address:</label>
                            <textarea :id="'edit-subject-address'" v-model="selectedSubjectEntityData.address" class="form-control" rows="3"></textarea>
                         </div>
                      </div>
                      <div v-else class="entity-card-content-empty">
                          <i class="fas fa-arrow-up"></i>
                          <span>Select the Compliance Subject entity above</span>
                      </div>
                  </div>
                  
                  <!-- Producer/Agency Card -->
                  <div class="entity-card" v-if="parsedCoiData.producer">
                      <div class="entity-card-header">
                        <i class="fas fa-building text-info"></i>
                        <h6>Insurance Producer/Agency</h6>
                      </div>
                      <div class="entity-card-content">
                          <div class="form-field">
                            <label :for="'edit-prod-name'" class="form-label">Agency Name:</label>
                            <input type="text" :id="'edit-prod-name'" v-model="parsedCoiData.producer.name" class="form-control">
                         </div>
                         <div class="form-field">
                            <label :for="'edit-prod-address'" class="form-label">Agency Address:</label>
                            <textarea :id="'edit-prod-address'" v-model="parsedCoiData.producer.address" class="form-control" rows="3"></textarea>
                         </div>
                      </div>
                  </div>
                  <div v-else class="entity-card">
                      <div class="entity-card-header">
                        <i class="fas fa-building text-muted"></i>
                        <h6>Insurance Producer/Agency</h6>
                      </div>
                      <div class="entity-card-content-empty">
                        <i class="fas fa-info-circle"></i>
                        <span>Not extracted from document</span>
                      </div>
                  </div>
                  
                  <!-- Producer Contact / Agent Card -->
                  <div class="entity-card">
                      <div class="entity-card-header">
                        <i class="fas fa-user-tie text-success"></i>
                        <h6>Producer Contact</h6>
                        <span class="required-badge">Required</span>
                      </div>
                      <!-- Edit the *first* contact found -->
                      <div class="entity-card-content" v-if="parsedCoiData.producer_contacts && parsedCoiData.producer_contacts.length > 0">
                          <div class="validation-tip">
                            <i class="fas fa-lightbulb"></i>
                            <span>Please verify and complete contact information. Email is required for notifications.</span>
                          </div>
                          
                          <div class="form-field">
                            <label :for="'edit-agent-name'" class="form-label">Contact Name:</label>
                            <input type="text" :id="'edit-agent-name'" v-model="parsedCoiData.producer_contacts[0].name" class="form-control">
                         </div>
                         
                         <div class="form-field">
                            <label :for="'edit-agent-email'" class="form-label required-field">
                              Email: <span class="required-asterisk">*</span>
                            </label>
                            <input type="email" 
                                   :id="'edit-agent-email'" 
                                   v-model="parsedCoiData.producer_contacts[0].email" 
                                   class="form-control"
                                   :class="{ 'is-invalid': !parsedCoiData.producer_contacts[0].email }"
                                   placeholder="Enter email address (required)"
                                   required>
                            <div v-if="!parsedCoiData.producer_contacts[0].email" class="invalid-feedback">
                              Email is required for contact records
                            </div>
                         </div>
                         
                         <div class="form-field">
                            <label :for="'edit-agent-phone'" class="form-label">Phone:</label>
                            <input type="tel" :id="'edit-agent-phone'" v-model="parsedCoiData.producer_contacts[0].phone" class="form-control" placeholder="(555) 123-4567">
                         </div>
                         
                         <div v-if="parsedCoiData.producer_contacts.length > 1" class="contact-note">
                            <i class="fas fa-info-circle"></i>
                            <span>Only the first extracted contact is shown for editing.</span>
                         </div>
                      </div>
                      <div v-else class="entity-card-content">
                          <div class="validation-tip">
                            <i class="fas fa-lightbulb"></i>
                            <span>Please add producer contact information from the document. Email is required.</span>
                          </div>
                          
                          <div class="form-field">
                            <label class="form-label">Contact Name:</label>
                            <input type="text" 
                                   v-model="manualContactName" 
                                   class="form-control"
                                   placeholder="Enter contact name">
                         </div>
                         
                         <div class="form-field">
                            <label class="form-label required-field">
                              Email: <span class="required-asterisk">*</span>
                            </label>
                            <input type="email" 
                                   v-model="manualContactEmail" 
                                   class="form-control"
                                   :class="{ 'is-invalid': !manualContactEmail }"
                                   placeholder="Enter email address (required)"
                                   required>
                            <div v-if="!manualContactEmail" class="invalid-feedback">
                              Email is required for contact records
                            </div>
                         </div>
                         
                         <div class="form-field">
                            <label class="form-label">Phone:</label>
                            <input type="tel" 
                                   v-model="manualContactPhone" 
                                   class="form-control" 
                                   placeholder="(555) 123-4567">
                         </div>
                      </div>
                  </div>
              </div> 

              <!-- REMOVED Other Identified Entities Section -->
              <!-- <div class="mt-3 border-top pt-3"> ... </div> -->

                  <!-- Loading/Error state during CONFIRMATION -->
                  <div v-if="isConfirmingCoi" class="status-message loading">
                      <span class="spinner-sm"></span> 
                      <span>Creating records in database...</span>
                  </div>
                  <div v-if="createFromCoiError" class="status-message error">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>{{ createFromCoiError }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Fallback if parsing failed before review step -->
          <div v-else class="fallback-error">
              <div class="status-message error">
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>{{ createFromCoiError }}</span>
              </div>
          </div>
          
        </div>
        
        <!-- Modal Footer: Show different buttons based on step -->
        <div class="modal-footer">
          <!-- Buttons for Step 1 (Initial Form) -->
          <button 
             v-if="!showCoiReviewStep" 
             @click="closeCreateFromCoiModal" 
             class="btn btn-secondary">Cancel</button>
          <button 
            v-if="!showCoiReviewStep"
            @click="processCoiDocument" 
            class="btn btn-primary"
            :disabled="(!selectedCoiDocumentId && !newCoiFile) || isCreatingSubjectFromCoi || isUploadingNewCoi"
          >
            <span v-if="isCreatingSubjectFromCoi || isUploadingNewCoi" class="spinner-sm"></span>
            {{ isUploadingNewCoi ? 'Uploading...' : (isCreatingSubjectFromCoi ? 'Processing...' : 'Process COI') }}
          </button>
          
          <!-- Buttons for Step 2 (Review/Edit) -->
          <div v-if="showCoiReviewStep" class="action-options-info mb-3">
            <h6 class="mb-2">📋 Choose Your Next Step:</h6>
            <div class="option-descriptions">
              <div class="option-desc">
                <strong>Create Subject Only:</strong> Add this compliance subject to your project. You can run analysis later.
              </div>
              <div class="option-desc">
                <strong>🚀 Create & Analyze:</strong> Create the subject AND immediately start comprehensive analysis with this PDF. <em>(Recommended - saves time!)</em>
              </div>
            </div>
          </div>
          
          <button 
             v-if="showCoiReviewStep" 
             @click="closeCreateFromCoiModal" 
             class="btn btn-secondary">Cancel</button> 
          <button 
             v-if="showCoiReviewStep" 
             @click="confirmAndCreateSubject(false)" 
             class="btn btn-success"
             :disabled="isConfirmingCoi || !selectedSubjectSourceKey"
          > 
            <span v-if="isConfirmingCoi" class="spinner-sm"></span>
            Create Subject Only
          </button>
          <button 
             v-if="showCoiReviewStep" 
             @click="confirmAndCreateSubject(true)" 
             class="btn btn-primary"
             :disabled="isConfirmingCoi || !selectedSubjectSourceKey"
          > 
            <span v-if="isConfirmingCoi" class="spinner-sm"></span>
            🚀 Create & Analyze
          </button>
        </div>
      </div>
    </div>
    <!-- END ADDED MODAL -->

    <!-- HITL Override Modal -->
    <div v-if="showOverrideModal" class="modal-overlay">
      <div class="modal-container override-modal">
        <div class="modal-header">
          <h3><i class="fas fa-edit"></i> Override LLM Result</h3>
          <button @click="closeOverrideModal" class="modal-close">&times;</button>
        </div>
        
        <div class="modal-body">
          <div v-if="overrideTarget" class="override-details">
            <div class="requirement-summary mb-3">
              <h4>{{ overrideTarget.requirement_name }}</h4>
              <div class="current-result">
                <strong>Current Result:</strong> 
                <span :class="['result-badge', getResultBadgeClass(overrideTarget)]">
                  {{ getResultDisplayText(overrideTarget) }}
                </span>
              </div>
              <div class="evidence-summary mt-2">
                <strong>Evidence:</strong> {{ getEvidenceText(overrideTarget) }}
              </div>
            </div>
            
            <div class="override-form">
              <div class="form-group">
                <label><strong>Correct Result:</strong></label>
                <div class="override-options">
                  <label class="radio-option">
                    <input type="radio" v-model="overrideData.newResult" value="PASS" />
                    <span class="result-badge result-pass">✅ PASS</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" v-model="overrideData.newResult" value="FAIL" />
                    <span class="result-badge result-fail">❌ FAIL</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" v-model="overrideData.newResult" value="PARTIAL" />
                    <span class="result-badge result-partial">⚠️ PARTIAL</span>
                  </label>
                </div>
              </div>
              
              <div class="form-group">
                <label><strong>Reason for Override:</strong></label>
                <select v-model="overrideData.reason" class="form-control">
                  <option value="">Select reason...</option>
                  <option value="incorrect_extraction">Incorrect Data Extraction</option>
                  <option value="wrong_interpretation">Wrong Policy Interpretation</option>
                  <option value="missing_context">Missing Context/Evidence</option>
                  <option value="calculation_error">Mathematical/Calculation Error</option>
                  <option value="policy_format_issue">Policy Format Not Recognized</option>
                  <option value="requirement_ambiguity">Requirement Definition Ambiguous</option>
                  <option value="other">Other (specify below)</option>
                </select>
              </div>
              
              <div class="form-group">
                <label><strong>Additional Comments:</strong></label>
                <textarea v-model="overrideData.comments" 
                         class="form-control" 
                         rows="3" 
                         placeholder="Explain why the LLM was wrong and what the correct interpretation should be..."></textarea>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeOverrideModal" class="btn btn-secondary">Cancel</button>
          <button @click="submitOverride" 
                  class="btn btn-warning"
                  :disabled="!overrideData.newResult || !overrideData.reason">
            <i class="fas fa-save"></i> Apply Override
          </button>
        </div>
      </div>
    </div>
    <!-- END OVERRIDE MODAL -->

    <!-- Enhanced Correction Modal -->
    <RequirementCorrectionModal
      v-if="showCorrectionModal && correctionTarget"
      :current-result="correctionTarget"
      @submit-correction="submitCorrection"
      @close="closeCorrectionModal"
      @error="handleCorrectionError"
    />
    <!-- END CORRECTION MODAL -->

  </div>
</template>

<script>
import api from '@/services/api';
import { useProjectTerminology } from '@/composables/useProjectTerminology';
import AddComplianceSubjectModal from './AddComplianceSubjectModal.vue';
import EntityManagerModal from './EntityManagerModal.vue';
import RequirementEditorModal from './RequirementEditorModal.vue';
import UploadRequestForm from '@/components/UploadRequestForm.vue';
import RequirementCorrectionModal from '@/components/RequirementCorrectionModal.vue';
// REMOVED Unused Import
// import DocumentPartyManagerModal from '@/components/modals/DocumentPartyManagerModal.vue'; 

// Helper to initialize form data for a new contact (MOVED HERE)
const initialNewContactFormData = () => ({
    name: '',
    email: '',
    phone: '',
    title: '',
    is_primary: false,
    user: null // Or appropriate default for user link if implemented
});

export default {
  name: 'ComprehensiveAnalysisDashboard',
  components: {
    AddComplianceSubjectModal,
    EntityManagerModal,
    RequirementEditorModal,
    UploadRequestForm,
    RequirementCorrectionModal,
    // REMOVED Unused Component Registration
    // DocumentPartyManagerModal, 
  },
  props: {
    projectId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      // UI State
      loading: false,
      downloading: false,
      error: null,
      activeTab: 'compliance',
      notification: {
        show: false,
        message: '',
        type: 'info', // info, success, error
        timeout: null
      },
      
      // Data
      checkDefinitions: [],
      checkRuns: [], // Legacy: kept for compatibility with old filtering code
      documents: [],
      documentNames: {}, // Map of document IDs to names
      documentFilePaths: {}, // Map of document IDs to file paths (legacy)
      documentObjects: {}, // Map of document IDs to full document objects with file_url
      requirementResults: [],
      complianceSubjects: [],
      
      // Project for terminology
      project: null,
      
      // Modals
      showRunDialog: false,
      showResultsDialog: false,
      showUploadModal: false,
      showRequirementEditorModal: false,
      editingRequirementId: null,
      selectedCheck: null,
      selectedRun: null,
      selectedDocuments: [],
      
      // Temporal policy context
      analysisReferenceDate: '', // Optional "as-of" date for policy lookup
      todayDate: new Date().toISOString().split('T')[0], // For date picker max
      
      // HITL Override Modal
      showOverrideModal: false,
      overrideTarget: null,
      overrideSubjectId: null,
      overrideData: {
        newResult: '',
        reason: '',
        comments: ''
      },
      
      // Enhanced Correction Modal
      showCorrectionModal: false,
      correctionTarget: null,
      
      // Upload state
      uploadData: {
        name: '',
        file: null
      },
      uploadStatus: {
        uploading: false,
        classifying: false,
        classificationResult: null,
        error: null
      },
      
      // Notification panel (PR1: transparency, PR2: recipient management)
      notificationPreview: null,
      notificationLoading: false,
      sendingNotification: false,
      showSendDrawer: false,
      sendForm: {
        to: '',
        cc: [],
        contact_id: null,
      },
      ccInput: '',
      sendTarget: 'selected',
      comparisonRun: null,
      comparisonResults: [],
      comparisonLoading: false,
      comparisonError: '',
      comparisonPoll: null,
      adoptingComparison: false,

      // Loading states
      loadingDocuments: false,
      loadingResults: false,
      runningCheck: false,
      loadingChecks: false,
      loadingRuns: false,
      loadingSubjects: false,
      errorMessage: null,
      showAddSubjectModal: false,
      showEntityManagerModal: false,
      
      // Expanded states for result details
      expandedStates: {},
      subjectForUpload: null,
      showRequestDocsModal: false,
      subjectForRequestModal: null,
      visibleContacts: {},
      visibleSubjectDetails: {}, // Added state for subject details visibility
      subjectDocuments: {}, // Added state for subject-specific documents
      loadingSubjectDocs: {}, // Added state for loading subject-specific docs
      entityContacts: {},
      loadingContactsFor: null,
      showAddContactForm: {},
      newContactFormData: {},
      addContactError: {},
      isSavingContact: {},
      // State for Document Party Manager Modal
      showDocumentPartyManagerModal: false,
      selectedDocumentIdForParties: null,
      // Subject Agent Manager state removed
      subjectDocumentParties: {}, // ADDED: Store parties for subject docs
      loadingSubjectParties: {}, // ADDED: Loading state for parties
      detailedSubjectData: {}, // ADDED: Store full subject details including formal agents
      loadingFormalAgents: {}, // ADDED: Specific loading state for formal agents fetch
      allEntities: [], // ADDED: Store all entities for selection dropdowns
      selectedAgentToAdd: {}, // ADDED: v-model for Add Agent dropdown per subject
      isSavingAgent: {}, // ADDED: Loading state for agent add/remove actions
      subjectRequirementDefinitions: {}, // Was used temporarily, now replaced by detailed status
      subjectDetailedStatus: {}, // ADDED: Store combined req defs and latest results
      loadingSubjectDetailedStatus: {}, // ADDED: Loading state for the new detailed status fetch
      subjectCheckRuns: {}, // ADDED: Store relevant check runs per subject
      loadingSubjectCheckRuns: {}, // ADDED: Loading state for subject check runs
      activeDetailTab: {}, // ADDED: Tracks active tab per subject { subjectId: 'status' | 'parties' | 'history' | 'agent' }
      // --- ADDED: Agent Intelligence State ---
      agentEvaluations: {}, // Store agent evaluation results per subject
      loadingAgentEvaluation: {}, // Loading state for agent evaluations
      // --- END Agent Intelligence State ---
      // --- ADDED: State for inline new agent form ---
      showAddAgentForm: {},
      newAgentFormData: {},
      isSavingNewAgent: {},
      newAgentError: {},
      // --- END ADDED ---
      showCreateFromCoiModal: false, 
      loadingDocumentsForCoi: false, 
      isCreatingSubjectFromCoi: false, 
      coiDocuments: [], 
      selectedCoiDocumentId: '', 
      createFromCoiError: null, 
      newCoiFile: null, 
      isUploadingNewCoi: false, 
      // --- ADDED: State for COI Review/Edit Step ---
      showCoiReviewStep: false, 
      parsedCoiData: null, // To store raw data from parse endpoint
      // editableCoiData: {}, // Or bind directly to parsedCoiData copy
      selectedSubjectSourceKey: 'policyholder', // Default to using 'policyholder' field
      isConfirmingCoi: false, // Loading state for the final confirmation call
      // Manual contact entry when not extracted
      manualContactName: '',
      manualContactEmail: '',
      manualContactPhone: '',
      // Store uploaded document info for viewer
      uploadedDocumentInfo: null,
      // PDF embed fallback state
      pdfEmbedFailed: false,
      // --- END ADDED ---
              dashboardTitle: 'Compliance Intelligence',
      isEditingDashboardTitle: false,
      complianceSubjectsTitle: 'Compliance Subjects',
      isEditingSubjectsTitle: false,
      expandedResults: {}, // Track which results have expanded details
      
      // Scalability features
      searchQuery: '',
      statusFilter: '',
      currentPage: 1,
      pageSize: 50,
      sortField: 'subject_entity_name',
      sortDirection: 'asc',
      selectedSubjects: [],
      searchResults: [],
      isSearching: false,
      searchTotal: 0,
      debouncedSearch: null,
      totalSubjects: 0,
      hasMoreSubjects: false,
      
      // Coverage matrix state
      coverageSummary: {}, // { [subjectId]: { required_coverages, present, status } }
      loadingCoverage: {}, // { [subjectId]: boolean }
      
      // Response intelligence state
      responseIntelligence: {}, // { [subjectId]: { analytics, prediction } }
      loadingResponseIntelligence: {}, // { [subjectId]: boolean }
    }
  },
  computed: {
    comparisonOverview() {
      return this.comparisonRun?.results_summary?.agent_overview || '';
    },
    comparisonInstructions() {
      const items = this.comparisonRun?.results_summary?.agent_instructions;
      return Array.isArray(items) ? items : [];
    },
    comparisonHasGap() {
      return Number(this.comparisonRun?.results_summary?.failed_count || 0) > 0;
    },
    comparisonRows() {
      const before = new Map(
        (this.requirementResults || []).map((row) => [
          row.requirement_code || row.requirement?.code,
          row,
        ]),
      );
      return (this.comparisonResults || []).map((row) => {
        const code = row.requirement_code || row.requirement?.code;
        const prior = before.get(code);
        const label = (passed) => (passed ? 'passing' : 'failing');
        return {
          code,
          name: row.requirement_name || row.requirement?.name || code,
          before: prior ? label(prior.passed) : 'not on the earlier run',
          after: label(row.passed),
        };
      });
    },
    // Dynamic terminology based on project configuration
    entityTerm() {
      if (!this.project) {
        return { singular: 'Company', plural: 'Companies' };
      }
      
      const { entityTerm } = useProjectTerminology({ value: this.project });
      return entityTerm.value;
    },
    
    locationTerm() {
      if (!this.project) {
        return { singular: 'Location', plural: 'Locations' };
      }
      
      const { locationTerm } = useProjectTerminology({ value: this.project });
      return locationTerm.value;
    },
    
    // UPDATED: These computed properties now work with subject-specific data
    // Note: These are primarily used for global stats, but with lazy loading
    // they may not have complete data until subjects are expanded
    completedRuns() {
      // Combine all loaded subject check runs
      const allRuns = Object.values(this.subjectCheckRuns).flat();
      return allRuns.filter(run => run.status === 'COMPLETED');
    },
    inProgressRuns() {
      const allRuns = Object.values(this.subjectCheckRuns).flat();
      return allRuns.filter(run => ['PENDING', 'IN_PROGRESS'].includes(run.status));
    },
    failedRuns() {
      const allRuns = Object.values(this.subjectCheckRuns).flat();
      return allRuns.filter(run => run.status === 'FAILED');
    },
    // Sort runs by date (newest first)
    sortedRuns() {
      const allRuns = Object.values(this.subjectCheckRuns).flat();
      return [...allRuns].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
    },

    // Streamlined Results: Filter by outcome for efficient scanning
    failedResults() {
      return this.requirementResults.filter(result => !this.getResultPassed(result));
    },
    passedResults() {
      return this.requirementResults.filter(result => this.getResultPassed(result));
    },
    unclearResults() {
      return this.requirementResults.filter(result => {
        const outcome = this.getResultDisplayText(result);
        return outcome === 'UNCLEAR' || outcome === 'PENDING' || outcome === 'ERROR';
      });
    },

    // PR1: notification transparency
    notificationPanelClass() {
      if (!this.notificationPreview) return '';
      if (this.notificationPreview.auto_sent) return 'notification-panel--sent';
      if (this.notificationPreview.failed_count === 0) return 'notification-panel--info';
      return 'notification-panel--pending';
    },
    notificationBlockingExplanations() {
      const reasons = this.notificationPreview?.blocking_reasons || [];
      const labels = {
        no_compliance_subject: 'No compliance subject is linked to this analysis.',
        no_contact_email: 'No agent contact email could be resolved.',
        no_failures: 'All requirements passed — there are no gaps to report.',
        document_suppressed:
          'This document was uploaded internally and notifications were suppressed for testing.',
        project_opt_out:
          'The project has not opted in to automatic notifications (Project setting: notify_on_upload_completion).',
      };
      return reasons.map((r) => labels[r] || r);
    },
    notificationHistory() {
      // PR2: server now returns the audit history from NotificationLog. Fall
      // back to the legacy results_summary blob for in-flight check runs.
      if (this.notificationPreview?.history) {
        return this.notificationPreview.history;
      }
      return this.selectedRun?.results_summary?.notification_history || [];
    },
    candidateContacts() {
      return this.notificationPreview?.candidate_contacts || [];
    },
    sendButtonTooltip() {
      if (!this.notificationPreview) return '';
      if (this.notificationPreview.can_send) {
        const r = this.notificationPreview.recipient?.email || 'a contact you choose';
        return `Open send dialog (defaults to ${r})`;
      }
      const explanations = this.notificationBlockingExplanations;
      if (explanations.length > 0) {
        return `Cannot send: ${explanations.join(' ')}`;
      }
      return 'Cannot send notification';
    },

    // Check if upload is ready (Name field removed)
    canUpload() {
      return this.uploadData.file && 
             !this.uploadStatus.uploading &&
             !this.uploadStatus.classifying;
    },
    complianceSubjectEntityIds() {
        return this.complianceSubjects.map(subject => subject.subject_entity);
    },
    
    // Scalability computed properties
    filteredSubjects() {
      // If user is searching (2+ chars), use backend search results
      if (this.searchQuery && this.searchQuery.length >= 2) {
        // Return search results if we have them, otherwise show empty while searching
        return this.isSearching ? [] : this.searchResults;
      }
      
      // Otherwise show the loaded subjects (25 most recent)
      let filtered = [...this.complianceSubjects];
      
      // Apply status filter
      if (this.statusFilter === 'ALL') {
        // Show everything including archived
      } else if (this.statusFilter === 'ARCHIVED') {
        // Show only archived
        filtered = filtered.filter(subject => 
          subject.current_status === 'ARCHIVED'
        );
      } else if (this.statusFilter) {
        // Show specific status (excluding archived)
        filtered = filtered.filter(subject => 
          subject.current_status === this.statusFilter
        );
      } else {
        // Default: exclude archived subjects
        filtered = filtered.filter(subject => 
          subject.current_status !== 'ARCHIVED'
        );
      }
      
      // Apply sorting
      filtered.sort((a, b) => {
        const aVal = a[this.sortField] || '';
        const bVal = b[this.sortField] || '';
        
        if (this.sortDirection === 'asc') {
          return aVal.toString().localeCompare(bVal.toString());
        } else {
          return bVal.toString().localeCompare(aVal.toString());
        }
      });
      
      return filtered;
    },
    
    paginatedSubjects() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredSubjects.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredSubjects.length / this.pageSize);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      const current = this.currentPage;
      const total = this.totalPages;
      
      let start = Math.max(1, current - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    allSubjectsSelected() {
      return this.paginatedSubjects.length > 0 && 
             this.selectedSubjects.length === this.paginatedSubjects.length &&
             this.paginatedSubjects.every(subject => this.selectedSubjects.includes(subject.id));
    },
    
    compliantCount() {
      return this.complianceSubjects.filter(subject => 
        subject.current_status === 'COMPLIANT'
      ).length;
    },
    
    nonCompliantCount() {
      return this.complianceSubjects.filter(subject => 
        subject.current_status !== 'COMPLIANT'
      ).length;
    },
    // --- ADDED: Computed property to get agent names for a subject ---
    subjectAgents() {
      return (subjectId) => {
        const parties = this.subjectDocumentParties[subjectId];
        if (!parties || parties.length === 0) {
          return [];
        }
        // Define agent roles (adjust if needed, e.g., add PRODUCER)
        const agentRoles = ['AGENT', 'PRODUCER']; 
        
        const agentNames = parties
          .filter(party => agentRoles.includes(party.role))
          .map(party => party.entity_name);
          
        // Return unique names
        return [...new Set(agentNames)];
      };
    },
    // --- ADDED: Computed property to get FORMAL agent details ---
    formalAgentDetails() {
      return (subjectId) => {
        return this.detailedSubjectData[subjectId]?.agent_details || [];
      };
    },
    // --- ADDED: Computed property to get OTHER document parties ---
    otherDocumentParties() {
        return (subjectId) => {
            const parties = this.subjectDocumentParties[subjectId];
            const subjectInfo = this.detailedSubjectData[subjectId]; // Get full subject info
            const formalAgents = this.formalAgentDetails(subjectId); // Get formal agents
            // REMOVE: Incorrect modification of state within computed property
            
            if (!parties || parties.length === 0 || !subjectInfo) {
                return [];
            }

            const subjectEntityId = subjectInfo.subject_entity;
            const formalAgentIds = formalAgents.map(agent => agent.id);
            const excludedEntityIds = new Set([subjectEntityId, ...formalAgentIds]);
            const excludedRoles = new Set(['AGENT', 'PRODUCER', 'INSURED']); // Roles handled elsewhere or implied

            const otherPartiesMap = {};

            parties.forEach(party => {
                if (!excludedEntityIds.has(party.entity) && !excludedRoles.has(party.role)) {
                    if (!otherPartiesMap[party.entity]) {
                        otherPartiesMap[party.entity] = {
                            entityId: party.entity,
                            entityName: party.entity_name,
                            roles: new Set()
                        };
                    }
                    otherPartiesMap[party.entity].roles.add(party.role);
                }
            });

            // Convert map to array and roles Set to array
            return Object.values(otherPartiesMap).map(info => ({
                ...info,
                roles: Array.from(info.roles)
            }));
        };
    },
    
    // --- ADDED: Computed property to get grouped entities with relationships ---
    getGroupedEntities() {
        return (subjectId) => {
            const entities = [];
            const subjectInfo = this.detailedSubjectData[subjectId];
            const formalAgents = this.formalAgentDetails(subjectId);
            const otherParties = this.otherDocumentParties(subjectId);
            const documentParties = this.subjectDocumentParties[subjectId] || [];
            
            if (!subjectInfo) {
                return entities;
            }

            // 1. Add the primary subject entity
            const subjectRelationships = [];
            const subjectDocumentRoles = documentParties
                .filter(party => party.entity === subjectInfo.subject_entity)
                .map(party => party.role);
            
            // Always add "Primary Subject" relationship
            subjectRelationships.push({ type: 'PRIMARY', label: 'Primary Subject (Insured)' });
            
            // Add any additional document roles
            subjectDocumentRoles.forEach(role => {
                if (role !== 'INSURED') {
                    subjectRelationships.push({ type: 'DOCUMENT', label: `Document Role: ${role}` });
                }
            });

            entities.push({
                entityId: subjectInfo.subject_entity,
                name: subjectInfo.subject_entity_name,
                icon: 'fas fa-user-tie',
                isPrimary: true,
                isFormalAgent: false,
                relationships: subjectRelationships,
                documentCount: this.subjectDocuments[subjectId]?.length || 0,
                primaryContact: this.getPrimaryContactForEntity(subjectInfo.subject_entity)
            });

            // 2. Add formal agents
            formalAgents.forEach(agent => {
                const agentRelationships = [];
                
                // Always add formal agent relationship
                agentRelationships.push({ type: 'FORMAL', label: 'Formal Agent/Broker' });
                
                // Add document roles if they exist in document parties
                const agentDocumentRoles = documentParties
                    .filter(party => party.entity === agent.id)
                    .map(party => party.role);
                    
                agentDocumentRoles.forEach(role => {
                    if (!['AGENT', 'PRODUCER'].includes(role)) {
                        agentRelationships.push({ type: 'DOCUMENT', label: `Document Role: ${role}` });
                    }
                });

                entities.push({
                    entityId: agent.id,
                    name: agent.name,
                    icon: 'fas fa-briefcase',
                    isPrimary: false,
                    isFormalAgent: true,
                    relationships: agentRelationships,
                    documentCount: 0, // TODO: Count documents associated with this agent
                    primaryContact: this.getPrimaryContactForEntity(agent.id)
                });
            });

            // 3. Add other document parties
            otherParties.forEach(party => {
                const relationships = party.roles.map(role => ({
                    type: 'DOCUMENT',
                    label: `Document Role: ${role}`
                }));

                entities.push({
                    entityId: party.entityId,
                    name: party.entityName,
                    icon: 'fas fa-user-friends',
                    isPrimary: false,
                    isFormalAgent: false,
                    relationships: relationships,
                    documentCount: 0, // TODO: Count documents associated with this entity
                    primaryContact: this.getPrimaryContactForEntity(party.entityId)
                });
            });

            return entities;
        };
    },
    
    // --- ADDED: Helper method to get primary contact for an entity ---
    getPrimaryContactForEntity() {
        return (entityId) => {
            const contacts = this.entityContacts[entityId];
            if (!contacts || contacts.length === 0) {
                return null;
            }
            
            // First try to find a contact marked as primary
            const primaryContact = contacts.find(contact => contact.is_primary);
            if (primaryContact) {
                return primaryContact;
            }
            
            // If no primary contact, return the first contact
            return contacts[0];
        };
    },
    
    // --- ADDED: Computed property to get entities available for agent selection ---
    availableEntitiesForAgentSelection() {
      return (subjectId) => {
          const subjectInfo = this.detailedSubjectData[subjectId];
          const formalAgents = this.formalAgentDetails(subjectId);

          if (!subjectInfo || !this.allEntities || this.allEntities.length === 0) {
              return [];
          }

          const subjectEntityId = subjectInfo.subject_entity;
          const formalAgentIds = new Set(formalAgents.map(agent => agent.id));
          const agentEntityTypes = ['AGENCY']; // Only show Agencies/Brokers

          return this.allEntities.filter(entity => 
              entity.id !== subjectEntityId && 
              !formalAgentIds.has(entity.id) &&
              agentEntityTypes.includes(entity.entity_type) // Added type filter
          );
      };
    },
    // --- END ADDED ---
    // --- ADDED: Computed property to sort requirement status by severity ---
    sortedRequirementStatus() {
        // Define the severity order mapping
        const severityOrder = {
            'CRITICAL': 4,
            'HIGH': 3,
            'MEDIUM': 2,
            'LOW': 1,
            // Add default for unknown/null severities if needed
            'UNKNOWN': 0,
            null: 0,
            undefined: 0
        };

        return (subjectId) => {
            const detailedStatus = this.subjectDetailedStatus[subjectId];
            if (!detailedStatus || !Array.isArray(detailedStatus)) {
                return [];
            }
            // Create a copy before sorting to avoid mutating original state
            return [...detailedStatus].sort((a, b) => {
                const severityA = severityOrder[a.requirement?.severity?.toUpperCase()] ?? 0;
                const severityB = severityOrder[b.requirement?.severity?.toUpperCase()] ?? 0;
                // Sort descending (higher severity first)
                return severityB - severityA;
            });
        };
    },
    // --- END ADDED ---
    
    // --- ADDED: Get data for the selected subject source --- 
    selectedSubjectEntityData() {
      if (!this.parsedCoiData || !this.selectedSubjectSourceKey) {
        return null; // Or an empty object {}
      }
      // Return the object corresponding to the selected radio button key
      return this.parsedCoiData[this.selectedSubjectSourceKey] || null;
    },
    
    // --- ADDED: Check if any valid subject source can be selected --- 
    isValidSubjectSelectionPossible() {
        if (!this.parsedCoiData) return false;
        const hasPolicyholder = this.parsedCoiData.policyholder && this.parsedCoiData.policyholder.name;
        const hasRecipient = this.parsedCoiData.certificate_recipient && this.parsedCoiData.certificate_recipient.name;
        const hasInsuredBox = this.parsedCoiData.insured_box_entity && this.parsedCoiData.insured_box_entity.name;
        return hasPolicyholder || hasRecipient || hasInsuredBox;
    },
    // --- END ADDED --- 
    // --- ADDED: Check if any 'other' entity will be displayed --- 
    anyOtherEntityShown() {
        if (!this.parsedCoiData) return false;
        const keysToCheck = ['policyholder', 'certificate_recipient', 'insured_box_entity'];
        return keysToCheck.some(key => this.shouldShowOtherEntity(key));
    },

    // --- ADDED: Get the current COI document for display ---
    currentCoiDocument() {
      if (this.selectedCoiDocumentId) {
        return this.coiDocuments.find(doc => doc.id === this.selectedCoiDocumentId);
      }
      
      // If using uploaded file, try to find it in the documents list
      if (this.newCoiFile && this.parsedCoiData?.document_id) {
        const foundDoc = this.coiDocuments.find(doc => doc.id === this.parsedCoiData.document_id);
        
        // If not found in coiDocuments, use the stored uploaded document info
        if (!foundDoc && this.uploadedDocumentInfo && this.uploadedDocumentInfo.id === this.parsedCoiData.document_id) {
          return this.uploadedDocumentInfo;
        }
        
        // Last resort: create a temporary document object from parsed data
        if (!foundDoc && this.parsedCoiData.document_id) {
          return {
            id: this.parsedCoiData.document_id,
            name: this.newCoiFile.name,
            file_url: this.parsedCoiData.file_url || `/media/documents/${this.newCoiFile.name}`,
            created_at: new Date().toISOString(),
            document_type: 'CERTIFICATE'
          };
        }
        
        return foundDoc;
      }
      
      return null;
    }
    // --- END ADDED --- 
  },
  directives: {
    focus: {
      mounted(el) {
        el.focus();
      }
    }
  },
  beforeUnmount() {
    this.stopComparisonPoll();
  },
  mounted() {
    this.refreshData();
    // Load titles from local storage, per-project
    const savedSubjectsTitle = localStorage.getItem(`complianceSubjectsTitle_${this.projectId}`);
    if (savedSubjectsTitle) {
      this.complianceSubjectsTitle = savedSubjectsTitle;
    }
    const savedDashboardTitle = localStorage.getItem(`dashboardTitle_${this.projectId}`);
    if (savedDashboardTitle) {
      this.dashboardTitle = savedDashboardTitle;
    }
    
    // Initialize debounced backend search
    const { debounce } = require('lodash-es');
    this.debouncedSearch = debounce(this.searchEntitiesBackend, 300);
  },
  watch: {
    searchQuery(newVal) {
      const trimmed = (newVal || '').trim();
      if (trimmed && trimmed.length >= 2) {
        // Trigger backend search for 2+ characters
        this.debouncedSearch();
      } else {
        // Clear search results for short queries
        this.searchResults = [];
        this.searchTotal = 0;
      }
    }
  },
  methods: {
    async searchEntitiesBackend() {
      // Backend search across ALL entities (not limited to 25 loaded)
      const trimmedQuery = (this.searchQuery || '').trim();
      if (!trimmedQuery || trimmedQuery.length < 2) {
        this.searchResults = [];
        this.isSearching = false;
        return;
      }
      
      this.isSearching = true;
      try {
        const response = await api.get(`/projects/${this.projectId}/entities/search/`, {
          params: { q: trimmedQuery, page_size: 100 }
        });
        
        if (response.data.success) {
          this.searchResults = response.data.subjects;
          this.searchTotal = response.data.total;
          console.log(`Backend search found ${response.data.total} entities matching "${trimmedQuery}"`);
        }
      } catch (error) {
        console.error('Entity search error:', error);
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },
    async loadCoverageSummary(subjectId) {
      try {
        this.loadingCoverage = { ...this.loadingCoverage, [subjectId]: true };
        const { data } = await api.get(`/subjects/${subjectId}/coverage-summary/`);
        this.coverageSummary = { ...this.coverageSummary, [subjectId]: data };
      } catch (e) {
        console.error('loadCoverageSummary error', e);
        this.showNotification('Failed to load coverage summary', 'error');
      } finally {
        this.loadingCoverage = { ...this.loadingCoverage, [subjectId]: false };
      }
    },
    coverageState(subjectId, code) {
      const sum = this.coverageSummary[subjectId];
      if (!sum || !Array.isArray(sum.status)) return null;
      return sum.status.find(s => s.coverage === code) || null;
    },
    coverageStateDisplay(subjectId, code) {
      const s = this.coverageState(subjectId, code);
      if (!s) return '—';
      if (s.state === 'OK') return 'OK';
      if (s.state === 'EXPIRING') return `EXPIRING (${s.days_to_expire}d)`;
      return 'MISSING';
    },
    coverageStateBadgeClass(subjectId, code) {
      const s = this.coverageState(subjectId, code);
      if (!s) return 'status-pending';
      if (s.state === 'OK') return 'status-compliant';
      if (s.state === 'EXPIRING') return 'status-warning';
      return 'status-non_compliant';
    },
    coveragePolicies(subjectId, code) {
      const sum = this.coverageSummary[subjectId];
      if (!sum) return [];
      const all = [...(sum.present?.entity || []), ...(sum.present?.agent || [])];
      return all.filter(p => p.coverage === code);
    },
    bestExpiration(subjectId, code) {
      const list = this.coveragePolicies(subjectId, code)
        .map(p => p.expiration)
        .filter(Boolean)
        .sort((a, b) => new Date(b) - new Date(a));
      return list.length ? this.formatDateOnly(list[0]) : null;
    },
    startEditingDashboardTitle() {
        this.isEditingDashboardTitle = true;
    },
    saveDashboardTitle() {
        this.isEditingDashboardTitle = false;
        localStorage.setItem(`dashboardTitle_${this.projectId}`, this.dashboardTitle);
        this.showNotification('Dashboard title updated!', 'success');
    },
    startEditingTitle() {
      this.isEditingSubjectsTitle = true;
    },
    saveTitle() {
      this.isEditingSubjectsTitle = false;
      // Save title to local storage, now per-project
      localStorage.setItem(`complianceSubjectsTitle_${this.projectId}`, this.complianceSubjectsTitle);
      this.showNotification('Title updated successfully!', 'success');
    },
    
    // Scalability methods
    handleSearch() {
      this.currentPage = 1; // Reset to first page when searching
    },
    
    applyFilters() {
      this.currentPage = 1; // Reset to first page when filtering
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.statusFilter = '';
      this.currentPage = 1;
    },
    
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
      this.currentPage = 1;
    },
    
    getSortIcon(field) {
      if (this.sortField !== field) {
        return 'fas fa-sort text-muted';
      }
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    
    handlePageSizeChange() {
      this.currentPage = 1; // Reset to first page when changing page size
    },
    
    toggleAllSubjects() {
      if (this.allSubjectsSelected) {
        // Deselect all
        this.selectedSubjects = this.selectedSubjects.filter(id => 
          !this.paginatedSubjects.some(subject => subject.id === id)
        );
      } else {
        // Select all visible subjects
        const visibleIds = this.paginatedSubjects.map(subject => subject.id);
        this.selectedSubjects = [...new Set([...this.selectedSubjects, ...visibleIds])];
      }
    },
    
    async bulkRequestDocuments() {
      if (this.selectedSubjects.length === 0) {
        this.showNotification('No subjects selected', 'warning');
        return;
      }
      
      // For bulk requests, open the modal for the first selected subject
      // In future, could implement a bulk email modal
      const firstSubjectId = this.selectedSubjects[0];
      const subject = this.complianceSubjects.find(s => s.id === firstSubjectId);
      
      if (subject) {
        this.openRequestDocsModal(subject);
        this.showNotification(`Opening request form. For bulk requests, send individually or use Compliance Health.`, 'info');
      } else {
        this.showNotification('Could not find selected subject', 'error');
      }
    },
    
    async bulkAnalyze() {
      if (this.selectedSubjects.length === 0) {
        this.showNotification('No subjects selected', 'warning');
        return;
      }
      
      try {
        this.showNotification(`Starting analysis for ${this.selectedSubjects.length} subjects...`, 'info');
        
        // Run comprehensive check for each selected subject
        let successCount = 0;
        let failCount = 0;
        
        for (const subjectId of this.selectedSubjects) {
          try {
            const subject = this.complianceSubjects.find(s => s.id === subjectId);
            if (subject) {
              // Trigger comprehensive check for this subject
              // This would need an endpoint that runs analysis for a subject
              await api.post(`/compliance-subjects/${subjectId}/run-analysis/`);
              successCount++;
            }
          } catch (err) {
            console.error(`Failed to start analysis for subject ${subjectId}:`, err);
            failCount++;
          }
        }
        
        if (successCount > 0) {
          this.showNotification(`Analysis started for ${successCount} subjects${failCount > 0 ? ` (${failCount} failed)` : ''}`, 'success');
        } else {
          this.showNotification('Failed to start analysis for selected subjects', 'error');
        }
        
        this.selectedSubjects = [];
        // Refresh to show updated status
        await this.fetchComplianceSubjects();
      } catch (error) {
        console.error('Error starting bulk analysis:', error);
        this.showNotification('Failed to start bulk analysis', 'error');
      }
    },
    
    confirmArchiveSubject(subject) {
      const entityName = subject.subject_entity_name || 'this subject';
      if (confirm(`Are you sure you want to archive "${entityName}"? This will remove it from the active list but preserve historical data.`)) {
        this.archiveSubject(subject);
      }
    },
    
    async archiveSubject(subject) {
      try {
        this.showNotification(`Archiving ${subject.subject_entity_name}...`, 'info');
        
        // Update the subject status to ARCHIVED
        await api.patch(`/compliance-subjects/${subject.id}/`, {
          current_status: 'ARCHIVED'
        });
        
        this.showNotification(`${subject.subject_entity_name} has been archived`, 'success');
        
        // Remove from local list or refresh
        await this.fetchComplianceSubjects();
      } catch (error) {
        console.error('Error archiving subject:', error);
        this.showNotification(`Failed to archive ${subject.subject_entity_name}: ${error.response?.data?.detail || error.message}`, 'error');
      }
    },
    async refreshData() {
      this.loading = true;
      this.error = null;
      this.loadingSubjects = true;
      try {
        // OPTIMIZATION: Only load essential data on page open
        // Check runs and detailed data will be loaded lazily when needed
        const results = await Promise.allSettled([
          this.fetchComplianceSubjects(), // Essential: Need to show subjects list
          this.fetchProjectData(), // Essential: Need project info
          this.fetchChecks() // Need check definitions for running new checks
        ]);

        if (results[0].status === 'rejected') {
            console.error("Failed to fetch compliance subjects during refresh");
        }
        if (results[1].status === 'rejected') {
            console.error("Failed to fetch project data during refresh");
        }

      } catch (error) {
        console.error('Generic error during data refresh:', error);
        this.error = "Failed to refresh dashboard data.";
      } finally {
        this.loading = false;
        this.loadingSubjects = false;
      }
    },
    
    async fetchChecks() {
      this.loadingChecks = true;
      try {
        const response = await api.get('/comprehensive-check-definitions/');
        let checkData = [];
        if (response && response.data) {
            if (Array.isArray(response.data.results)) {
                checkData = response.data.results;
            } else if (Array.isArray(response.data)) {
                checkData = response.data;
            } else {
                 console.warn("Unexpected check definitions structure:", response.data);
            }
        }
        this.checkDefinitions = checkData;
      } catch (error) {
        console.error('Error fetching check definitions:', error);
        this.error = error.message || 'Failed to load comprehensive checks.';
      } finally {
          this.loadingChecks = false;
      }
    },
    
    // REMOVED: fetchCheckRuns - now loaded lazily per subject
    // This method loaded ALL check runs for the project, causing performance issues
    // Check runs are now loaded only when Analysis History tab is opened for specific subjects
    
    async fetchComplianceSubjects() {
      if (!this.projectId) {
        console.warn('Cannot fetch compliance subjects without projectId');
        this.complianceSubjects = [];
        return;
      }
      this.loadingSubjects = true;
      try {
        console.log(`Fetching most recent 25 compliance subjects for project ${this.projectId}`);
        
        // SCALABILITY FIX: Load only 25 most recent subjects initially
        // User can search for others or load more
        const response = await api.get(`/compliance-subjects/`, {
          params: { 
            project: this.projectId,
            page_size: 25,  // Only load 25 most recent
            ordering: '-updated_at'  // Most recently updated first
          }
        });
        
        if (response && response.data) {
            if (Array.isArray(response.data)) {
                this.complianceSubjects = response.data;
            } else if (response.data.results && Array.isArray(response.data.results)) {
                this.complianceSubjects = response.data.results;
                // Store total count for "Load More" button
                this.totalSubjects = response.data.count || response.data.results.length;
            } else {
                 console.warn("Unexpected compliance subjects structure:", response.data);
                 this.complianceSubjects = [];
            }
        } else {
            console.warn("No data received for compliance subjects");
            this.complianceSubjects = [];
        }
        console.log(`Fetched ${this.complianceSubjects.length} most recent compliance subjects (total: ${this.totalSubjects || 'unknown'})`);
      } catch (error) {
        console.error('Error fetching compliance subjects:', error);
        this.complianceSubjects = [];
        throw new Error(error.message || 'Failed to load compliance subjects'); 
      } finally {
        this.loadingSubjects = false;
      }
    },
    
    async fetchProjectData() {
      if (!this.projectId) {
        console.warn('Cannot fetch project data without projectId');
        return;
      }
      try {
        console.log(`Fetching project data for project ${this.projectId}`);
        const response = await api.get(`/projects/${this.projectId}/`);
        if (response && response.data) {
          this.project = response.data;
          console.log('Fetched project data:', this.project);
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
      }
    },
    
    async fetchDocumentNames(documentIds) {
      // Ensure we only fetch if needed and avoid duplicates
      const idsToFetch = documentIds.filter(id => id && !this.documentNames[id]);
      if (idsToFetch.length === 0) return;
      
      console.log(`Fetching names/paths for document IDs: ${idsToFetch.join(',')}`);
      try {
        // Ideally use a batch endpoint: GET /api/documents/?id__in=1,2,3
        // Simulating with individual calls for now if batch isn't available
        const promises = idsToFetch.map(id => api.get(`/documents/${id}/`));
        const responses = await Promise.allSettled(promises);

        const newNameMap = { ...this.documentNames };
        const newPathMap = { ...this.documentFilePaths };
        const newDocumentsMap = { ...this.documentObjects }; // Store full document objects

        responses.forEach((res, index) => {
            const docId = idsToFetch[index];
            if (res.status === 'fulfilled' && res.value.data) {
                newNameMap[docId] = res.value.data.name || `Document ${docId}`;
                newPathMap[docId] = res.value.data.file; // Store the file path for legacy compatibility
                newDocumentsMap[docId] = res.value.data; // Store the full document with file_url
            } else {
                 console.warn(`Failed to fetch details for document ID ${docId}`);
                 newNameMap[docId] = `Document ${docId}`; // Default name on error
                 newPathMap[docId] = null;
                 newDocumentsMap[docId] = null;
            }
        });
        
        this.documentNames = newNameMap;
        this.documentFilePaths = newPathMap; // Store the paths for legacy compatibility
        this.documentObjects = newDocumentsMap; // Store full documents with file_url

      } catch (error) {
        console.error('Error fetching document names/paths:', error);
        // Handle error appropriately, maybe show notification
      }
    },
    
    openNewAnalysisModal() {
      if (this.checkDefinitions.length > 0) {
        this.runCheck(this.checkDefinitions[0].id);
      } else {
        this.showNotification('No analysis templates available.', 'error');
      }
    },
    
    async runCheck(checkDefinitionId) {
      // Lazy load check definitions if not already loaded
      if (this.checkDefinitions.length === 0 && !this.loadingChecks) {
        console.log('Lazy loading check definitions for run dialog...');
        await this.fetchChecks();
      }
      
      this.selectedCheck = this.checkDefinitions.find(c => c.id === checkDefinitionId);
      this.selectedDocuments = [];
      this.showRunDialog = true;
      await this.fetchDocuments();
    },
    
    async fetchDocuments() {
      this.loadingDocuments = true;
      try {
        console.log(`Fetching documents for project ${this.projectId}`);
        
        const response = await api.get('/projects/' + this.projectId + '/documents/');
        console.log('Documents API raw response:', response);
        
        let documents = [];
        
        if (Array.isArray(response.data)) {
          documents = response.data;
          console.log(`Found ${documents.length} documents in array format`);
        } else if (response.data && response.data.documents && Array.isArray(response.data.documents)) {
          documents = response.data.documents;
          console.log(`Found ${documents.length} documents in wrapped format`);
        } else if (response.data && typeof response.data === 'object') {
          const possibleDocuments = Object.values(response.data).find(val => Array.isArray(val));
          if (possibleDocuments) {
            documents = possibleDocuments;
            console.log(`Found ${documents.length} documents in nested object`);
          } else {
            if (response.data.id) {
              documents = [response.data];
              console.log('Found a single document');
            } else {
              console.log('Unexpected response format, could not find documents array:', response.data);
            }
          }
        }
        
        if (documents && documents.length > 0) {
          console.log('Sample document structure:', documents[0]);
        } else {
          console.log('No documents found or format not recognized');
        }
        
        if (this.selectedCheck && this.selectedCheck.document_types && this.selectedCheck.document_types.length > 0) {
          this.documents = documents.filter(doc => 
            this.selectedCheck.document_types.includes(doc.document_type)
          );
          console.log(`After filtering by document types, ${this.documents.length} documents remain`);
        } else {
          this.documents = documents;
        }
        
        const documentMap = {};
        documents.forEach(doc => {
          if (doc && doc.id) {
            documentMap[doc.id] = doc.name || `Document ${doc.id}`;
          }
        });
        this.documentNames = { ...this.documentNames, ...documentMap };
        
      } catch (err) {
        console.error('Error fetching documents:', err);
        this.documents = [];
        this.showNotification('Failed to load documents. Check console for details.', 'error');
      } finally {
        this.loadingDocuments = false;
      }
    },
    
    toggleDocument(docId) {
      const index = this.selectedDocuments.indexOf(docId);
      if (index === -1) {
        this.selectedDocuments.push(docId);
      } else {
        this.selectedDocuments.splice(index, 1);
      }
      console.log('Toggled document:', docId, 'Selected now:', this.selectedDocuments);
    },
    
    closeRunDialog() {
      this.showRunDialog = false;
      this.selectedCheck = null;
      this.selectedDocuments = [];
      this.analysisReferenceDate = ''; // Reset reference date
    },
    
    async confirmRunCheck() {
      if (this.selectedDocuments.length === 0 || !this.selectedCheck) {
        return;
      }
      
      this.runningCheck = true;
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      try {
        console.log('Starting comprehensive analysis for multiple documents:');
        console.log('- Check definition:', this.selectedCheck.id, this.selectedCheck.name);
        console.log('- Selected documents:', this.selectedDocuments);
        console.log('- Project ID:', this.projectId);
        
        let sessionId = null;
        try {
          const sessionName = `Comprehensive Check - ${this.selectedCheck.name} - ${new Date().toLocaleString()}`;
          const sessionResponse = await api.post(`/projects/${this.projectId}/analysis-sessions/`, {
            name: sessionName,
            project: this.projectId,
            status: 'PROCESSING'
          });
          if (sessionResponse && sessionResponse.data && typeof sessionResponse.data === 'object' && sessionResponse.data.id) {
            sessionId = sessionResponse.data.id;
            console.log('Created Analysis Session:', sessionId);
          } else if (sessionResponse && typeof sessionResponse === 'object' && sessionResponse.id) {
            sessionId = sessionResponse.id;
            console.log('Created Analysis Session (direct response):', sessionId);
          } else {
            throw new Error('Failed to create a valid Analysis Session - Invalid response format.');
          }
        } catch (sessionError) {
          console.error('Error creating Analysis Session:', sessionError);
          this.showNotification(`Failed to create analysis session: ${sessionError.message || 'Unknown error'}`, 'error');
          this.runningCheck = false;
          return;
        }

        const promises = this.selectedDocuments.map(docId => {
          // Build request payload with optional reference_date for temporal policy context
          const requestPayload = {
            check_definition: this.selectedCheck.id,
            document: docId, 
            project_id: this.projectId,
            analysis_session: sessionId
          };
          
          // Include reference_date if user specified one for temporal policy lookup
          if (this.analysisReferenceDate) {
            requestPayload.reference_date = this.analysisReferenceDate;
            console.log(`Using reference date ${this.analysisReferenceDate} for temporal policy context`);
          }
          
          return api.post('/comprehensive-check-runs/', requestPayload).then(response => {
              console.log(`Successfully started run for document ${docId}:`, response.data);
              successCount++;
          }).catch(error => {
              console.error(`Failed to start run for document ${docId}:`, error);
              errors.push(`Doc ${docId}: ${error.message || 'Unknown error'}`);
              errorCount++;
          });
        });

        await Promise.all(promises);

        this.closeRunDialog();
        
        if (successCount > 0) {
           this.showNotification(`${successCount} analysis run(s) started successfully! Check progress below.`, 'success');
        }
        if (errorCount > 0) {
           this.showNotification(`Failed to start ${errorCount} analysis run(s). Errors: ${errors.join('; ')}`, 'error');
        }

        this.refreshData();
        
      } catch (err) {
        console.error('Error processing comprehensive check runs:', err);
        this.showNotification('An unexpected error occurred while starting analyses.', 'error');
      } finally {
        this.runningCheck = false;
      }
    },
    
    async viewResults(run, subjectId) {
      this.selectedRun = run;
      this.selectedRun.subject_id = subjectId; // Add subject ID to the run object
      this.showResultsDialog = true;
      this.requirementResults = [];
      this.notificationPreview = null;

      await this.fetchRequirementResults(run.id);
      // PR1: load notification recipient/decision (non-blocking)
      this.loadNotificationPreview();
    },
    
    async fetchRequirementResults(runId) {
      this.loadingResults = true;
      try {
        const response = await api.get(`/requirement-results/?check_run_id=${runId}`);
        console.log('Requirement results response:', response);
        
        if (Array.isArray(response.data)) {
          this.requirementResults = response.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
          this.requirementResults = response.data.results;
        } else {
          console.log('Unexpected requirement results format:', response.data);
          this.requirementResults = [];
        }
      } catch (err) {
        console.error('Error fetching requirement results:', err);
        this.showNotification('Failed to load analysis results.', 'error');
      } finally {
        this.loadingResults = false;
      }
    },
    
    closeResultsDialog() {
      this.stopComparisonPoll();
      this.showResultsDialog = false;
      this.selectedRun = null;
      this.requirementResults = [];
      this.notificationPreview = null;
      this.showSendDrawer = false;
      this.sendForm = { to: '', cc: [], contact_id: null };
      this.ccInput = '';
      this.sendTarget = 'selected';
      this.comparisonRun = null;
      this.comparisonResults = [];
      this.comparisonLoading = false;
      this.comparisonError = '';
      this.adoptingComparison = false;
    },

    stopComparisonPoll() {
      if (this.comparisonPoll) {
        clearInterval(this.comparisonPoll);
        this.comparisonPoll = null;
      }
    },

    async startComparison() {
      if (!this.selectedRun?.id || this.comparisonLoading) return;
      this.comparisonLoading = true;
      this.comparisonError = '';
      this.comparisonRun = null;
      this.comparisonResults = [];
      try {
        const response = await api.post(
          `/comprehensive-check-runs/${this.selectedRun.id}/rerun-comparison/`,
        );
        this.comparisonRun = response.data.check_run;
        this.pollComparison();
      } catch (err) {
        this.comparisonError = err?.response?.data?.error
          || 'Could not start the comparison.';
        this.comparisonLoading = false;
      }
    },

    pollComparison() {
      this.stopComparisonPoll();
      this.comparisonPoll = setInterval(() => {
        this.refreshComparison();
      }, 5000);
      this.refreshComparison();
    },

    async refreshComparison() {
      if (!this.comparisonRun?.id) return;
      try {
        const response = await api.get(`/comprehensive-check-runs/${this.comparisonRun.id}/`);
        this.comparisonRun = response.data;
        if (this.comparisonRun.status === 'FAILED') {
          this.comparisonError = 'The comparison check failed. The original result is unchanged.';
          this.comparisonLoading = false;
          this.stopComparisonPoll();
          return;
        }
        const summaryReady = Boolean(this.comparisonRun.results_summary?.agent_overview)
          || Boolean(this.comparisonRun.results_summary?.generated_at);
        if (this.comparisonRun.status === 'COMPLETED' && summaryReady) {
          await this.loadComparisonResults();
          this.comparisonLoading = false;
          this.stopComparisonPoll();
        }
      } catch (err) {
        this.comparisonError = 'Could not refresh the comparison.';
        this.comparisonLoading = false;
        this.stopComparisonPoll();
      }
    },

    async loadComparisonResults() {
      const response = await api.get(
        `/requirement-results/?check_run_id=${this.comparisonRun.id}`,
      );
      const data = response.data;
      this.comparisonResults = Array.isArray(data) ? data : (data?.results || []);
    },

    async openComparisonSend() {
      if (!this.comparisonRun?.id) return;
      this.sendTarget = 'comparison';
      try {
        const response = await api.get(
          `/comprehensive-check-runs/${this.comparisonRun.id}/notification-preview/`,
        );
        this.notificationPreview = response.data;
        if (!response.data?.can_send) {
          this.comparisonError = 'No recipient is available for this request yet.';
          this.sendTarget = 'selected';
          await this.loadNotificationPreview();
          return;
        }
        this.openSendDrawer();
      } catch (err) {
        this.comparisonError = 'Could not load the recipient for this request.';
      }
    },

    async adoptComparison() {
      if (!this.comparisonRun?.id || this.adoptingComparison) return;
      this.adoptingComparison = true;
      this.comparisonError = '';
      try {
        const response = await api.post(
          `/comprehensive-check-runs/${this.comparisonRun.id}/adopt-comparison/`,
        );
        this.comparisonRun = response.data;
        this.showNotification('This result is now the live one. The earlier run is still on file.', 'success');
      } catch (err) {
        this.comparisonError = err?.response?.data?.error || 'Could not use this result.';
      } finally {
        this.adoptingComparison = false;
      }
    },

    // Notification panel (PR1: transparency, PR2: recipient management)
    async loadNotificationPreview() {
      if (!this.selectedRun?.id) return;
      this.notificationLoading = true;
      try {
        const response = await api.get(
          `/comprehensive-check-runs/${this.selectedRun.id}/notification-preview/`
        );
        this.notificationPreview = response.data;
      } catch (err) {
        console.error('Error loading notification preview:', err);
        this.notificationPreview = null;
      } finally {
        this.notificationLoading = false;
      }
    },

    openSendDrawer() {
      if (this.sendTarget !== 'comparison') {
        this.sendTarget = 'selected';
      }
      if (!this.notificationPreview?.can_send) return;
      const recipient = this.notificationPreview.recipient || {};
      this.sendForm = {
        to: recipient.email || '',
        cc: [],
        contact_id: recipient.contact_id || null,
      };
      this.ccInput = '';
      this.showSendDrawer = true;
    },

    closeSendDrawer() {
      if (this.sendingNotification) return;
      const wasComparison = this.sendTarget === 'comparison';
      this.showSendDrawer = false;
      this.ccInput = '';
      if (wasComparison) {
        this.sendTarget = 'selected';
        this.loadNotificationPreview();
      }
    },

    onCandidateChosen() {
      const chosen = this.candidateContacts.find(
        (c) => c.contact_id === this.sendForm.contact_id
      );
      if (chosen) {
        this.sendForm.to = chosen.email;
      }
    },

    onCcKeydown(event) {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        this.addCc();
      }
    },

    addCc() {
      const value = (this.ccInput || '').trim().replace(/,$/, '');
      if (!value) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        this.showNotification("That doesn't look like a valid email address.", 'warning');
        return;
      }
      const lower = value.toLowerCase();
      if (this.sendForm.cc.some((c) => c.toLowerCase() === lower)) {
        this.ccInput = '';
        return;
      }
      if (this.sendForm.to && this.sendForm.to.toLowerCase() === lower) {
        this.ccInput = '';
        return;
      }
      this.sendForm.cc.push(value);
      this.ccInput = '';
    },

    removeCc(idx) {
      this.sendForm.cc.splice(idx, 1);
    },

    async confirmSend() {
      if (!this.sendForm.to) return;
      this.sendingNotification = true;
      try {
        const payload = {
          to: this.sendForm.to,
          cc: this.sendForm.cc,
        };
        if (this.sendForm.contact_id) {
          payload.contact_id = this.sendForm.contact_id;
        }
        const runId = this.sendTarget === 'comparison'
          ? this.comparisonRun.id
          : this.selectedRun.id;
        const response = await api.post(
          `/comprehensive-check-runs/${runId}/send-notification/`,
          payload,
        );
        if (response.data.sent) {
          this.showNotification(`Notification sent to ${response.data.email}.`, 'success');
          this.showSendDrawer = false;
        } else {
          this.showNotification(
            `Notification not sent: ${response.data.reason || 'unknown reason'}`,
            'warning'
          );
        }
      } catch (err) {
        console.error('Error sending notification:', err);
        const detail =
          err?.response?.data?.reason ||
          err?.response?.data?.message ||
          err?.message ||
          'Unknown error';
        this.showNotification(`Failed to send notification: ${detail}`, 'error');
      } finally {
        this.sendingNotification = false;
        try {
          const runId = this.sendTarget === 'comparison'
            ? this.comparisonRun.id
            : this.selectedRun.id;
          const runResponse = await api.get(`/comprehensive-check-runs/${runId}/`);
          if (this.sendTarget === 'comparison') {
            this.comparisonRun = runResponse.data;
          } else {
            this.selectedRun = { ...this.selectedRun, ...runResponse.data };
          }
        } catch (_) { /* non-fatal */ }
        this.sendTarget = 'selected';
        await this.loadNotificationPreview();
      }
    },

    formatNotificationSource(source) {
      const labels = {
        agent_primary_contact: 'agent primary contact',
        agent_contact: 'agent contact',
        agent_entity_email: 'agent entity email',
        agent_contact_on_insured: 'insurance agent on file for the insured',
        subject_entity_email: 'subject entity email',
      };
      return labels[source] || source;
    },
    
    async retryAnalysis(run) {
      try {
        await api.post(`/comprehensive-check-runs/${run.id}/retry/`);
        this.showNotification('Analysis retry started.', 'success');
        this.refreshData();
      } catch (err) {
        console.error('Error retrying analysis:', err);
        this.showNotification('Failed to retry analysis.', 'error');
      }
    },
    
    async exportResults(run) {
      try {
        this.showNotification('Generating PDF report...', 'info');
        console.log('Export results for run:', run.id);
        
        // Get requirement results for this run
        let requirementResults = [];
        try {
          const resultsResponse = await api.get(`/requirement-results/?check_run_id=${run.id}`);
          // Handle both array and paginated response formats
          if (Array.isArray(resultsResponse.data)) {
            requirementResults = resultsResponse.data;
          } else if (resultsResponse.data && resultsResponse.data.results && Array.isArray(resultsResponse.data.results)) {
            requirementResults = resultsResponse.data.results;
          } else {
            console.log('Unexpected requirement results format:', resultsResponse.data);
            requirementResults = [];
          }
        } catch (error) {
          console.error('Error fetching requirement results for PDF:', error);
          // Fall back to already loaded results if available
          if (this.requirementResults && this.requirementResults.length > 0) {
            requirementResults = this.requirementResults;
          }
        }

        // Generate HTML report
        const reportHTML = this.generatePDFReportHTML(run, requirementResults);
        
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        // Check if popup was blocked
        if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
          this.showNotification('Popup blocked! Please allow popups for this site and try again.', 'error');
          return;
        }
        
        printWindow.document.write(reportHTML);
        printWindow.document.close();
        
        // Wait for content to load, then trigger print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            this.showNotification('PDF report ready! Use your browser\'s print dialog to save as PDF.', 'success');
          }, 500);
        };
        
      } catch (err) {
        console.error('Error exporting results:', err);
        this.showNotification('Failed to export results: ' + (err.message || 'Unknown error'), 'error');
      }
    },

    generatePDFReportHTML(run, requirementResults) {
      // Helper function to safely escape HTML
      const escapeHtml = (text) => {
        if (!text) return '';
        return String(text)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };

      const documentName = this.documentNames[run.document] || `Document ${run.document}`;
      const runDate = this.formatDate(run.created_at);
      const checkDefinitionName = run.check_definition_name || 'Comprehensive Check';
      
             // Count results by status - handle both boolean and string results
       const passed = requirementResults.filter(r => {
         const result = this.getResultPassed(r);
         return result === true || String(result || '').toUpperCase() === 'PASS';
       }).length;
       
       const failed = requirementResults.filter(r => {
         const result = this.getResultPassed(r);
         return result === false || String(result || '').toUpperCase() === 'FAIL';
       }).length;
       
       const partial = requirementResults.filter(r => {
         const result = this.getResultPassed(r);
         const displayText = this.getResultDisplayText(r);
         return String(result || '').toUpperCase() === 'PARTIAL' || 
                displayText === 'NEEDS REVIEW' || 
                displayText === 'PARTIAL';
       }).length;
       
       const unclear = requirementResults.filter(r => {
         const result = this.getResultPassed(r);
         return String(result || '').toUpperCase() === 'UNCLEAR';
       }).length;
       
       const pending = requirementResults.filter(r => {
         const result = this.getResultPassed(r);
         return String(result || '').toUpperCase() === 'PENDING';
       }).length;
      
      const totalResults = requirementResults.length;
      const passRate = totalResults > 0 ? Math.round((passed / totalResults) * 100) : 0;

      let requirementDetailsHTML = '';
             requirementResults.forEach((result, index) => {
         const status = this.getResultPassed(result);
         const displayText = this.getResultDisplayText(result);
         
         // Convert boolean results to proper status strings
         let statusStr;
         if (status === true) {
           statusStr = 'PASS';
         } else if (status === false) {
           statusStr = 'FAIL';
         } else if (displayText === 'NEEDS REVIEW') {
           statusStr = 'NEEDS REVIEW';
         } else if (displayText === 'PARTIAL') {
           statusStr = 'PARTIAL';
         } else {
           statusStr = String(status || displayText || 'UNKNOWN').toUpperCase();
         }
         const confidence = this.getConfidence(result);
         const evidence = this.getEvidenceText(result) || '';
         const reasoning = this.getReasoning(result) || '';
         const deficiencies = this.getDeficiencies(result) || [];
         const suggestions = this.getSuggestions(result) || [];
         
                  requirementDetailsHTML += `
           <div class="requirement-result" style="page-break-inside: avoid; margin-bottom: 20px; border: 1px solid #ddd; padding: 15px; border-radius: 5px;">
             <h4 style="margin: 0 0 10px 0; color: #333;">${index + 1}. ${escapeHtml(result.requirement_name || 'Requirement')}</h4>
             <div style="margin-bottom: 10px;">
               <strong>Status:</strong> 
               <span class="status-${statusStr.toLowerCase().replace(' ', '-')}" style="padding: 3px 8px; border-radius: 3px; font-weight: bold; 
                 ${statusStr === 'PASS' ? 'background-color: #d4edda; color: #155724;' : 
                   statusStr === 'FAIL' ? 'background-color: #f8d7da; color: #721c24;' : 
                   statusStr === 'PARTIAL' ? 'background-color: #fff3cd; color: #856404;' : 
                   statusStr === 'NEEDS REVIEW' ? 'background-color: #fff3cd; color: #856404;' :
                   statusStr === 'UNCLEAR' ? 'background-color: #f3e5f5; color: #7b1fa2;' : 
                   'background-color: #d1ecf1; color: #0c5460;'}">${escapeHtml(statusStr)}</span>
               ${confidence ? `<strong style="margin-left: 15px;">Confidence:</strong> ${escapeHtml(confidence)}%` : ''}
             </div>
             
             ${result.requirement_description ? `<p style="margin: 10px 0; color: #666; font-style: italic;">${escapeHtml(result.requirement_description)}</p>` : ''}
             
             ${evidence ? `
               <div style="margin: 10px 0;">
                 <strong>Evidence:</strong>
                 <div style="background-color: #f8f9fa; padding: 10px; border-left: 3px solid #007bff; margin-top: 5px; font-family: monospace; white-space: pre-wrap;">${escapeHtml(evidence)}</div>
               </div>
             ` : ''}
             
             ${reasoning ? `
               <div style="margin: 10px 0;">
                 <strong>AI Reasoning:</strong>
                 <div style="background-color: #f8f9fa; padding: 10px; border-left: 3px solid #28a745; margin-top: 5px;">${escapeHtml(reasoning)}</div>
               </div>
             ` : ''}
             
             ${Array.isArray(deficiencies) && deficiencies.length > 0 ? `
               <div style="margin: 10px 0;">
                 <strong>Deficiencies Found:</strong>
                 <ul style="margin: 5px 0 0 20px;">
                   ${deficiencies.map(def => `<li style="margin: 3px 0;">${escapeHtml(String(def))}</li>`).join('')}
                 </ul>
               </div>
             ` : ''}
             
             ${Array.isArray(suggestions) && suggestions.length > 0 ? `
               <div style="margin: 10px 0;">
                 <strong>Recommendations:</strong>
                 <ul style="margin: 5px 0 0 20px;">
                   ${suggestions.map(sug => `<li style="margin: 3px 0;">${escapeHtml(String(sug))}</li>`).join('')}
                 </ul>
               </div>
             ` : ''}
           </div>
         `;
      });

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Comprehensive Analysis Report - ${documentName}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.4; 
              margin: 20px; 
              color: #333; 
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              padding-bottom: 20px; 
              border-bottom: 2px solid #007bff; 
            }
            .summary { 
              background-color: #f8f9fa; 
              padding: 20px; 
              border-radius: 5px; 
              margin-bottom: 30px; 
            }
            .summary-grid { 
              display: grid; 
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); 
              gap: 15px; 
              margin-top: 15px; 
            }
            .summary-item { 
              text-align: center; 
              padding: 10px; 
              background: white; 
              border-radius: 3px; 
              border: 1px solid #ddd; 
            }
            .summary-value { 
              font-size: 24px; 
              font-weight: bold; 
              color: #007bff; 
            }
            .summary-label { 
              font-size: 12px; 
              color: #666; 
              margin-top: 5px; 
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Comprehensive Analysis Report</h1>
            <h2>${documentName}</h2>
            <p><strong>Check Definition:</strong> ${checkDefinitionName}</p>
            <p><strong>Analysis Date:</strong> ${runDate}</p>
            <p><strong>Run ID:</strong> ${run.id}</p>
          </div>
          
          <div class="summary">
            <h3>Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-value">${totalResults}</div>
                <div class="summary-label">Total Checks</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #28a745;">${passed}</div>
                <div class="summary-label">Passed</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #dc3545;">${failed}</div>
                <div class="summary-label">Failed</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #ffc107;">${partial}</div>
                <div class="summary-label">Needs Review</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #6f42c1;">${unclear}</div>
                <div class="summary-label">Unclear</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: #007bff;">${pending}</div>
                <div class="summary-label">Pending</div>
              </div>
              <div class="summary-item">
                <div class="summary-value" style="color: ${passRate >= 80 ? '#28a745' : passRate >= 60 ? '#ffc107' : '#dc3545'};">${passRate}%</div>
                <div class="summary-label">Pass Rate</div>
              </div>
            </div>
          </div>
          
          <h3>Detailed Results</h3>
          ${requirementDetailsHTML}
          
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px;">
            <p>Generated by Praetorian AI Compliance System</p>
            <p>Report generated on: ${new Date().toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;
    },
    
    showNotification(message, type = 'info') {
      if (this.notification.timeout) {
        clearTimeout(this.notification.timeout);
      }
      
      this.notification = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
          this.notification.show = false;
        }, 5000)
      };
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleString();
      } catch (e) {
        return dateString;
      }
    },
    // --- ADDED: Date only formatter ---
    formatDateOnly(dateString) {
      if (!dateString) return 'N/A';
      try {
        // Adjust format as needed (e.g., toLocaleDateString arguments)
        return new Date(dateString).toLocaleDateString(); 
      } catch (e) {
        return 'Invalid date';
      }
    },
    // --- END ADDED ---
    
    formatStatus(status) {
      if (!status) return 'Unknown';
      return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    formatOutcome(outcome) {
      if (!outcome) return '--';
      return outcome.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    formatDocumentTypes(types) {
      if (!types || types.length === 0) {
        return 'All document types';
      }
      return types.join(', ');
    },
    
    formatDocumentType(type) {
      if (!type) return 'Unknown type';
      
      return type
        .split('_')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    },
    
    formatCoverageType(type) {
      const mapping = {
        'GENERAL_LIABILITY': 'General Liability',
        'WORKERS_COMP': 'Workers Comp',
        'AUTO_LIABILITY': 'Auto Liability',
        'UMBRELLA': 'Umbrella',
        'PROFESSIONAL_LIABILITY': 'Professional Liability',
        'PROPERTY': 'Property',
        'CYBER': 'Cyber Liability',
        'POLLUTION': 'Pollution Liability',
        'BUILDERS_RISK': 'Builders Risk',
        'INLAND_MARINE': 'Inland Marine'
      };
      return mapping[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    getConfidenceClass(confidence) {
      if (!confidence) return 'confidence-low';
      if (confidence >= 0.9) return 'confidence-high';
      if (confidence >= 0.7) return 'confidence-medium';
      return 'confidence-low';
    },

    // --- ADDED: Agent Intelligence Methods ---
    // NEW: Navigate to prompt enhancement for specific requirement
    goToPromptEnhancement(requirementCode) {
      if (!requirementCode) {
        this.$toast?.error('Requirement code not available');
        return;
      }
      
      // Navigate to prompt enhancement page with requirement code pre-selected
      this.$router.push({
        name: 'PromptEnhancement',
        query: { search: requirementCode }
      });
    },
    
    // NEW: Edit requirement (opens modal)
    editRequirement(result) {
      if (!result.requirement) {
        this.$toast?.error('Requirement ID not available');
        return;
      }
      
      // Open the requirement editor modal
      this.editingRequirementId = result.requirement;
      this.showRequirementEditorModal = true;
    },
    
    // Handle requirement editor modal close
    closeRequirementEditor() {
      this.showRequirementEditorModal = false;
      this.editingRequirementId = null;
    },
    
    // Handle requirement saved
    handleRequirementSaved(requirement) {
      console.log('Requirement saved:', requirement);
      // Optionally refresh data
      this.refreshData();
    },
    
    async openPartiesTab(subjectId) {
      // Set active tab
      this.activeDetailTab = { ...this.activeDetailTab, [subjectId]: 'parties' };
      
      // Load parties if not already loaded (lazy loading)
      if (this.subjectDocumentParties[subjectId] || this.loadingSubjectParties[subjectId]) {
        return; // Already loaded or loading
      }
      
      console.log(`[Performance] Lazy loading parties for subject ${subjectId}...`);
      
      // Fetch documents and parties
      const currentProjectId = this.detailedSubjectData[subjectId]?.project || this.projectId;
      await this.fetchSubjectDocuments(subjectId, currentProjectId, true); // true = load parties
    },
    
    async loadAgentEvaluation(subjectId) {
      if (this.agentEvaluations[subjectId] || this.loadingAgentEvaluation[subjectId]) {
        return; // Already loaded or loading
      }

      this.loadingAgentEvaluation = { ...this.loadingAgentEvaluation, [subjectId]: true };
      
      try {
        const response = await api.get(`/compliance-subjects/${subjectId}/agent-evaluation/`);
        this.agentEvaluations = { ...this.agentEvaluations, [subjectId]: response.data };
      } catch (error) {
        console.error('Error loading agent evaluation:', error);
        this.agentEvaluations = { ...this.agentEvaluations, [subjectId]: {
          action: 'ERROR',
          reasoning: 'Failed to load agent evaluation data'
        } };
      } finally {
        this.loadingAgentEvaluation = { ...this.loadingAgentEvaluation, [subjectId]: false };
      }
    },

    async loadResponseIntelligence(subjectId) {
      if (this.responseIntelligence[subjectId] || this.loadingResponseIntelligence[subjectId]) {
        return; // Already loaded or loading
      }

      this.loadingResponseIntelligence = { ...this.loadingResponseIntelligence, [subjectId]: true };
      
      try {
        const response = await api.get(`/subjects/${subjectId}/response-intelligence/`);
        this.responseIntelligence = { ...this.responseIntelligence, [subjectId]: response.data };
      } catch (error) {
        console.error('Error loading response intelligence:', error);
        this.responseIntelligence = { ...this.responseIntelligence, [subjectId]: {
          analytics: {
            total_requests_90_days: 0,
            response_rate: 0,
            average_response_time_hours: 0,
            reliability_score: 0.5,
            overdue_count: 0,
            recent_requests: []
          },
          prediction: {
            predicted_response_hours: 48,
            confidence_score: 0.3,
            recommendation: {
              recommended_method: 'EMAIL',
              recommended_timing: 'ANYTIME',
              strategy_notes: []
            }
          }
        } };
      } finally {
        this.loadingResponseIntelligence = { ...this.loadingResponseIntelligence, [subjectId]: false };
      }
    },

    formatActionName(action) {
      if (!action) return 'No Action';
      return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    formatActionDescription(description) {
      if (!description) return 'No description available';
      return description;
    },

    getUrgencyClass(urgency) {
      const urgencyMap = {
        'CRITICAL': 'urgency-critical',
        'HIGH': 'urgency-high', 
        'MEDIUM': 'urgency-medium',
        'LOW': 'urgency-low'
      };
      return urgencyMap[urgency?.toUpperCase()] || 'urgency-low';
    },

    getAgentConfidenceClass(confidence) {
      if (confidence >= 0.9) return 'confidence-very-high';
      if (confidence >= 0.8) return 'confidence-high';
      if (confidence >= 0.6) return 'confidence-medium';
      if (confidence >= 0.3) return 'confidence-low';
      return 'confidence-unknown';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      try {
        return new Date(dateTime).toLocaleString();
      } catch (e) {
        return 'Invalid date';
      }
    },

    getReliabilityClass(score) {
      if (score >= 0.8) return 'text-success'
      if (score >= 0.6) return 'text-warning'
      return 'text-danger'
    },

    getRequestStatusClass(request) {
      if (request.status === 'Responded') return 'bg-success'
      if (request.is_overdue) return 'bg-danger'
      if (request.status === 'Sent') return 'bg-primary'
      return 'bg-secondary'
    },
    // --- END Agent Intelligence Methods ---

    openUploadModal() { // General upload from header
      this.subjectForUpload = null; // Ensure context is null for general upload
      this.showUploadModal = true;
    },

    // --- ADDED: Method to open upload modal for a specific subject ---
    openUploadModalForSubject(subject) {
      if (!subject || !subject.subject_entity) {
          console.error("Cannot open upload modal: Invalid subject data provided.", subject);
          this.showNotification("Cannot upload for this subject: missing data.", "error");
          return;
      }
      console.log(`Opening upload modal for subject: ${subject.subject_entity_name} (Entity ID: ${subject.subject_entity})`);
      this.subjectForUpload = subject; // Set the context
      this.showUploadModal = true; // Open the same modal
    },
    // --- END ADDED Method ---

    closeUploadModal() {
      this.showUploadModal = false;
      this.subjectForUpload = null; // <-- ADDED: Reset context on close
      this.uploadData = {
        name: '',
        file: null
      };
      this.uploadStatus = {
        uploading: false,
        classifying: false,
        classificationResult: null,
        error: null
      };
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadData.file = file;
        this.uploadData.name = file.name;
        this.uploadStatus.error = null; // Clear errors when new file selected
      }
    },
    async uploadDocument() {
      if (!this.canUpload) return;

      this.uploadStatus.uploading = true;
      this.uploadStatus.classifying = true;
      this.uploadStatus.error = null;
      this.uploadStatus.classificationResult = null;
      let newDocument = null; // Variable to hold the created document info

      try {
        const formData = new FormData();
        formData.append('name', this.uploadData.name);
        // Removed: document_type - AI will classify automatically
        formData.append('file', this.uploadData.file);

        // --- MODIFIED: Handle Check Definition based on context --- 
        // Only add check_definition_id if NOT uploading for a specific subject
        // (Assuming subject uploads shouldn't trigger immediate comprehensive checks)
        if (!this.subjectForUpload) {
            const activeCheck = this.checkDefinitions.find(check => 
              check.id === this.selectedCheck?.id // This logic might need review - assumes selectedCheck is relevant
            ) || this.checkDefinitions[0]; // Fallback might not be desired
            
            if (activeCheck) {
              formData.append('check_definition_id', activeCheck.id);
              console.log('Generic upload: Using check definition:', activeCheck.id, activeCheck.name);
            } else {
              console.warn('Generic upload: No specific check definition selected or available.');
              // Decide if this is an error or ok
            }
        } else {
             console.log('Subject-specific upload: Skipping check definition in initial upload.');
        }
        // --- END MODIFICATION ---

        // Step 1: Upload the document
        console.log("Uploading document to project", this.projectId);
        const uploadResponse = await api.post(`/projects/${this.projectId}/documents/upload/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        console.log('Document upload response:', uploadResponse);
        newDocument = uploadResponse.data?.document; // Get the created document data

        if (!newDocument || !newDocument.id) {
            throw new Error("Upload response did not include valid document data.");
        }
        
        // Capture classification result
        this.uploadStatus.classifying = false;
        if (uploadResponse.data?.classification) {
          this.uploadStatus.classificationResult = uploadResponse.data.classification;
          console.log('Classification result:', this.uploadStatus.classificationResult);
        } else if (newDocument.classified_document_type) {
          this.uploadStatus.classificationResult = {
            primary_type: newDocument.classified_document_type,
            confidence: newDocument.classification_confidence,
            coverage_types: newDocument.coverage_types || [],
            is_multi_document: newDocument.is_multi_document
          };
        }
        
        let message = 'Document uploaded successfully.';
        if (this.uploadStatus.classificationResult) {
          message += ` Classified as: ${this.formatDocumentType(this.uploadStatus.classificationResult.primary_type)}`;
        }
        this.showNotification(message, 'success');

        // Step 2: If uploaded for a subject, create the DocumentParty link
        if (this.subjectForUpload) {
            console.log(`Linking document ${newDocument.id} to entity ${this.subjectForUpload.subject_entity} as INSURED`);
            try {
                const partyPayload = {
                    document: newDocument.id,
                    entity: this.subjectForUpload.subject_entity, // ID of the subject's entity
                    role: 'INSURED' // Assume subject is the insured for their own docs
                };
                const partyResponse = await api.post('/document-parties/', partyPayload);
                console.log("DocumentParty link created:", partyResponse.data);
                this.showNotification(`Linked document to ${this.subjectForUpload.subject_entity_name} as Insured.`, 'success');
            } catch (partyError) {
                 console.error('Error creating DocumentParty link:', partyError);
                 // Notify user, but don't necessarily fail the whole upload
                 this.showNotification(`Document uploaded, but failed to link to subject: ${partyError.message || 'Unknown error'}`, 'error');
            }

            // --- ADDED: Create Analysis Session and trigger comprehensive check --- 
            if (newDocument && newDocument.id) {
                console.log(`Attempting to create Analysis Session for subject ${this.subjectForUpload.id} and document ${newDocument.id}`);
                try {
                    const sessionPayload = {
                        project: this.projectId,
                        compliance_subject: this.subjectForUpload.id, 
                        name: `Analysis for ${newDocument.name} (Subject: ${this.subjectForUpload.subject_entity_name})`,
                        status: 'PROCESSING', // Start processing immediately
                        documents: [
                            {
                                document_id: newDocument.id,
                                analysis_type: 'COMPREHENSIVE' // Specify the analysis type
                            }
                        ]
                    };
                    const sessionResponse = await api.post(`/projects/${this.projectId}/analysis-sessions/`, sessionPayload);
                    console.log("Analysis Session creation response:", sessionResponse.data);
                    this.showNotification(`Started comprehensive analysis for ${newDocument.name}.`, 'success');
                } catch (sessionError) {
                    console.error('Error creating Analysis Session or starting check:', sessionError);
                    this.showNotification(`Document uploaded and linked, but failed to start analysis: ${sessionError.message || 'Unknown error'}`, 'error');
                }
            }
            // --- END ADDED --- 
        }
        
        this.closeUploadModal(); // Close modal on success
        this.refreshData(); // Refresh dashboard data

      } catch (err) {
        console.error('Error during upload process:', err);
        // Use a more specific error message if available
        this.uploadStatus.error = err.message || 'Failed to upload document.'; 
        this.showNotification(this.uploadStatus.error, 'error');
        // Do not close modal on error
      } finally {
        this.uploadStatus.uploading = false;
      }
    },
    async toggleRequirementWaiver(requirementResultId, shouldWaive) {
      try {
        const endpoint = `/requirement-results/${requirementResultId}/${shouldWaive ? 'waive' : 'unwaive'}/`;
        await api.post(endpoint);
        console.log('Waiver status updated');
        if (this.selectedRun) {
          this.fetchRequirementResults(this.selectedRun.id);
        } else {
          this.refreshData();
        }
      } catch (error) {
        console.error('Error updating requirement waiver status:', error);
        this.error = error.message || 'Failed to update waiver status.';
      }
    },
    async submitRequirementFeedback(resultId, subjectId, accuracy) { // Accept subjectId
      console.log(`Submitting feedback for result ${resultId} (Subject: ${subjectId}): ${accuracy}`);
      
      try {
        const response = await api.patch(`/requirement-results/${resultId}/feedback/`, {
          accuracy: accuracy
        });
        
        // Update local data - handle both dashboard table view and results modal
        if (this.showResultsDialog && this.requirementResults) {
          // Results modal context - update requirementResults array
          const resultIndex = this.requirementResults.findIndex(r => r.id === resultId);
          if (resultIndex !== -1) {
            this.requirementResults.splice(resultIndex, 1, response.data);
            console.log(`Updated requirement result in results modal.`);
          }
        } else if (subjectId && this.latestSubjectRequirementResults[subjectId]) {
          // Dashboard table context - update subject-specific array
          const subjectResults = this.latestSubjectRequirementResults[subjectId];
          const resultIndex = subjectResults.findIndex(r => r.id === resultId);
          if (resultIndex !== -1) {
            this.latestSubjectRequirementResults[subjectId].splice(resultIndex, 1, response.data);
            console.log(`Updated requirement result for subject ${subjectId} in dashboard.`);
          }
        }
        
        this.showNotification('Feedback submitted successfully!', 'success');

      } catch (error) {
        console.error('Error submitting requirement feedback:', error);
        // Use existing handleError or show notification
        this.showNotification(`Failed to submit feedback: ${error.message || 'Unknown error'}`, 'error');
      }
    },
    openAddSubjectModal() {
      this.showAddSubjectModal = true;
    },
    handleSubjectAdded(newSubject) {
        console.log('Subject added:', newSubject);
        this.showAddSubjectModal = false;
        this.fetchComplianceSubjects();
    },
    openEntityManagerModal() {
      this.showEntityManagerModal = true;
    },
    openRequestDocsModal(subject) {
        if (!subject || !subject.subject_entity) {
          console.error("Cannot open request modal: Invalid subject data provided.", subject);
          this.showNotification("Cannot request for this subject: missing data.", "error");
          return;
        }
      console.log(`Opening request docs modal for subject: ${subject.subject_entity_name}`);
      this.subjectForRequestModal = subject;
      this.showRequestDocsModal = true;
    },
    closeRequestDocsModal() {
      this.showRequestDocsModal = false;
      this.subjectForRequestModal = null;
    },
    handleRequestSent(result) {
        console.log("Upload request sent event received:", result);
        if (result.success) {
            this.showNotification(result.message || 'Upload request sent successfully!', 'success');
            this.closeRequestDocsModal();
            // Optionally refresh pending requests list if we display it here later
        } else {
            // Error should be displayed within the form component, but maybe add general notification
            this.showNotification(result.message || 'Failed to send upload request.', 'error');
        }
    },
    toggleContacts(entityId) {
      // Toggle visibility
      const currentlyVisible = !!this.visibleContacts[entityId];
      this.visibleContacts = { 
          ...this.visibleContacts, 
          [entityId]: !currentlyVisible 
      };
      
      // Fetch contacts if becoming visible and not already loaded/loading
      if (!currentlyVisible && !this.entityContacts[entityId]) {
          this.fetchEntityContacts(entityId);
      } else if (currentlyVisible) {
          // Hide the add form when collapsing the section
          this.cancelAddContact(entityId);
      }
    },
    async fetchEntityContacts(entityId) {
        // --- ADD Log at start ---
        console.log(`fetchEntityContacts called for entityId: ${entityId}`);
        // --- END Log ---
        this.loadingContactsFor = entityId; 
        this.addContactError = { ...this.addContactError, [entityId]: null}; 
        try {
            console.log(`Fetching contacts for entity ${entityId}`);
            const response = await api.get(`/contacts/`, { params: { entity: entityId } });
            let contactsData = [];
            if (response && response.data) {
                if (Array.isArray(response.data)) {
                    contactsData = response.data;
                } else if (response.data.results && Array.isArray(response.data.results)) {
                    contactsData = response.data.results; 
                } else {
                    console.warn("Unexpected contacts list structure:", response.data);
                }
            } else {
                console.warn("No data received for entity contacts");
            }
            this.entityContacts = {
              ...this.entityContacts,
              [entityId]: contactsData
            };
            console.log(`Fetched ${contactsData.length} contacts for entity ${entityId}`);
            // --- ADDED DEBUG LOG HERE ---
            console.log('Current this.entityContacts state after update:', JSON.parse(JSON.stringify(this.entityContacts)));
            // --- END ADDED DEBUG LOG ---
        } catch (err) {
             console.error('Error fetching entity contacts:', err);
             // Use handleError if available or set a local error
             this.showNotification('Failed to load contacts: ' + (err.message || ''), 'error'); 
             this.entityContacts = { ...this.entityContacts, [entityId]: [] };
        } finally {
            if (this.loadingContactsFor === entityId) { 
                this.loadingContactsFor = null;
            }
            // --- ADDED Force Update ---
            console.log(`Forcing update after fetching contacts for ${entityId}`);
            this.$forceUpdate();
            // --- END Force Update ---
        }
    },
    openAddContactForm(entityId) {
        this.newContactFormData = {
            ...this.newContactFormData,
            [entityId]: initialNewContactFormData()
        };
        this.addContactError = { ...this.addContactError, [entityId]: null }; 
        this.showAddContactForm = { ...this.showAddContactForm, [entityId]: true };
         // Optional: scroll to form - might need adjustment depending on final template structure
        // this.$nextTick(() => { ... }); 
    },
    cancelAddContact(entityId) {
        this.showAddContactForm = { ...this.showAddContactForm, [entityId]: false };
        this.addContactError = { ...this.addContactError, [entityId]: null };
    },
    async addContact(entityId) {
        const formData = this.newContactFormData[entityId];
        if (!formData || !formData.name || !formData.email) {
            this.addContactError = { ...this.addContactError, [entityId]: 'Name and Email are required.' };
            return;
        }

        this.isSavingContact = { ...this.isSavingContact, [entityId]: true };
        this.addContactError = { ...this.addContactError, [entityId]: null };
        
        const payload = {
            ...formData,
            entity: entityId // Ensure the entity ID is included
        };

        Object.keys(payload).forEach(key => {
            if (payload[key] === '') payload[key] = null;
        });

        // --- ADD Log before API ---
        console.log(`Adding contact for entity ${entityId}. Current entityContacts state:`, JSON.parse(JSON.stringify(this.entityContacts)));
        // --- END Log ---
        console.log("Adding contact payload:", payload);

        try {
            const response = await api.post('/contacts/', payload);
            console.log("Contact created:", response.data);
            this.cancelAddContact(entityId); // Hide form 
            // Maybe show success notification
            this.showNotification("Contact added successfully.", "success");

            // --- ADD Log before refresh ---
            console.log(`Contact added for ${entityId}. Fetching updated contacts...`);
            // --- END Log ---
            await this.fetchEntityContacts(entityId); // Refresh the list

            // --- ADD Log after refresh ---
            console.log(`Finished refreshing contacts for ${entityId} after add. Final entityContacts state:`, JSON.parse(JSON.stringify(this.entityContacts)));
            // --- END Log ---

        } catch (err) {
             console.error(`Error adding contact for entity ${entityId}:`, err);
             let errorMsg = 'Failed to add contact.';
             if (err.response && err.response.data) {
                 if (typeof err.response.data === 'string') {
                     errorMsg = err.response.data;
                 } else if (err.response.data.detail) {
                     errorMsg = err.response.data.detail;
                 } else if (err.response.data.email) { // Specific check for unique email error
                     errorMsg = `Email error: ${err.response.data.email[0]}`;
                 } else {
                     let detailMsg = '';
                     for (const field in err.response.data) {
                         detailMsg += ` ${field}: ${err.response.data[field].join(', ')}`;
                     }
                     if (detailMsg) errorMsg = `Validation Error:${detailMsg}`;
                 }
             } else if (err.message) {
                 errorMsg = err.message;
             }
              this.addContactError = { ...this.addContactError, [entityId]: errorMsg };
              // Also show a general notification maybe
              // this.showNotification(errorMsg, 'error');
        } finally {
            this.isSavingContact = { ...this.isSavingContact, [entityId]: false };
        }
    },
    viewSubjectDetails(subject) {
      console.log("Toggling details for subject:", subject.id);
      const currentlyVisible = !!this.visibleSubjectDetails[subject.id];
      this.visibleSubjectDetails = {
        ...this.visibleSubjectDetails,
        [subject.id]: !currentlyVisible
      };

      // --- CLEAN ARCHITECTURE: Load ALL tab data when subject expands ---
      if (!currentlyVisible) {
         // Set default tab to history
         this.activeDetailTab = { ...this.activeDetailTab, [subject.id]: 'history' }; 
         
         // Load ALL subject data at once (no lazy loading, no race conditions)
         const alreadyFetched = Object.prototype.hasOwnProperty.call(this.detailedSubjectData, subject.id);
         if (!alreadyFetched && !this.loadingSubjectDocs[subject.id]) {
           console.log(`Loading complete subject data for ${subject.id} (all tabs)...`);
           this.fetchCompleteSubjectData(subject.id);
         }
      }
    },
    // REMOVED: loadAnalysisHistory - now check runs are loaded as part of fetchCompleteSubjectData
    // REMOVED: fetchSubjectSpecificCheckRuns - replaced with comprehensive loading

    // --- NEW: Method to fetch COMPLETE subject data for ALL tabs at once ---
    async fetchCompleteSubjectData(subjectId) {
        // Set loading states (parties NOT loaded initially - lazy load when tab clicked)
        this.loadingSubjectDocs = { ...this.loadingSubjectDocs, [subjectId]: true };
        this.loadingSubjectParties = { ...this.loadingSubjectParties, [subjectId]: false }; // Don't load parties yet
        this.loadingFormalAgents = { ...this.loadingFormalAgents, [subjectId]: true }; 
        this.loadingSubjectDetailedStatus = { ...this.loadingSubjectDetailedStatus, [subjectId]: true }; 
        this.loadingSubjectCheckRuns = { ...this.loadingSubjectCheckRuns, [subjectId]: true }; // Set loading state

        try {
            // 1. Fetch Full Subject Details (including agent_details, applicable_requirements)
            console.log(`Fetching full details for subject ${subjectId}`);
            const subjectResponse = await api.get(`/compliance-subjects/${subjectId}/`);
            this.detailedSubjectData = { ...this.detailedSubjectData, [subjectId]: subjectResponse.data };
            // --- ADDED: Store applicable requirements --- 
            this.subjectRequirementDefinitions = { 
                ...this.subjectRequirementDefinitions, 
                [subjectId]: subjectResponse.data?.applicable_requirements || [] 
            };
            // --- END ADDED ---
            console.log(`Fetched subject details for ${subjectId}`, subjectResponse.data);
            this.loadingFormalAgents = { ...this.loadingFormalAgents, [subjectId]: false }; 

            // 2. Fetch Associated Documents (Parties loaded lazily when tab opened)
            const currentProjectId = this.detailedSubjectData[subjectId]?.project || this.projectId;
            await this.fetchSubjectDocuments(subjectId, currentProjectId, false); // false = don't load parties yet
            // Parties will be loaded when "Associated Parties" tab is clicked

            // 3. Fetch Detailed Compliance Status 
            console.log(`Fetching detailed status for subject ${subjectId}...`);
            const statusResponse = await api.get(`/compliance-subjects/${subjectId}/detailed_status/`);
            this.subjectDetailedStatus = {
                ...this.subjectDetailedStatus,
                [subjectId]: statusResponse.data || [] // Expecting an array from the new endpoint
            };
            console.log(`Fetched ${statusResponse.data?.length || 0} detailed requirement statuses.`);
            this.loadingSubjectDetailedStatus = { ...this.loadingSubjectDetailedStatus, [subjectId]: false }; 

            // 4. Load check runs (analysis history) - NO MORE LAZY LOADING
            console.log(`Loading analysis history for subject ${subjectId}...`);
            this.loadingSubjectCheckRuns = { ...this.loadingSubjectCheckRuns, [subjectId]: true };
            
            try {
              // Get subject's analysis sessions from the detailed data we just loaded
              const subjectData = this.detailedSubjectData[subjectId];
              const sessionIds = subjectData?.analysis_sessions || [];
              
              if (sessionIds.length === 0) {
                console.log(`No analysis sessions found for subject ${subjectId}`);
                this.subjectCheckRuns = { ...this.subjectCheckRuns, [subjectId]: [] };
              } else {
                console.log(`Loading check runs for subject ${subjectId} sessions:`, sessionIds);
                
                // Load check runs for each session
                const sessionPromises = sessionIds.map(sessionId => 
                  api.get('/comprehensive-check-runs/', {
                    params: {
                      session_id: sessionId,
                      project: this.projectId
                    }
                  })
                );
                
                const sessionResponses = await Promise.all(sessionPromises);
                
                // Combine all check runs from all sessions for this subject
                let allSubjectRuns = [];
                sessionResponses.forEach((response, index) => {
                  let runData = [];
                  if (response?.data?.results) {
                    runData = response.data.results;
                  } else if (response?.data && Array.isArray(response.data)) {
                    runData = response.data;
                  }
                  console.log(`Session ${sessionIds[index]} has ${runData.length} check runs`);
                  allSubjectRuns = [...allSubjectRuns, ...runData];
                });
                
                // Sort by date (newest first)
                allSubjectRuns.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                
                this.subjectCheckRuns = { ...this.subjectCheckRuns, [subjectId]: allSubjectRuns };
                console.log(`Loaded ${allSubjectRuns.length} total check runs for subject ${subjectId}`);
                
                // Extract document IDs from check runs and fetch document details
                const documentIds = [...new Set(allSubjectRuns.map(run => run.document))];
                if (documentIds.length > 0) {
                  console.log(`Fetching document details for ${documentIds.length} documents from check runs...`);
                  await this.fetchDocumentNames(documentIds);
                }
              }
            } catch (checkRunError) {
              console.error(`Error loading check runs for subject ${subjectId}:`, checkRunError);
              this.subjectCheckRuns = { ...this.subjectCheckRuns, [subjectId]: [] };
            } finally {
              this.loadingSubjectCheckRuns = { ...this.loadingSubjectCheckRuns, [subjectId]: false };
            }
            
            console.log(`Complete subject data loaded for ${subjectId} (all tabs ready)`);

        } catch (error) {
            console.error(`Error fetching full details for subject ${subjectId}:`, error);
            this.showNotification(`Failed to load details for subject ${subjectId}.`, 'error');
            // Reset potentially partial data on error?
            this.detailedSubjectData = { ...this.detailedSubjectData, [subjectId]: null };
            // Removed subjectDocuments reset
            this.subjectDocumentParties = { ...this.subjectDocumentParties, [subjectId]: [] };
            this.subjectCheckRuns = { ...this.subjectCheckRuns, [subjectId]: [] }; // Reset runs on error
        } finally {
            // Load primary contacts for all entities to display in the table
            await this.loadPrimaryContactsForSubject(subjectId);

            // Reset remaining loading states 
            this.loadingSubjectDocs = { ...this.loadingSubjectDocs, [subjectId]: false };
            // loadingSubjectParties is reset within fetchPartiesForDocuments
            // loadingSubjectDetailedStatus was reset earlier
            // loadingFormalAgents was reset earlier
             this.loadingSubjectCheckRuns = { ...this.loadingSubjectCheckRuns, [subjectId]: false }; // Ensure reset in finally
        }
    },
    
    // --- ADDED: Method to load primary contacts for all entities in a subject ---
    async loadPrimaryContactsForSubject(subjectId) {
        try {
            const entities = this.getGroupedEntities(subjectId);
            const entityIds = entities.map(entity => entity.entityId);
            
            // Load contacts for each entity (if not already loaded)
            const contactPromises = entityIds.map(async (entityId) => {
                if (!this.entityContacts[entityId]) {
                    try {
                        await this.fetchEntityContacts(entityId);
                    } catch (error) {
                        console.warn(`Failed to load contacts for entity ${entityId}:`, error);
                        // Continue with other entities even if one fails
                    }
                }
            });
            
            await Promise.all(contactPromises);
            console.log(`Loaded primary contacts for subject ${subjectId} entities`);
        } catch (error) {
            console.error(`Error loading primary contacts for subject ${subjectId}:`, error);
        }
    },
    // --- END ADDED METHOD ---
    
    // --- END MODIFIED Method --- 

    // --- Method to fetch Subject Documents (parties loaded lazily if needed) --- 
    async fetchSubjectDocuments(subjectId, projectId, loadParties = true) {
        // No longer need loadingSubjectDocs state if list isn't displayed
        // this.loadingSubjectDocs = { ...this.loadingSubjectDocs, [subjectId]: true };
        console.log(`Fetching document IDs for subject ${subjectId}${loadParties ? ' to get parties' : ''}...`);
 
        try {
            // PERFORMANCE OPTIMIZATION: Fetch ONLY sessions for this subject (not entire project)
            // Before: Loaded 857 sessions for project, filtered client-side
            // After: Load only ~5-20 sessions for this subject
            console.log(`Fetching analysis sessions for subject ${subjectId} only...`);
            const sessionResponse = await api.get(`/compliance-subjects/${subjectId}/analysis-sessions/`);
            
            let subjectSessions = [];
            if (sessionResponse.data && sessionResponse.data.sessions) {
                subjectSessions = sessionResponse.data.sessions;
            } else if (sessionResponse.data && Array.isArray(sessionResponse.data)) {
                subjectSessions = sessionResponse.data;
            } else {
                console.warn("Unexpected structure for subject sessions response:", sessionResponse.data);
            }
            
            console.log(`Found ${subjectSessions.length} analysis sessions for subject ${subjectId}:`, subjectSessions);

            if (subjectSessions.length === 0) {
                console.log("No analysis sessions found for this subject, no documents to fetch.");
                this.subjectDocuments = { ...this.subjectDocuments, [subjectId]: [] }; // Ensure empty array
                return; // Exit early
            }

            // Step 3: Extract unique document IDs from the filtered sessions' analyses
            let documentIds = new Set();
            subjectSessions.forEach(session => {
                // Ensure 'analyses' exists and is an array before iterating
                if (session.analyses && Array.isArray(session.analyses)) { 
                    session.analyses.forEach(analysis => {
                        // Ensure 'document' exists and is not null/undefined
                        if (analysis.document) { 
                            documentIds.add(analysis.document); 
                        }
                    });
                }
            });

            const uniqueDocIds = Array.from(documentIds);
            console.log(`Extracted ${uniqueDocIds.length} unique document IDs:`, uniqueDocIds);

            if (uniqueDocIds.length === 0) {
                console.log("No document IDs found in sessions for party fetching.");
                // Clear parties if no docs found
                this.subjectDocumentParties = { ...this.subjectDocumentParties, [subjectId]: [] };
                return;
            }

            // Fetch parties only if requested (lazy loading optimization)
            if (loadParties && uniqueDocIds.length > 0) {
                await this.fetchPartiesForDocuments(uniqueDocIds, subjectId);
            } else {
                console.log(`Skipping parties load for subject ${subjectId} (will load when tab opened)`)
                this.loadingSubjectParties = { ...this.loadingSubjectParties, [subjectId]: false };
            }

        } catch (error) {
            console.error(`Error fetching document IDs for subject ${subjectId}:`, error);
            this.showNotification(`Failed to load document/party info for subject ${subjectId}: ${error.message || 'Unknown error'}`, 'error');
            // Reset parties on error
            this.subjectDocumentParties = { ...this.subjectDocumentParties, [subjectId]: [] }; 
        } finally {
            // No loadingSubjectDocs state to reset
        }
    },
    // Method to open the Document Party Manager Modal
    openDocumentPartyManager(doc) {
      if (!doc || !doc.id) {
        console.error("Cannot manage parties: Invalid document data provided.", doc);
        this.showNotification("Could not open party manager: Invalid document.", "error");
        return;
      }
      console.log(`Opening party manager for document ID: ${doc.id}`);
      this.selectedDocumentIdForParties = doc.id;
      this.showDocumentPartyManagerModal = true;
    },
    // Optional handler if we need to react to party updates
    handlePartiesUpdated() {
        console.log("Parties were updated for document:", this.selectedDocumentIdForParties);
        // Optionally refresh some data if needed, though the modal handles its own list refresh.
    },
    // --- ADDED: Method to fetch DocumentParties for a list of document IDs ---
    async fetchPartiesForDocuments(documentIds, subjectId) {
        if (!documentIds || documentIds.length === 0) {
            this.subjectDocumentParties = { ...this.subjectDocumentParties, [subjectId]: [] };
            return; // No documents to fetch parties for
        }
        this.loadingSubjectParties = { ...this.loadingSubjectParties, [subjectId]: true };
        console.log(`Fetching document parties for subject ${subjectId}, document IDs: ${documentIds.join(',')}`);
        try {
            const response = await api.get('/document-parties/', {
                params: { 
                    document__in: documentIds.join(',') 
                }
            });
            let partiesData = [];
            if (response && response.data) {
                if (Array.isArray(response.data)) {
                    partiesData = response.data;
                } else if (response.data.results && Array.isArray(response.data.results)) {
                    partiesData = response.data.results; 
                } else {
                    console.warn("Unexpected document parties list structure:", response.data);
                }
            } else {
                console.warn("No data received for document parties");
            }
            this.subjectDocumentParties = {
              ...this.subjectDocumentParties,
              [subjectId]: partiesData
            };
            console.log(`Fetched ${partiesData.length} document parties for subject ${subjectId}`);
        } catch (err) {
            console.error(`Error fetching document parties for subject ${subjectId}:`, err);
            this.showNotification(`Failed to load document parties for subject ${subjectId}: ${err.message || 'Unknown error'}`, 'error');
            this.subjectDocumentParties = { ...this.subjectDocumentParties, [subjectId]: [] };
        } finally {
            this.loadingSubjectParties = { ...this.loadingSubjectParties, [subjectId]: false };
        }
    },
    // --- END ADDED --- 

    // --- ADDED: Method to remove a formal agent from a subject ---
    async removeFormalAgent(subject, agentIdToRemove) {
        // --- ADDED Debug Log ---
        console.log('removeFormalAgent called with:', { 
          subjectId: subject?.id, 
          subjectEntityName: subject?.subject_entity_name, 
          agentIdToRemove 
        });
        // --- END Debug Log ---

        if (!subject || !subject.id || !agentIdToRemove) {
            console.error("removeFormalAgent called with invalid arguments:", subject, agentIdToRemove);
            return;
        }

        // --- ADDED Debug Log ---
        console.log('Showing confirmation dialog...');
        // --- END Debug Log ---
        if (!confirm(`Are you sure you want to remove this agent link for ${subject.subject_entity_name}?`)) {
             console.log('Confirmation dialog cancelled.'); // Log cancellation
             return;
        }

        console.log(`Attempting to remove agent ${agentIdToRemove} from subject ${subject.id}`);
        this.isSavingAgent = { ...this.isSavingAgent, [subject.id]: true }; // Use same loading state
        
        // Get current agent IDs and filter out the one to remove
        const currentAgentIds = this.formalAgentDetails(subject.id).map(agent => agent.id);
        const updatedAgentIds = currentAgentIds.filter(id => id !== agentIdToRemove);

        const payload = { agents: updatedAgentIds };

        try {
            await api.patch(`/compliance-subjects/${subject.id}/`, payload);
            this.showNotification("Agent removed successfully.", "success");
            // Refetch details to update the UI completely
            await this.fetchFullSubjectDetails(subject.id); 
        } catch (err) {
            console.error(`Error removing agent ${agentIdToRemove} from subject ${subject.id}:`, err);
            this.showNotification(`Failed to remove agent: ${err.message || 'Unknown error'}`, 'error');
        } finally {
            this.isSavingAgent = { ...this.isSavingAgent, [subject.id]: false };
        }
    },
    // --- END ADDED --- 

    // --- ADDED: Method to add a formal agent to a subject --- 
    async addFormalAgent(subject) {
        const agentIdToAdd = this.selectedAgentToAdd[subject.id];
        if (!agentIdToAdd) {
            this.showNotification("Please select an entity to add as an agent.", "error");
            return;
        }
        if (!subject || !subject.id) {
             console.error("addFormalAgent called with invalid subject:", subject);
             return;
        }

        console.log(`Attempting to add agent ${agentIdToAdd} to subject ${subject.id}`);
        this.isSavingAgent = { ...this.isSavingAgent, [subject.id]: true };
        
        // Get current agent IDs
        const currentAgentIds = this.formalAgentDetails(subject.id).map(agent => agent.id);
        // Create updated list (ensure no duplicates, though UI should prevent this)
        const updatedAgentIds = [...new Set([...currentAgentIds, agentIdToAdd])]; 

        const payload = { agents: updatedAgentIds };

        try {
            await api.patch(`/compliance-subjects/${subject.id}/`, payload);
            this.showNotification("Agent added successfully.", "success");
            // Reset dropdown selection for this subject
            this.selectedAgentToAdd = { ...this.selectedAgentToAdd, [subject.id]: '' }; 
            // Refetch details to update the UI completely
            await this.fetchFullSubjectDetails(subject.id); 
        } catch (err) {
            console.error(`Error adding agent to subject ${subject.id}:`, err);
            this.showNotification(`Failed to add agent: ${err.message || 'Unknown error'}`, 'error');
            // Optionally handle specific API errors from err.response.data
        } finally {
            this.isSavingAgent = { ...this.isSavingAgent, [subject.id]: false };
        }
    },
    // --- END ADDED --- 

    // --- MOVED: Method to fetch all entities for dropdowns --- 
    async fetchAllEntitiesForSelection() {
        console.log("Fetching all entities for selection...");
        try {
            // Consider pagination if the entity list is very large
            const response = await api.get('/entities/'); 
            this.allEntities = response.data?.results || response.data || [];
            console.log(`Fetched ${this.allEntities.length} total entities.`);
        } catch (err) {
            console.error("Error fetching all entities:", err);
            this.showNotification("Failed to load entities for selection.", "error");
            this.allEntities = []; // Ensure it's an empty array on error
        }
        // No loading state change here, happens as part of overall refresh
    },
    // --- END MOVED --- 

    // --- ADDED: Method to fetch latest results for a subject ---
    // eslint-disable-next-line no-unused-vars
    async fetchLatestSubjectResults(subjectId) {
        this.loadingLatestSubjectResults = { ...this.loadingLatestSubjectResults, [subjectId]: true };
        this.latestSubjectRun = { ...this.latestSubjectRun, [subjectId]: null };
        this.latestSubjectRequirementResults = { ...this.latestSubjectRequirementResults, [subjectId]: [] };
        console.log(`Fetching latest analysis results for subject ${subjectId}...`);

        try {
            // Step 1: Find the latest relevant AnalysisSession for this subject
            // Ideally, filter server-side: ?compliance_subject={subjectId}&ordering=-created_at&limit=1
            // Simulating client-side filtering for now based on existing fetched runs
            // Use subject-specific check runs if available, otherwise return empty
            const relevantRuns = this.subjectCheckRuns[subjectId] || [];

            // Find the most recent *completed* run among relevant ones
            const latestCompletedRun = relevantRuns
                .filter(run => run.status === 'COMPLETED')
                .sort((a, b) => new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at))[0];

            if (latestCompletedRun) {
                console.log(`Found latest completed run ${latestCompletedRun.id} for subject ${subjectId}.`);
                this.latestSubjectRun = { ...this.latestSubjectRun, [subjectId]: latestCompletedRun };
                // Step 2: Fetch requirement results for this specific run
                const runId = latestCompletedRun.id;
                console.log(`Fetching requirement results for run ID: ${runId}`);
                const response = await api.get(`/requirement-results/?check_run_id=${runId}`); // Corrected API call
                let resultsData = [];
                // --- ADDED LOG ---
                console.log("Sample Requirement Result Object:", response.data?.[0]);
                // --- END LOG ---
                 if (Array.isArray(response.data)) {
                    resultsData = response.data;
                } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
                    resultsData = response.data.results;
                } else {
                    console.warn(`Unexpected requirement results format for run ${runId}:`, response.data);
                }
                this.latestSubjectRequirementResults = { ...this.latestSubjectRequirementResults, [subjectId]: resultsData };
                console.log(`Fetched ${resultsData.length} requirement results for latest run ${runId}.`);
            } else {
                console.log(`No completed check runs found for subject ${subjectId}.`);
            }

        } catch (error) {
            console.error(`Error fetching latest results for subject ${subjectId}:`, error);
            this.showNotification(`Failed to load latest analysis results for subject ${subjectId}.`, 'error');
        } finally {
            this.loadingLatestSubjectResults = { ...this.loadingLatestSubjectResults, [subjectId]: false };
        }
    },
    // --- END ADDED ---

    // --- UNIFIED: Use centralized document URL helper ---
    getDocumentUrl(doc) {
      // Import at runtime to avoid circular dependencies
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },
    viewDocument(doc) {
      if (doc && doc.file) {
        window.open(this.getDocumentUrl(doc), '_blank');
      }
    },
    
    async handleDownload(doc) {
      // Download document with proper authentication
      if (!doc || !doc.id) {
        console.warn('Cannot download: no document id');
        return;
      }
      
      this.downloading = true;
      try {
        const { downloadDocument } = require('@/utils/documentUrlHelper');
        await downloadDocument(doc, api);
      } catch (error) {
        console.error('Download failed:', error);
        this.showNotification('Failed to download document. Please try again.', 'error');
      } finally {
        this.downloading = false;
      }
    },

    // --- ADDED: PDF viewing helper methods for comprehensive check results ---
    getSelectedRunDocument() {
      if (!this.selectedRun || !this.selectedRun.document) {
        return null;
      }
      
      // Use full document object if available (includes file_url with pre-signed URL)
      const fullDocument = this.documentObjects[this.selectedRun.document];
      if (fullDocument) {
        return fullDocument;
      }
      
      // Fallback to constructed object for legacy compatibility
      return {
        id: this.selectedRun.document,
        file: this.documentFilePaths[this.selectedRun.document],
        name: this.documentNames[this.selectedRun.document]
      };
    },

    isPDF(doc) {
      // Import at runtime to avoid circular dependencies
      const { isPDF } = require('@/utils/documentUrlHelper');
      return isPDF(doc);
    },
    
    async fetchFreshDocumentUrl(documentId) {
      try {
        console.log(`🔄 Fetching fresh document data for ID: ${documentId}`);
        const response = await api.get(`/documents/${documentId}/`);
        
        if (response.data && response.data.file_url) {
          console.log('✅ Got fresh file_url:', response.data.file_url);
          
          // Update the document object in our cache
          if (this.documentObjects[documentId]) {
            this.documentObjects[documentId].file_url = response.data.file_url;
          } else {
            this.documentObjects[documentId] = response.data;
          }
          
          // Force a re-render by updating the reactive data
          this.$forceUpdate();
        }
      } catch (error) {
        console.error('❌ Error fetching fresh document URL:', error);
      }
    },
    // --- END ADDED --- 

    // --- ADDED: Method to trigger opening Entity Manager for a specific party ---
    viewPartyDetails(entityId) {
        if (!entityId) {
            console.error("Cannot view party details: Invalid Entity ID provided.");
            return;
        }
        console.log(`Request to view/toggle contacts for Entity ID: ${entityId}.`);
        // CHANGED: Instead of opening EntityManagerModal, call toggleContacts for inline display.
        this.toggleContacts(entityId);
    },
    // --- END ADDED ---

    // --- ADDED: Method to toggle inline agent form --- 
    toggleAddAgentForm(subjectId) {
        const currentVisibility = !!this.showAddAgentForm[subjectId];
        // Reset form data and errors when toggling
        this.newAgentFormData = {
            ...this.newAgentFormData,
            [subjectId]: { name: '', primary_email: '', primary_phone: '' } // Initialize form
        };
        this.newAgentError = { ...this.newAgentError, [subjectId]: null };
        // Toggle visibility
        this.showAddAgentForm = {
            ...this.showAddAgentForm,
            [subjectId]: !currentVisibility
        };
    },
    // --- END ADDED --- 

    // --- ADDED: Method to save new agent from inline form --- 
    async saveNewAgent(subject) {
        const subjectId = subject.id;
        const formData = this.newAgentFormData[subjectId];

        if (!formData || !formData.name || !formData.name.trim()) {
            this.newAgentError = { ...this.newAgentError, [subjectId]: 'Agency Name is required.' };
            return;
        }

        this.isSavingNewAgent = { ...this.isSavingNewAgent, [subjectId]: true };
        this.newAgentError = { ...this.newAgentError, [subjectId]: null };
        
        const payload = {
            name: formData.name.trim(),
            primary_email: formData.primary_email || null,
            primary_phone: formData.primary_phone || null,
            entity_type: 'AGENCY' // Default to Agency/Broker
        };
        
        console.log("Creating new agent entity:", payload);
        let newAgentEntity = null; // Define variable to hold the new entity

        try {
            const response = await api.post('/entities/', payload);
            newAgentEntity = response.data; // Store the created entity
            console.log("New agent entity created:", newAgentEntity);

            this.showNotification("New Agency/Broker created successfully.", "success");
            this.toggleAddAgentForm(subjectId); // Close the form

            // Refresh the full list of entities to update dropdowns
            await this.fetchAllEntitiesForSelection();

            // --- MODIFIED: Automatically link the newly created agent ---
            if (newAgentEntity && newAgentEntity.id) {
                console.log(`Automatically linking new agent ${newAgentEntity.id} to subject ${subjectId}`);
                // Set the selectedAgentToAdd to the new ID before calling addFormalAgent
                this.selectedAgentToAdd = { ...this.selectedAgentToAdd, [subjectId]: newAgentEntity.id };
                // Use a separate try-catch for the linking action
                try {
                    // Call addFormalAgent to link it (reuse existing logic/loading state)
                    await this.addFormalAgent(subject); 
                    // Notification for linking success is handled within addFormalAgent
                } catch (linkError) {
                    console.error(`Error automatically linking new agent ${newAgentEntity.id}:`, linkError);
                    // Notify user that creation succeeded but linking failed
                    this.showNotification(`New Agency created, but failed to automatically link it: ${linkError.message || 'Unknown error'}`, 'warning');
                }
            } else {
                 console.warn("New agent entity response did not contain an ID. Cannot automatically link.");
                 this.showNotification("New Agency created, but could not get ID to link automatically.", "warning");
            }
            // --- END MODIFICATION ---

        } catch (err) {
             console.error(`Error creating new agent entity:`, err);
             let errorMsg = 'Failed to create new agency.';
             // Basic error handling
             if (err.response && err.response.data) {
                 if (typeof err.response.data === 'string') { errorMsg = err.response.data; }
                 else if (err.response.data.detail) { errorMsg = err.response.data.detail; }
                 else if (err.response.data.name) { errorMsg = `Name error: ${err.response.data.name[0]}`; }
                 else if (err.response.data.email) { errorMsg = `Email error: ${err.response.data.email[0]}`; } // Corrected field name if needed
                 else {
                     try { errorMsg = JSON.stringify(err.response.data); } catch { /* ignore */ }
                 }
             } else if (err.message) { errorMsg = err.message; }
             this.newAgentError = { ...this.newAgentError, [subjectId]: errorMsg };
        } finally {
            this.isSavingNewAgent = { ...this.isSavingNewAgent, [subjectId]: false };
            // Ensure addFormalAgent loading state is also cleared if linking failed before it started
            if (newAgentEntity && newAgentEntity.id && this.isSavingAgent[subjectId]) {
                 this.isSavingAgent = { ...this.isSavingAgent, [subjectId]: false };
            }
        }
    },
    // --- END ADDED --- 

    // --- ADDED: Method to delete a contact ---
    async deleteContact(contactId, entityId) {
      if (!contactId || !entityId) {
        console.error("deleteContact called with invalid arguments:", contactId, entityId);
        return;
      }

      if (!confirm("Are you sure you want to delete this contact?")) {
        return; 
      }

      console.log(`Attempting to delete contact ${contactId} for entity ${entityId}`);
      // Optional: Add a loading state specific to deleting this contact if needed
      // this.isDeletingContact = { ...this.isDeletingContact, [contactId]: true };

      try {
        await api.delete(`/contacts/${contactId}/`);
        this.showNotification("Contact deleted successfully.", "success");
        
        // Refresh the contact list for the parent entity
        await this.fetchEntityContacts(entityId);

      } catch (err) {
        console.error(`Error deleting contact ${contactId}:`, err);
        this.showNotification(`Failed to delete contact: ${err.message || 'Unknown error'}`, 'error');
        // Handle specific errors from err.response.data if necessary
      } finally {
        // Clear loading state if used
        // this.isDeletingContact = { ...this.isDeletingContact, [contactId]: false };
      }
    },
    // --- END ADDED ---

    // --- ADDED: Method to fetch documents specifically for COI modal ---
    async fetchCoiDocuments() {
      this.loadingDocumentsForCoi = true;
      this.coiDocuments = [];
      this.createFromCoiError = null; // Clear previous errors
      try {
        console.log(`Fetching CERTIFICATE documents for project ${this.projectId}`);
        // Assuming backend supports filtering by type: /documents/?project=<id>&document_type=CERTIFICATE
        const response = await api.get(`/projects/${this.projectId}/documents/`, {
          params: { document_type: 'CERTIFICATE' } 
        });
        
        let allDocs = [];
        // --- UPDATED: Handle nested response structure --- 
        if (response.data && Array.isArray(response.data.documents)) { // Check for nested array
             allDocs = response.data.documents;
             console.log(`Found ${allDocs.length} documents in nested 'documents' key.`);
        } else if (response.data && Array.isArray(response.data.results)) { // Handle DRF pagination structure
             allDocs = response.data.results;
             console.log(`Found ${allDocs.length} documents in nested 'results' key.`);
        } else if (response.data && Array.isArray(response.data)) { // Handle flat array structure
             allDocs = response.data;
             console.log(`Found ${allDocs.length} documents in flat array.`);
        } else {
            console.warn("Unexpected documents format received", response.data);
        }
        // --- END UPDATED --- 

        // Ensure filtering client-side as a fallback or primary method
        this.coiDocuments = allDocs.filter(doc => 
             doc.document_type === 'CERTIFICATE' || 
             doc.document_type === 'certificate' // Handle potential case variations
        ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Sort newest first

        console.log(`Found ${this.coiDocuments.length} COI documents after filtering.`);

      } catch (err) {
        console.error('Error fetching COI documents:', err);
        this.createFromCoiError = 'Failed to load Certificate documents. Please try again.';
      } finally {
        this.loadingDocumentsForCoi = false;
      }
    },
    // --- END ADDED --- 

    // --- ADDED: Methods to control the new modal ---
    async openCreateFromCoiModal() {
      this.selectedCoiDocumentId = ''; // Reset selection
      this.createFromCoiError = null; // Clear errors
      this.showCreateFromCoiModal = true;
      await this.fetchCoiDocuments(); // Fetch documents when modal opens
    },

    closeCreateFromCoiModal() {
      this.showCreateFromCoiModal = false;
      this.selectedCoiDocumentId = '';
      this.createFromCoiError = null;
      this.newCoiFile = null;
      this.isUploadingNewCoi = false;
      // --- ADDED: Reset confirmation state on close --- 
      this.showCoiReviewStep = false;
      this.parsedCoiData = null;
      // Clear manual contact fields
      this.manualContactName = '';
      this.manualContactEmail = '';
      this.manualContactPhone = '';
      // Clear uploaded document info
      this.uploadedDocumentInfo = null;
      // Reset PDF embed state
      this.pdfEmbedFailed = false;
      // --- END ADDED ---
    },

    // --- RENAMED and UPDATED: Now calls PARSE endpoint --- 
    async processCoiDocument() {
      let documentIdToProcess = this.selectedCoiDocumentId;
      this.createFromCoiError = null; 
      this.showCoiReviewStep = false; // Ensure review step isn't shown yet
      this.parsedCoiData = null;
      this.isConfirmingCoi = false; // Ensure confirming state is reset
      
      // 1. Check if a new file needs to be uploaded
      if (this.newCoiFile) {
          // Set uploading state specifically for UI feedback during upload
          this.isUploadingNewCoi = true; 
          try {
              documentIdToProcess = await this.uploadNewCoiFile(); 
          } catch (uploadError) {
              // Error is already set in uploadNewCoiFile
              this.isUploadingNewCoi = false; // Reset upload state on error
              return; 
          } finally {
              this.isUploadingNewCoi = false; // Reset upload state after success/failure
          }
      }
      
      // 2. Ensure we have a document ID 
      if (!documentIdToProcess) {
        this.createFromCoiError = "Please select or upload a COI document.";
        return;
      }

      // 3. Proceed with PARSING the document
      this.isCreatingSubjectFromCoi = true; // Use this state for the parsing step
      try {
        console.log(`Calling PARSE endpoint for COI document ID: ${documentIdToProcess}, Project ID: ${this.projectId}`);
        // --- UPDATED: Call the PARSE endpoint --- 
        const response = await api.post('/subjects/parse_coi/', {
          document_id: documentIdToProcess,
          project_id: Number(this.projectId)
        });
        // --- END UPDATE --- 

        console.log("Parse COI response:", response.data);
        // Store result and show review step
        this.parsedCoiData = response.data; 
        // Default subject selection based on the (potentially incorrect) policyholder identified by LLM
        this.selectedSubjectSourceKey = 'policyholder'; 
        this.showCoiReviewStep = true;

      } catch (err) {
        // Handle errors from the PARSE endpoint
        console.error('Error parsing COI:', err);
        let errorMsg = 'Failed to parse COI.';
        if (err.response && err.response.data) {
          errorMsg = err.response.data.error || err.response.data.detail || errorMsg;
          if (err.response.data.parsing_errors) {
               errorMsg += ` Parsing Errors: ${err.response.data.parsing_errors.join(', ')}`;
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
        this.createFromCoiError = errorMsg;
        this.showCoiReviewStep = false; // Don't show review step if parsing failed
      } finally {
        this.isCreatingSubjectFromCoi = false; // Parsing step finished
      }
    },
    // --- END RENAMED/UPDATED ---
    
    // --- NEW METHOD: Calls CONFIRM endpoint --- 
    async confirmAndCreateSubject(runAnalysis = false) {
      // Use optional chaining for safety when accessing nested props
      if (!this.parsedCoiData || !this.selectedSubjectSourceKey || !this.parsedCoiData[this.selectedSubjectSourceKey]?.name) {
        this.createFromCoiError = "Cannot confirm: Subject selection is missing or the selected entity has no name.";
        return;
      }
      
      // --- ADDED: Check if document_id is present in parsed data --- 
      if (!this.parsedCoiData.document_id) {
          this.createFromCoiError = "Cannot confirm: Document ID missing from parsed data.";
          return;
      }
      // --- END ADDED --- 

      // --- ADDED: Validate required contact email ---
      let contactValidationError = null;

      // Check if we have extracted contacts with email
      if (this.parsedCoiData.producer_contacts && this.parsedCoiData.producer_contacts.length > 0) {
        const firstContact = this.parsedCoiData.producer_contacts[0];
        if (!firstContact.email || firstContact.email.trim() === '') {
          contactValidationError = "Producer contact email is required but missing. Please enter an email address.";
        }
      } 
      // Check manual contact entry if no extracted contacts
      else if (this.manualContactName || this.manualContactEmail || this.manualContactPhone) {
        if (!this.manualContactEmail || this.manualContactEmail.trim() === '') {
          contactValidationError = "Contact email is required. Please enter an email address for the producer contact.";
        } else {
          // Add manual contact to parsed data for processing
          this.parsedCoiData.producer_contacts = [{
            name: this.manualContactName || 'Producer Contact',
            email: this.manualContactEmail,
            phone: this.manualContactPhone || null
          }];
        }
      } else {
        contactValidationError = "Producer contact information is required. Please add at least a contact name and email address.";
      }

      if (contactValidationError) {
        this.createFromCoiError = contactValidationError;
        return;
      }
      // --- END ADDED ---

      this.isConfirmingCoi = true;
      this.createFromCoiError = null;
      
      // --- SIMPLIFIED: Construct payload directly from parsedCoiData --- 
      const payload = {
          document_id: this.parsedCoiData.document_id, 
          project_id: this.projectId,
          policyholder: this.parsedCoiData.policyholder,
          producer: this.parsedCoiData.producer,
          certificate_recipient: this.parsedCoiData.certificate_recipient,
          insured_box_entity: this.parsedCoiData.insured_box_entity,
          producer_contacts: this.parsedCoiData.producer_contacts, 
          subject_entity_source_key: this.selectedSubjectSourceKey
      };
      // --- END SIMPLIFIED --- 
      
      // --- REMOVED Fallback logic for document_id --- 
      // if (!payload.document_id) {
      //     const docIdFromUpload = this.coiDocuments.find(doc => doc.name === this.newCoiFile?.name)?.id;
      //     payload.document_id = this.selectedCoiDocumentId || docIdFromUpload;
      //     if (!payload.document_id) {
      //         this.createFromCoiError = "Could not determine original Document ID.";
      //         this.isConfirmingCoi = false;
      //         return;
      //     }
      // }
      // --- END REMOVED --- 

      try {
         console.log("Calling CONFIRM endpoint with payload:", payload);
         const response = await api.post('/subjects/confirm_coi_creation/', payload);
         
         console.log("Confirm COI Creation response:", response.data);
         const createdSubject = response.data.subject;
         const documentId = this.parsedCoiData.document_id;
         
         if (runAnalysis && createdSubject && documentId) {
           // Start comprehensive analysis immediately
           try {
             console.log(`🚀 Starting comprehensive analysis for subject ${createdSubject.id} with document ${documentId}`);
             
             // Create analysis session
             const sessionPayload = {
               project: this.projectId,
               compliance_subject: createdSubject.id,
               name: `Analysis for ${createdSubject.subject_entity_name} (Auto-created)`,
               status: 'PROCESSING',
               documents: [{
                 document_id: documentId,
                 analysis_type: 'COMPREHENSIVE'
               }]
             };
             
             const sessionResponse = await api.post(`/projects/${this.projectId}/analysis-sessions/`, sessionPayload);
             console.log("Analysis session created:", sessionResponse.data);
             
             this.showNotification(`Subject created and comprehensive analysis started for ${createdSubject.subject_entity_name}!`, 'success');
           } catch (analysisError) {
             console.error('Error starting analysis:', analysisError);
             this.showNotification(`Subject created successfully, but failed to start analysis: ${analysisError.message}`, 'warning');
           }
         } else {
           const message = response.data?.message || "Subject processing initiated successfully."; 
           this.showNotification(message, 'success');
         }
         
         this.closeCreateFromCoiModal(); // Close modal on final success
         await this.refreshData(); // Refresh dashboard data
         
      } catch (err) {
         console.error('Error confirming COI creation:', err);
         let errorMsg = 'Failed to confirm COI creation.';
         if (err.response && err.response.data) {
           errorMsg = err.response.data.error || err.response.data.detail || errorMsg;
           if (typeof err.response.data === 'object' && err.response.data !== null) {
               const errors = Object.entries(err.response.data)
                   .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                   .join('; ');
               if (errors && !errorMsg.includes('Details')) errorMsg += ` Details: ${errors}`;
           }
         } else if (err.message) {
           errorMsg = err.message;
         }
         this.createFromCoiError = errorMsg; // Display error within the modal
      } finally {
         this.isConfirmingCoi = false;
      }
    }, 
    // --- END NEW METHOD ---

    // --- Handle new COI file selection --- 
    handleNewCoiUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.newCoiFile = file;
        this.selectedCoiDocumentId = ''; // Clear dropdown selection
        this.createFromCoiError = null; // Clear errors
      } else {
        this.newCoiFile = null;
      }
      // Reset file input value so @change triggers even if same file is selected again
      event.target.value = null;
    },
    clearNewCoiFile() {
      this.newCoiFile = null;
      // Also clear the visual file input element if needed
      const fileInput = document.getElementById('new-coi-file');
      if (fileInput) {
          fileInput.value = '';
      }
    },


 

    // --- ADDED: Helper to upload the new COI file --- 
    async uploadNewCoiFile() {
      if (!this.newCoiFile) {
        throw new Error("No new COI file selected for upload.");
      }
      
      this.isUploadingNewCoi = true;
      this.createFromCoiError = null; // Clear previous errors before upload
      console.log(`Uploading new COI file: ${this.newCoiFile.name}`);

      try {
        const formData = new FormData();
        formData.append('name', this.newCoiFile.name);
        formData.append('document_type', 'CERTIFICATE'); // Hardcode type
        formData.append('file', this.newCoiFile);
        
        const response = await api.post(`/projects/${this.projectId}/documents/upload/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (!response.data || !response.data.document || !response.data.document.id) {
            throw new Error("Upload response did not include valid document data.");
        }
        
        console.log("New COI uploaded successfully:", response.data.document);
        this.showNotification(`COI file '${this.newCoiFile.name}' uploaded.`, 'info');
        
        // Store the uploaded document info for the document viewer
        this.uploadedDocumentInfo = response.data.document;
        console.log("Stored uploaded document info:", this.uploadedDocumentInfo);
        
        return response.data.document.id; // Return the new document ID

      } catch (err) {
        console.error('Error uploading new COI file:', err);
        let errorMsg = 'Failed to upload new COI file.';
         if (err.response && err.response.data) {
           errorMsg = err.response.data.error || err.response.data.detail || errorMsg;
         } else if (err.message) {
           errorMsg = err.message;
         }
        // Set error state for the modal specifically
        this.createFromCoiError = errorMsg; 
        throw new Error(errorMsg); // Re-throw to stop confirmCreateSubjectFromCoi
      } finally {
         this.isUploadingNewCoi = false;
      }
    },
    // --- END ADDED ---

    // --- ADDED: Helper to decide if other entity should be shown --- 
    shouldShowOtherEntity(key) {
        // Show if data exists for this key AND it's not the currently selected subject source
        // --- UPDATED: Use parsedCoiData --- 
        return this.parsedCoiData && 
               this.parsedCoiData[key] && 
               this.parsedCoiData[key].name && 
               key !== this.selectedSubjectSourceKey;
        // --- END UPDATE --- 
    },
    // --- END ADDED ---

    // Enhanced Result Parsing Methods for Enhanced Prompts
    getResultPassed(result) {
      if (!result) return false;
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.result) {
            return enhancedData.result.toUpperCase() === 'PASS';
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed === true;
    },

    getResultDisplayText(result) {
      if (!result) return 'PENDING';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.result) {
            return enhancedData.result.toUpperCase();
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed ? 'PASSED' : 'FAILED';
    },

    getResultBadgeClass(result) {
      if (!result) return 'result-pending';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.result) {
            const resultValue = enhancedData.result.toUpperCase();
            switch (resultValue) {
              case 'PASS': return 'result-passed';
              case 'FAIL': return 'result-failed';
              case 'PARTIAL': return 'result-partial';
              case 'UNCLEAR': return 'result-unclear';
              default: return 'result-pending';
            }
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed ? 'result-passed' : 'result-failed';
    },

    getResultTooltip(result) {
      if (!result) return 'Analysis pending';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.confidence) {
            const confidence = Math.round(enhancedData.confidence * 100);
            return `Result: ${enhancedData.result || 'Unknown'} (${confidence}% confidence)`;
          }
        } catch (e) {
          // Not JSON, fall through to legacy format
        }
      }
      
      // Legacy format fallback
      return result.passed ? 'Requirement passed' : 'Requirement failed';
    },

    getEvidenceText(result) {
      if (!result) return '--';
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          if (enhancedData.evidence) {
            return enhancedData.evidence;
          }
        } catch (e) {
          // Not JSON, treat as simple text evidence
          return result.evidence;
        }
      }
      
      // Legacy format or simple evidence
      return result.evidence || '--';
    },

    // New methods for expand/collapse functionality
    shouldShowExpandButton(text) {
      return text && text.length > 150; // Show expand button if text is longer than 150 chars
    },

    isExpanded(section, resultId) {
      const key = `${section}_${resultId}`;
      return this.expandedStates[key] || false;
    },

    toggleExpanded(section, resultId) {
      const key = `${section}_${resultId}`;
      const oldValue = this.expandedStates[key] || false;
      
      // Vue 3 reactive update using Object.assign for reactivity
      this.expandedStates = {
        ...this.expandedStates,
        [key]: !oldValue
      };
    },

    getDisplayText(text, section, resultId) {
      if (!text) return '';
      
      const isExpanded = this.isExpanded(section, resultId);
      const shouldTruncate = this.shouldShowExpandButton(text);
      
      if (!shouldTruncate || isExpanded) {
        return text;
      }
      
      return text.substring(0, 150) + '...';
    },

    getConfidenceScore(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return null;
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.confidence || null;
      } catch (e) {
        return null;
      }
    },

    getFullEvidence(result) {
      if (!result) {
        console.log('🔴 getFullEvidence: result is null/undefined');
        return '';
      }
      
      console.log('🔍 getFullEvidence called with result.evidence type:', typeof result.evidence);
      console.log('🔍 getFullEvidence result.evidence preview:', result.evidence ? result.evidence.substring(0, 100) : 'null');
      
      // Handle new enhanced prompt format
      if (result.evidence && typeof result.evidence === 'string') {
        try {
          const enhancedData = JSON.parse(result.evidence);
          console.log('✅ Successfully parsed evidence JSON. Keys:', Object.keys(enhancedData));
          console.log('✅ enhancedData.evidence value:', enhancedData.evidence);
          const evidenceValue = enhancedData.evidence || 'Enhanced analysis completed';
          console.log('✅ Returning evidence value:', evidenceValue);
          return evidenceValue;
        } catch (e) {
          console.warn('⚠️ Failed to parse evidence as JSON:', e.message);
          console.warn('⚠️ Raw evidence string:', result.evidence.substring(0, 200));
          // Check if the entire evidence field is the raw JSON (common issue)
          if (result.evidence.includes('"result":') && result.evidence.includes('"confidence":')) {
            // This is likely raw JSON being displayed - extract just the evidence part
            try {
              const match = result.evidence.match(/"evidence":\s*"([^"]+)"/);
              if (match) {
                console.log('✅ Extracted evidence via regex:', match[1]);
                return match[1];
              }
              console.warn('⚠️ Regex match failed');
              return 'Enhanced analysis completed';
            } catch (extractError) {
              console.error('❌ Regex extraction error:', extractError);
              return 'Enhanced analysis completed';
            }
          }
          // Not JSON, return raw evidence
          console.log('📝 Returning raw evidence (not JSON)');
          return result.evidence;
        }
      }
      
      console.log('🔴 Evidence is not a string, returning as-is');
      return result.evidence || '';
    },

    hasEnhancedData(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return false;
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.result || enhancedData.confidence || enhancedData.reasoning;
      } catch (e) {
        return false;
      }
    },

    getConfidence(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return null;
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.confidence || null;
      } catch (e) {
        return null;
      }
    },

    getReasoning(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return '';
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.reasoning || '';
      } catch (e) {
        // If it's not JSON, return empty for reasoning (avoid displaying raw JSON)
        return '';
      }
    },

    getDeficiencies(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return [];
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.deficiencies || [];
      } catch (e) {
        return [];
      }
    },

    getDeficienciesText(result) {
      const deficiencies = this.getDeficiencies(result);
      return deficiencies.join('; ');
    },

    getSuggestions(result) {
      if (!result || !result.evidence || typeof result.evidence !== 'string') return [];
      
      try {
        const enhancedData = JSON.parse(result.evidence);
        return enhancedData.suggestions || [];
      } catch (e) {
        return [];
      }
    },

    getSuggestionsText(result) {
      const suggestions = this.getSuggestions(result);
      return suggestions.join('; ');
    },

    hasAdditionalInfo(result) {
      const reasoning = this.getReasoning(result);
      const deficiencies = this.getDeficiencies(result);
      const suggestions = this.getSuggestions(result);
      return reasoning.length > 0 || deficiencies.length > 0 || suggestions.length > 0;
    },

    toggleResultDetails(resultId) {
      if (!this.expandedResults) {
        this.expandedResults = {};
      }
      this.expandedResults = {
        ...this.expandedResults,
        [resultId]: !this.expandedResults[resultId]
      };
    },

    // --- HITL Override Methods ---
    openOverrideModal(requirementResult, subjectId) {
      console.log('Opening override modal for result:', requirementResult);
      this.overrideTarget = requirementResult;
      this.overrideSubjectId = subjectId;
      this.overrideData = {
        newResult: '',
        reason: '',
        comments: ''
      };
      this.showOverrideModal = true;
    },

    closeOverrideModal() {
      this.showOverrideModal = false;
      this.overrideTarget = null;
      this.overrideSubjectId = null;
      this.overrideData = {
        newResult: '',
        reason: '',
        comments: ''
      };
    },

    openFeedbackModal(requirementResult, subjectId) {
      // For now, just prompt for a comment using a simple dialog
      const comment = prompt(
        `Why was this LLM result accurate?\n\nRequirement: ${requirementResult.requirement_name}\nResult: ${this.getResultDisplayText(requirementResult)}\n\nYour feedback helps improve model performance:`
      );
      
      if (comment && comment.trim()) {
        this.submitDetailedFeedback(requirementResult.id, subjectId, 'ACCURATE', comment.trim());
      }
    },

    async submitDetailedFeedback(resultId, subjectId, accuracy, comment) {
      try {
        const response = await api.patch(`/requirement-results/${resultId}/feedback/`, {
          accuracy: accuracy,
          comment: comment
        });
        
        // Update local data
        if (this.showResultsDialog && this.requirementResults) {
          const resultIndex = this.requirementResults.findIndex(r => r.id === resultId);
          if (resultIndex !== -1) {
            this.requirementResults.splice(resultIndex, 1, response.data);
          }
        }
        
        this.showNotification(`Feedback saved: "${comment}"`, 'success');
        
      } catch (error) {
        console.error('Error submitting detailed feedback:', error);
        this.showNotification('Failed to save feedback: ' + (error.response?.data?.error || error.message), 'error');
      }
    },

    // --- Enhanced Correction Modal Methods ---
    openCorrectionModal(requirementResult) {
      console.log('Opening correction modal for result:', requirementResult);
      this.correctionTarget = requirementResult;
      this.showCorrectionModal = true;
    },

    closeCorrectionModal() {
      this.showCorrectionModal = false;
      this.correctionTarget = null;
    },

    async submitCorrection(correctionData) {
      if (!this.correctionTarget) return;

      try {
        const response = await api.patch(`/requirement-results/${this.correctionTarget.id}/feedback/`, correctionData);
        
        this.showNotification('Correction submitted successfully!', 'success');
        
        // Update the local data
        this.updateLocalRequirementResult(this.correctionTarget.id, response.data);
        
      } catch (error) {
        console.error('Error submitting correction:', error);
        this.showNotification(`Failed to submit correction: ${error.message || 'Unknown error'}`, 'error');
      }
    },

    updateLocalRequirementResult(resultId, updatedResult) {
      // Update in results modal if open
      if (this.showResultsDialog && this.requirementResults) {
        const resultIndex = this.requirementResults.findIndex(r => r.id === resultId);
        if (resultIndex !== -1) {
          this.requirementResults.splice(resultIndex, 1, updatedResult);
        }
      }
      
      // Update in dashboard table
      for (const subjectId in this.latestSubjectRequirementResults) {
        const subjectResults = this.latestSubjectRequirementResults[subjectId];
        const resultIndex = subjectResults.findIndex(r => r.id === resultId);
        if (resultIndex !== -1) {
          this.latestSubjectRequirementResults[subjectId].splice(resultIndex, 1, updatedResult);
          break;
        }
      }
    },

    getCorrectionButtonClass(result) {
      if (result.user_feedback_accuracy === 'ACCURATE') {
        return 'btn-success';
      } else if (result.user_feedback_accuracy === 'PARTIALLY_CORRECT') {
        return 'btn-warning';
      } else if (result.user_feedback_accuracy === 'INACCURATE') {
        return 'btn-danger';
      }
      return 'btn-secondary';
    },

    getCorrectionButtonText(result) {
      if (result.correct_result_should_be || result.correct_evidence_should_be) {
        return 'Edit Correction';
      } else if (result.user_feedback_accuracy === 'ACCURATE') {
        return 'Add Details';
      } else {
        return 'Provide Correction';
      }
    },

    getCorrectionResultBadgeClass(result) {
      if (result === 'PASS') return 'bg-success';
      if (result === 'FAIL') return 'bg-danger';
      if (result === 'UNCLEAR') return 'bg-secondary';
      return 'bg-light';
    },

    handleCorrectionError(error) {
      console.error('Correction modal error:', error);
      this.showNotification(`Correction error: ${error.message || 'Unknown error'}`, 'error');
    },

    async submitOverride() {
      if (!this.overrideTarget || !this.overrideData.newResult || !this.overrideData.reason) {
        this.showNotification('Please fill in all required fields', 'error');
        return;
      }

      try {
        console.log('Submitting override for result:', this.overrideTarget.id);
        
        // Call API to override the result
        await api.post(`/requirement-results/${this.overrideTarget.id}/override/`, {
          override_result: this.overrideData.newResult,
          override_reason: this.overrideData.reason,
          override_comments: this.overrideData.comments
        });

        // Update local data in requirementResults array to trigger reactivity
        const resultIndex = this.requirementResults.findIndex(r => r.id === this.overrideTarget.id);
        if (resultIndex !== -1) {
          // Update the result in the array to trigger computed property updates
          const updatedResult = { ...this.requirementResults[resultIndex] };
          updatedResult.passed = this.overrideData.newResult === 'PASS';
          updatedResult.user_override = true;
          updatedResult.override_reason = this.overrideData.reason;
          updatedResult.override_comments = this.overrideData.comments;
          
          // If this is an enhanced prompt result (JSON evidence), update the evidence too
          if (updatedResult.evidence && typeof updatedResult.evidence === 'string') {
            try {
              const evidenceData = JSON.parse(updatedResult.evidence);
              evidenceData.result = this.overrideData.newResult;
              evidenceData.user_override = true;
              updatedResult.evidence = JSON.stringify(evidenceData);
              console.log(`Updated enhanced prompt evidence for result ${this.overrideTarget.id}`);
            } catch (e) {
              console.log(`Result ${this.overrideTarget.id} doesn't have JSON evidence, using passed field only`);
            }
          }
          
          // Use Vue's array reactivity
          this.requirementResults.splice(resultIndex, 1, updatedResult);
          console.log(`Updated result ${this.overrideTarget.id} in requirementResults array - new passed:`, updatedResult.passed);
          console.log(`getResultPassed check:`, this.getResultPassed(updatedResult));
        }

        this.showNotification('Result successfully overridden', 'success');
        this.closeOverrideModal();
        
        // Note: We don't need to refresh detailed status since we're in the results modal
        // The local update above should be sufficient for immediate UI feedback

      } catch (error) {
        console.error('Error submitting override:', error);
        this.showNotification('Failed to override result: ' + (error.response?.data?.error || error.message), 'error');
      }
    }
    // --- END HITL Override Methods ---
  }
};
</script>

<style scoped>
.internal-review-note {
  margin: 0.5rem 0 0;
  color: #92400e;
  background: #fffbeb;
  border-left: 3px solid #d97706;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.analysis-dashboard {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.dashboard-actions {
  display: flex;
  gap: 10px;
}

/* Notifications */
.notification {
  padding: 12px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notification-info {
  background-color: #e3f2fd;
  color: #0d47a1;
  border-left: 4px solid #2196f3;
}

.notification-success {
  background-color: #e8f5e9;
  color: #1b5e20;
  border-left: 4px solid #4caf50;
}

.notification-error {
  background-color: #ffebee;
  color: #b71c1c;
  border-left: 4px solid #f44336;
}

.notification-close {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
}

.notification-close:hover {
  opacity: 1;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border-left-color: var(--color-primary);
  animation: spin 1s ease infinite;
}

.spinner-sm {
  border: 3px solid rgba(0, 0, 0, 0.1);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border-left-color: currentColor;
  animation: spin 1s ease infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error */
.error-container {
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
}

/* Dashboard content */
.dashboard-content {
  margin-top: 20px;
}

/* Simulation mode notification */
.simulation-notice {
  background-color: #e3f2fd;
  border-radius: 6px;
  padding: 12px 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.simulation-notice i {
  margin-right: 10px;
  color: #0d47a1;
}

.simulation-notice span {
  color: #0d47a1;
}

/* Stats cards */
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stat-title {
  font-size: 14px;
  color: #757575;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: var(--color-primary);
}

/* Tabs */
.tabs {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 20px;
}

.tab-button {
  padding: 10px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #757575;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.tab-button:hover {
  color: var(--color-primary);
}

.tab-button.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
}

.tab-content {
  padding: 10px 0;
}

/* Empty state */
.empty-state {
  padding: 30px;
  text-align: center;
  background-color: #f5f5f5;
  border-radius: 6px;
  color: #757575;
}

/* Table styles */
.analyses-table-container {
  overflow-x: auto;
}

.analyses-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
  background-color: white;
}

.analyses-table th,
.analyses-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.analyses-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.analyses-table tr:hover {
  background-color: #f9f9f9;
}

.actions-cell {
  white-space: nowrap;
}

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-completed {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.status-pending, .status-in_progress {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.status-failed {
  background-color: #ffebee;
  color: #b71c1c;
}

/* Severity badges */
.severity-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.severity-critical {
  background-color: #ffebee;
  color: #b71c1c;
}

.severity-high {
  background-color: #fff8e1;
  color: #ff6f00;
}

.severity-medium {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.severity-low {
  background-color: #e3f2fd;
  color: #0d47a1;
}

/* Result badges */
.result-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.result-passed {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.result-failed {
  background-color: #ffebee;
  color: #b71c1c;
}

.result-partial {
  background-color: #fff3e0;
  color: #ef6c00;
}

.result-unclear {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.result-pending {
  background-color: #e3f2fd;
  color: #0d47a1;
}

/* Templates grid */
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.template-card {
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.template-header {
  padding: 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.template-header h3 {
  margin: 0;
  font-size: 18px;
}

.template-body {
  padding: 15px;
  flex-grow: 1;
}

.requirements-summary {
  margin: 10px 0;
}

.requirement-count {
  display: inline-block;
  padding: 4px 8px;
  background-color: #e3f2fd;
  color: #0d47a1;
  border-radius: 4px;
  font-size: 12px;
}

.document-types {
  font-size: 14px;
  color: #757575;
}

.template-footer {
  padding: 15px;
  border-top: 1px solid #e0e0e0;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-large {
  width: 800px;
}

.modal-extra-large {
  width: 95%;
  max-width: 1400px;
  height: 90vh;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #757575;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Document selection in modal */
.document-selection {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-top: 10px;
}

.document-item {
  padding: 10px 15px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.document-item:last-child {
  border-bottom: none;
}

.document-item:hover {
  background-color: #f5f5f5;
}

.document-item.selected {
  background-color: #e3f2fd;
}

.document-item input[type="checkbox"] {
  margin-right: 10px;
}

.document-details {
  flex-grow: 1;
}

.document-name {
  font-weight: 500;
}

.document-meta {
  font-size: 12px;
  color: #757575;
}

.selection-summary {
  margin-top: 10px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

/* Results dialog - Compact layout */
.results-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.result-info {
  line-height: 1.4;
  font-size: 0.9rem;
}

.result-summary {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.summary-card {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  min-width: 80px;
  text-align: center;
}

.summary-title {
  font-size: 0.7rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  font-weight: 500;
}

.summary-value {
  font-size: 1.1rem;
  font-weight: 600;
}

.result-success {
  color: #2e7d32;
}

.result-warning {
  color: #ff6f00;
}

.result-danger {
  color: #b71c1c;
}

.requirements-results {
  margin-top: 20px;
}

.requirements-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
}

.requirements-table th,
.requirements-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.requirements-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.failed-row {
  background-color: #fff8f8;
}

/* Loading inline */
.loading-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #757575;
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s, transform 0.1s;
}

.btn:active {
  transform: translateY(1px);
}

.btn-sm {
  padding: 4px 10px;
  font-size: 12px;
}

.btn-primary {
  background-color: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--color-secondary);
}

.btn-secondary {
  background-color: #e0e0e0;
  color: #424242;
}

.btn-secondary:hover {
  background-color: #d5d5d5;
}

.btn-warning {
  background-color: #ffb74d;
  color: #fff;
}

.btn-warning:hover {
  background-color: #ffa726;
}

.btn:disabled {
  background-color: #bdbdbd;
  cursor: not-allowed;
}

/* Hints */
.hint {
  font-size: 12px;
  color: #757575;
  font-style: italic;
}

/* Form styles */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.file-upload-container {
  position: relative;
  border: 2px dashed #ddd;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s;
  cursor: pointer;
}

.file-upload-container:hover {
  border-color: var(--color-primary);
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.file-upload-label {
  color: #666;
}

.file-upload-label i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.upload-error {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #ffebee;
  border-left: 3px solid #f44336;
  color: #b71c1c;
  border-radius: 4px;
}

/* Added Action Log Section */
.action-log-results {
  margin-top: 20px;
}

.action-log-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
}

.action-log-table th,
.action-log-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.action-log-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.action-log-table tr:hover {
  background-color: #f9f9f9;
}

.action-log-table td {
  font-size: 14px;
  color: #757575;
}

.action-log-table .status {
  font-weight: 500;
}

.action-log-table .status.passed {
  color: #2e7d32;
}

.action-log-table .status.failed {
  color: #b71c1c;
}

/* Run doc-type badge in history table */
.run-doc-type-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  vertical-align: middle;
}

/* Exclusion Scan Results */
.exclusion-scan-results {
  margin-top: 20px;
  padding: 16px 20px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
}

.exclusion-scan-results h3 {
  margin: 0 0 12px 0;
  font-size: 1rem;
  font-weight: 700;
  color: #78350f;
  display: flex;
  align-items: center;
  gap: 8px;
}

.exclusion-scan-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.scan-doc-badge {
  padding: 2px 10px;
  background: #fef9c3;
  border: 1px solid #fde047;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #713f12;
  font-weight: 500;
}

.scan-status-badge {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.scan-completed { background: #dcfce7; color: #166534; }
.scan-error { background: #fee2e2; color: #991b1b; }
.scan-skipped { background: #f1f5f9; color: #475569; }

.exclusion-group { margin-bottom: 14px; }

.exclusion-group-title {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.exclusion-group-title.warn { color: #b45309; }
.exclusion-group-title.info { color: #0369a1; }
.exclusion-group-title.danger { color: #b91c1c; }

.exclusion-chips { display: flex; flex-wrap: wrap; gap: 6px; }

.exclusion-chip {
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.exclusion-chip.warn { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
.exclusion-chip.info { background: #e0f2fe; color: #075985; border: 1px solid #bae6fd; }

.exclusion-none-found {
  font-size: 0.875rem;
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.exclusion-flag-list {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.875rem;
  color: #7f1d1d;
}

.exclusion-flag-list li { margin-bottom: 4px; }

.exclusion-summary {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #fde68a;
}

.exclusion-summary label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.exclusion-summary p {
  margin: 0;
  font-size: 0.875rem;
  color: #451a03;
  line-height: 1.5;
}

/* Feedback cell */
.feedback-cell {
  /* Removed display: flex and related properties */
  text-align: center; /* Center buttons horizontally */
  /* vertical-align: top; is inherited from .requirements-table td */
}

.feedback-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #757575;
  margin: 0 5px;
}

.feedback-btn:disabled {
  color: #bdbdbd;
  cursor: not-allowed;
}

.feedback-btn.active {
  color: var(--color-primary);
}

/* Optional: Style for btn-info if not defined globally */
.btn-info {
    color: #fff;
    background-color: #0dcaf0;
    border-color: #0dcaf0;
}
.btn-info:hover {
    color: #fff;
    background-color: #31d2f2;
    border-color: #25cff2;
}

/* Analysis History Table Specific */
.analysis-history-table-container {
    max-height: 300px; /* Limit height and make scrollable if needed */
    overflow-y: auto;
}
.analysis-history-table th {
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
    background-color: #f8f9fa;
}
.analysis-history-table td {
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
    vertical-align: middle;
}

/* --- ADDED: Styles for Main Subjects Table Column Widths --- */
.subjects-table thead th {
  /* Add explicit widths or min-widths */
  /* Example using percentages (adjust as needed): */
  &:nth-child(1) { width: 35%; } /* Subject Name */
  &:nth-child(2) { width: 15%; } /* Status */
  &:nth-child(3) { width: 25%; } /* Last Updated */
  &:nth-child(4) { width: 25%; } /* Actions */

  /* Ensure text doesn't wrap excessively if needed */
   white-space: nowrap; 
}
/* --- END ADDED --- */

/* --- ADDED: Specific Column Widths for Detail Tables --- */

/* Requirements Table */
.requirements-table thead th {
  white-space: normal; /* Allow header text to wrap */
  vertical-align: bottom;
}
.requirements-table thead th:nth-child(1) { width: 28%; } /* Requirement */
.requirements-table thead th:nth-child(2) { width: 12%; } /* Severity */
.requirements-table thead th:nth-child(3) { width: 12%; } /* Result */
.requirements-table thead th:nth-child(4) { width: 33%; } /* Evidence */
.requirements-table thead th:nth-child(5) { width: 15%; } /* Feedback */

.requirements-table td {
   vertical-align: top; /* Align long evidence nicely */
   word-break: break-word; /* Help wrap long text */
}
.requirements-table td:nth-child(4) { /* Evidence column */
    font-size: 0.8em; /* Slightly smaller font for potentially long evidence */
    color: #4a5568; 
}

/* --- ADDED: Vertically center feedback buttons --- */
.requirements-table td.feedback-cell {
    vertical-align: middle;
}
/* --- END ADDED --- */

/* Requirement Identifier Styling */
.requirement-identifier {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.req-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.req-code {
  background: #e3f2fd;
  color: #1565c0;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
}

.req-description {
  color: #666;
  font-size: 0.8rem;
  font-style: italic;
  line-height: 1.3;
}

/* Parties Table */
.parties-compliance-table thead th {
   white-space: normal; 
   vertical-align: bottom;
}
.parties-compliance-table thead th:nth-child(1) { width: 35%; } /* Party */
.parties-compliance-table thead th:nth-child(2) { width: 20%; } /* Role */
.parties-compliance-table thead th:nth-child(3) { width: 30%; } /* Status Summary */
.parties-compliance-table thead th:nth-child(4) { width: 15%; } /* Actions */

/* History Table */
.analysis-history-table thead th {
    white-space: normal; 
    vertical-align: bottom;
}
.analysis-history-table thead th:nth-child(1) { width: 35%; } /* Template/Document */
.analysis-history-table thead th:nth-child(2) { width: 25%; } /* Date */
.analysis-history-table thead th:nth-child(3) { width: 10%; } /* Status */
.analysis-history-table thead th:nth-child(4) { width: 10%; } /* Outcome */
.analysis-history-table thead th:nth-child(5) { width: 20%; } /* Actions */

/* Adjust first TD in history for the two lines */
.analysis-history-table td:nth-child(1) div.small {
    margin-top: 2px;
    line-height: 1.2;
}

/* --- END ADDED --- */

/* Analysis History Table Specific */
/* ... keep existing styles below this ... */

/* --- ADDED: Styling for Section 1 Summary --- */
.subject-context .summary-details {
  gap: 1rem; /* Increased gap slightly */
  align-items: baseline; /* Align text baseline */
}

.summary-item {
  display: inline-flex; /* Align icon and text */
  align-items: baseline; /* Align text baseline within item */
}

.summary-item i.fas {
  /* Ensure icons have consistent width and slight size increase */
  /* fa-fw ensures fixed width */
  font-size: 0.9em; 
  color: #6c757d; /* Standard muted color */
  margin-right: 0.3rem; /* Adjust spacing */
  vertical-align: text-bottom; /* Try aligning icon bottom with text */
}

.summary-label {
  /* Optional: if you want to bold the label */
  /* font-weight: 500; */
  margin-right: 0.25rem;
  color: #6c757d; /* Make label muted too */
}

.summary-value {
   color: #343a40; /* Darker value text */
   font-weight: 500; /* Slightly bolder value */
}
/* Adjust badge padding/font if needed */
.summary-value.badge {
    font-size: 0.75em; 
    vertical-align: middle;
}
/* --- END ADDED --- */

/* --- ADDED: Subject Detail Row Full Width Styling --- */
.subject-detail-row {
    width: 100%;
}

.subject-detail-row td {
    width: 100%;
    padding: 0;
}

.subject-detail-card {
    width: 100%;
    margin: 0;
    border-left: none;
    border-right: none;
    border-radius: 0;
}

/* --- ADDED: Tab Styling --- */
/* Style the container for the internal tabs */
.subject-detail-tabs {
    display: flex;
    list-style: none;
    padding-left: 0; 
    border-bottom: 1px solid #e0e0e0; /* Match main tab container border */
    margin-bottom: 0 !important; /* Override default nav margin */
    width: 100%;
    justify-content: flex-start;
    gap: 0.25rem;
    flex-wrap: nowrap;
    overflow-x: auto;
}

/* Style the individual tab links */
.subject-detail-tabs .nav-link { 
    /* Match main tab button style */
    padding: 10px 16px; 
    font-size: 0.9rem; 
    color: #757575; 
    border: none; 
    border-bottom: 3px solid transparent; 
    background: none; 
    border-radius: 0;
    transition: color 0.2s ease, border-color 0.2s ease;
    cursor: pointer; /* Ensure cursor changes */
    white-space: nowrap;
    flex: 0 0 auto;
    min-width: fit-content;
}

/* Document action buttons styling */
.document-actions-inline {
    display: inline-flex;
    gap: 0.25rem;
    margin-left: 0.5rem;
}

.doc-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 18px;
    padding: 0;
    background: none;
    border: none;
    color: #6c757d;
    font-size: 0.75rem;
    border-radius: 3px;
    transition: all 0.2s ease;
    text-decoration: none;
    cursor: pointer;
}

.doc-action-btn:hover {
    background-color: #e9ecef;
    color: #495057;
    text-decoration: none;
}

.doc-action-btn:active {
    transform: scale(0.95);
}

/* Entity relationship styling */
.relationships-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.relationship-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 0.375rem;
    white-space: nowrap;
}

.relationship-primary {
    background-color: #e3f2fd;
    color: #1565c0;
    border: 1px solid #2196f3;
}

.relationship-formal {
    background-color: #f3e5f5;
    color: #7b1fa2;
    border: 1px solid #9c27b0;
}

.relationship-document {
    background-color: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #4caf50;
}

.status-indicators {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
}

.entity-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

/* Entity info styling */
.entity-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.entity-main {
    display: flex;
    align-items: center;
}

.entity-contact {
    padding-left: 1.5rem; /* Align with entity name (accounting for icon) */
    font-size: 0.85rem;
    line-height: 1.3;
}

.entity-contact strong {
    color: #495057;
}

.entity-contact i {
    color: #6c757d;
    font-size: 0.75rem;
}
.subject-detail-tabs .nav-link:hover {
     color: var(--color-primary); /* Match main tab hover */
}
.subject-detail-tabs .nav-link.active {
    /* Match main tab active style */
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
    background: none; 
    font-weight: 500; 
}
.subject-detail-tab-content {
    /* Optional: Add padding or borders if needed */
    padding-top: 1rem;
}
/* --- END ADDED --- */

/* Optional: Add specific styles for the new modal if needed */
.document-selection.form-group label {
  margin-bottom: 0.5rem; /* Add some space below the label */
  font-weight: 500;
}

.form-select {
   /* Use default Bootstrap styles or add custom ones */
   display: block;
   width: 100%;
   padding: 0.375rem 2.25rem 0.375rem 0.75rem;
   font-size: 1rem;
   font-weight: 400;
   line-height: 1.5;
   color: #212529;
   background-color: #fff;
   background-clip: padding-box;
   border: 1px solid #ced4da;
   border-radius: 0.25rem;
   transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
   appearance: none; 
   background-repeat: no-repeat;
   background-position: right 0.75rem center;
   background-size: 16px 12px;
   /* Add dropdown arrow SVG or use Bootstrap's default */
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
}
.form-select:focus {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
}

/* Style for small empty state inside modal */
.empty-state.small {
  padding: 15px;
  font-size: 0.9em;
  background-color: #f8f9fa; /* Slightly different background */
}

/* Adjust modal footer button spacing if needed */
.modal-footer .btn {
  margin-left: 0.5rem; 
}

/* --- ADDED: Styling for Recommended Option --- */
.subject-options-container .form-check {
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid transparent; /* Base border */
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
}

.subject-options-container .form-check.recommended-option {
  background-color: #e8f5e9; /* Light green background */
  border-color: #a5d6a7; /* Slightly darker green border */
  font-weight: 500; /* Make recommended text slightly bolder */
}

.subject-options-container .form-check:not(.recommended-option):hover {
    background-color: #f8f9fa; /* Subtle hover for other options */
}
/* Ensure label text is easily readable */
.subject-options-container .form-check label i {
   color: #495057; /* Darker grey for italicized name */
}

.card-header-sm {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    background-color: #f8f9fa;
}
/* --- END ADDED --- */

/* --- ADDED: Wider modal for COI review --- */
.modal-large {
  max-width: 1000px; /* Increase max-width further */
}
/* --- END ADDED --- */

/* --- ADDED: Explicit row borders for subjects-table --- */
.subjects-table tbody tr td {
  border-bottom: 1px solid #e0e0e0; /* Match requirements-table border color */
}
/* Ensure last row in tbody doesn't have a double border if table itself has one */
.subjects-table tbody tr:last-child td {
  border-bottom: none; /* Or set to 0, or match table's main border if preferred */
}
/* --- END ADDED --- */

/* --- ADDED: Styling for subjects-table to match requirements-table more closely --- */
.subjects-table {
  border: 1px solid #e0e0e0; /* Add overall table border */
  /* table-sm already handles reduced padding, keep if desired, otherwise remove table-sm class */
}

.subjects-table th {
  background-color: #f5f5f5; /* Match header background */
  font-weight: 600; /* Match header font weight */
  border-bottom-width: 1px; /* Match requirements-table header border thickness */
  border-top: none; /* Remove any default top border on th if present */
}
/* --- END ADDED --- */

/* Enhanced Evidence Display Styles */
.evidence-cell {
  max-width: 300px;
}

.evidence-container {
  width: 100%;
}

.evidence-text {
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
}

.enhanced-details {
  margin-top: 5px;
  font-size: 0.75rem;
}

.enhanced-details .btn-xs {
  padding: 0 2px;
  font-size: 0.7rem;
}

.expanded-result-details {
  font-size: 0.8rem;
  line-height: 1.3;
  max-width: 100%;
}

.expanded-result-details .reasoning {
  color: #495057;
}

.expanded-result-details .deficiencies {
  color: #dc3545;
}

.expanded-result-details .suggestions {
  color: #28a745;
}

/* New Expandable Content Styles */
.evidence-header,
.issues-header,
.actions-header,
.confirmation-header,
.review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.expand-btn {
  background: none;
  border: none;
  color: #6c757d;
  padding: 2px 4px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.expand-btn:hover {
  background-color: #f8f9fa;
  color: #495057;
}

.expand-btn:focus {
  outline: 2px solid #007bff;
  outline-offset: 1px;
}

.evidence-content,
.reasoning-content,
.confirmation-content,
.review-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
  transition: max-height 0.3s ease;
  position: relative;
}

.evidence-content.expanded,
.reasoning-content.expanded,
.confirmation-content.expanded,
.review-content.expanded {
  max-height: none;
}

/* List styling for issues and suggestions */
.issues-list,
.actions-list {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  list-style-type: disc;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
}

.issues-list li,
.actions-list li {
  margin-bottom: 0.5rem;
  padding-left: 0.25rem;
}

.issues-list li:last-child,
.actions-list li:last-child {
  margin-bottom: 0;
}

/* Collapsed state for lists */
.issues-list:not(.expanded),
.actions-list:not(.expanded) {
  max-height: 150px;
  overflow: hidden;
  position: relative;
}

.issues-list.expanded,
.actions-list.expanded {
  max-height: none;
}

/* Improved readability for long text */
.result-text {
  margin-bottom: 0.75rem;
}

.result-text:last-child {
  margin-bottom: 0;
}

/* Better spacing for high zoom levels and DPI */
@media screen and (min-resolution: 144dpi), screen and (-webkit-min-device-pixel-ratio: 1.5) {
  .result-details {
    gap: 0.5rem;
  }
  
  .expand-btn {
    padding: 4px 6px;
    font-size: 0.8rem;
    min-width: 24px;
    min-height: 24px;
  }
  
  .evidence-content,
  .reasoning-content,
  .confirmation-content,
  .review-content {
    line-height: 1.6;
    font-size: 0.9rem;
  }
  
  .result-text {
    margin-bottom: 1rem;
  }
}

/* Improved support for very high zoom levels (200%+) */
@media screen and (min-resolution: 192dpi), screen and (-webkit-min-device-pixel-ratio: 2) {
  .result-item {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .result-content {
    gap: 1.5rem;
  }
  
  .result-badges {
    gap: 0.75rem;
  }
  
  .severity-badge {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    min-width: 60px;
    text-align: center;
  }
  
  .confidence-badge {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
    gap: 0.4rem;
  }
  
  .confidence-badge i {
    font-size: 0.7rem;
  }
  
  .expand-btn {
    padding: 6px 8px;
    font-size: 0.9rem;
    min-width: 32px;
    min-height: 32px;
    border-radius: 4px;
  }
  
  .evidence-content,
  .reasoning-content,
  .confirmation-content,
  .review-content {
    font-size: 1rem;
    line-height: 1.7;
  }
}

/* Mobile and tablet responsive improvements */
@media (max-width: 768px) {
  .result-item {
    padding: 1rem;
  }
  
  .result-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .result-badges {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }
  
  .severity-badge {
    margin-bottom: 0;
  }
  
  .expand-btn {
    padding: 6px 8px;
    font-size: 0.8rem;
  }
  
  .evidence-header,
  .issues-header,
  .actions-header,
  .confirmation-header,
  .review-header {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

/* Accessibility improvements for high contrast */
@media (prefers-contrast: high) {
  .expand-btn {
    border: 1px solid #6c757d;
  }
  
  .expand-btn:hover {
    background-color: #495057;
    color: white;
  }
}

/* Better support for reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .evidence-content,
  .reasoning-content,
  .confirmation-content,
  .review-content,
  .issues-list,
  .actions-list {
    transition: none;
  }
  
  .expand-btn {
    transition: none;
  }
}

/* Action Options Info Styling */
.action-options-info {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;
}

.action-options-info h6 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 10px;
}

.option-descriptions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-desc {
  font-size: 14px;
  line-height: 1.4;
  color: #6c757d;
}

.option-desc strong {
  color: #495057;
}

.option-desc em {
  color: #007bff;
  font-style: italic;
}

.expanded-result-details ul {
  padding-left: 1rem;
}

.expanded-result-details li {
  margin-bottom: 0.25rem;
}

/* ========================================
   MODERN COI MODAL STYLING
   ======================================== */

/* Modern Modal Container */
.modern-coi-modal {
  max-width: 900px;
  max-height: 90vh;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

/* Modern Header */
.modern-coi-header {
  background: linear-gradient(135deg, #2c3e50, #3498db);
  color: white;
  padding: 2rem;
  border-bottom: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content {
  flex: 1;
}

.header-content h2 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.step-number {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.step-text {
  font-size: 0.9rem;
  opacity: 0.9;
}

.modern-close {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  opacity: 0.8;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modern-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

/* Modern Body */
.modern-coi-body {
  padding: 0;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Upload Form Container */
.upload-form-container {
  padding: 2rem;
}

.form-section {
  margin-bottom: 2rem;
}

.form-section h3 {
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-group {
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  display: block;
}

.modern-select, 
.modern-file-input {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.modern-select:focus,
.modern-file-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

/* Form Separator */
.form-separator {
  text-align: center;
  margin: 2rem 0;
  position: relative;
}

.form-separator::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e9ecef;
}

.separator-text {
  background: white;
  padding: 0 1rem;
  font-weight: bold;
  color: #6c757d;
  font-size: 0.9rem;
}

/* File Selected Indicator */
.file-selected {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #e8f5e8;
  border: 1px solid #4caf50;
  border-radius: 6px;
  color: #2e7d32;
  font-size: 0.9rem;
}

/* Loading and Empty States */
.loading-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.empty-state-small {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
}

/* Status Messages */
.status-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  font-size: 0.95rem;
}

.status-message.loading {
  background: #e3f2fd;
  border: 1px solid #2196f3;
  color: #1565c0;
}

.status-message.error {
  background: #ffebee;
  border: 1px solid #f44336;
  color: #c62828;
}

/* Review Form Container */
.review-form-container {
  padding: 2rem;
}

.review-intro {
  margin-bottom: 2rem;
}

.review-intro p {
  color: #495057;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
}

/* Subject Selection Card */
.subject-selection-card {
  background: linear-gradient(135deg, #e3f2fd, #f8f9fa);
  border: 2px solid #3498db;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.subject-selection-card .form-label {
  font-size: 1.1rem;
  color: #2c3e50;
  margin-bottom: 1rem;
}

/* Entity Cards Grid */
.entity-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.entity-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.entity-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.entity-card-header {
  background: #f8f9fa;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.entity-card-header h6 {
  margin: 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 600;
}

.entity-card-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.entity-card-content-empty {
  padding: 2rem 1.5rem;
  text-align: center;
  color: #6c757d;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.entity-card-content-empty i {
  font-size: 1.5rem;
  opacity: 0.5;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-field .form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
}

.form-field .form-control {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.form-field .form-control:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
}

.contact-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6c757d;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
}

/* Fallback Error */
.fallback-error {
  padding: 2rem;
}

/* Streamlined Results for Efficient Scanning */
.streamlined-results {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.result-section {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.section-header {
  margin: 0;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.failed-section {
  border: 1px solid #dc3545;
}

.failed-header {
  background: #dc3545;
  color: white;
}

.passed-section {
  border: 1px solid #28a745;
}

.passed-header {
  background: #28a745;
  color: white;
}

.unclear-section {
  border: 1px solid #ffc107;
}

.unclear-header {
  background: #ffc107;
  color: #212529;
}

.result-item {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: white;
}

.result-item:last-child {
  border-bottom: none;
}

.result-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

/* Result badges container */
.result-badges {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
  align-items: flex-start;
}

.severity-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.confidence-badge {
  padding: 0.2rem 0.4rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: help;
  transition: all 0.2s ease;
}

.confidence-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.confidence-badge i {
  font-size: 0.6rem;
}

.confidence-high {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.confidence-medium {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.confidence-low {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.severity-high {
  background: #dc3545;
  color: white;
}

.severity-medium {
  background: #ffc107;
  color: #212529;
}

.severity-low {
  background: #6c757d;
  color: white;
}

.severity-unknown {
  background: #e9ecef;
  color: #495057;
}

.result-message {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.result-icon {
  flex-shrink: 0;
  font-size: 1.1rem;
  margin-top: 0.1rem;
}

.result-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.result-text {
  line-height: 1.3;
  color: #2c3e50;
  font-size: 0.85rem;
  word-wrap: break-word;
}

.result-text strong {
  font-weight: 600;
  color: #495057;
}

.result-text.evidence {
  color: #6c757d;
  font-size: 0.8rem;
}

.result-text.deficiencies {
  color: #dc3545;
}

.result-text.actions {
  color: #0d6efd;
}

.result-text.response {
  color: #28a745;
}

.result-text.review {
  color: #fd7e14;
}

/* Validation Styling */
.required-badge {
  background: #ff6b35;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-left: auto;
}

.validation-tip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #e3f2fd;
  border-left: 3px solid #2196f3;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: #1565c0;
}

.required-field {
  color: #495057 !important;
}

.required-asterisk {
  color: #dc3545;
  font-weight: bold;
  margin-left: 2px;
}

.is-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.1) !important;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 0.5rem;
}

/* Split-Pane Layout for Document Review */
.split-pane-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.split-pane-layout {
  display: grid;
  grid-template-columns: 50% 50%; /* Equal split for comprehensive check results */
  flex: 1;
  gap: 0;
  min-height: 0; /* Important for proper flex shrinking */
}

.document-panel,
.extracted-data-panel,
.results-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0; /* Important for proper flex shrinking */
}

.document-panel {
  border-right: 1px solid #e9ecef;
  background: #f8f9fa;
}

.extracted-data-panel,
.results-panel {
  background: white;
  overflow-y: auto;
}

.panel-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: white;
  flex-shrink: 0;
}

.panel-header h4 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
}

.panel-subtitle {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.document-viewer {
  flex: 1;
  padding: 1.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.document-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.document-info {
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.document-info h5 {
  margin: 0 0 0.25rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.document-meta {
  font-size: 0.9rem;
  color: #6c757d;
}

.document-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.btn-outline {
  background: transparent;
  border: 1px solid #6c757d;
  color: #6c757d;
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: #6c757d;
  color: white;
  text-decoration: none;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.document-preview {
  flex: 1;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: auto;
  background: white;
  min-height: 600px;
  max-height: 85vh;
}

.pdf-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-preview {
  flex: 1;
  width: 100%;
  height: 80vh;
  border: none;
  border-radius: 8px 8px 0 0;
  min-height: 600px;
  /* Ensure PDF embed allows scrolling */
  overflow: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on mobile */
}

.pdf-fallback {
  padding: 10px;
  text-align: center;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
  border-top: 1px solid #dee2e6;
}

.pdf-fallback p {
  margin: 5px 0;
}

.pdf-fallback .fas {
  color: #dc3545;
  margin-right: 5px;
}

.preview-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
  padding: 2rem;
}

.preview-placeholder i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-document {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #dc3545;
  text-align: center;
  padding: 2rem;
}

.no-document i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.extracted-data-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

/* Much wider modal for document viewing */
.modern-coi-modal {
  max-width: 95vw;
  width: 1400px;
  max-height: 90vh;
}

/* Make it even wider on large screens */
@media (min-width: 1600px) {
  .modern-coi-modal {
    width: 1600px;
  }
}

/* Responsive Design */
@media (max-width: 1200px) {
  .split-pane-layout {
    grid-template-columns: 1fr;
    grid-template-rows: 350px 1fr;
  }
  
  .document-panel {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    max-height: 350px;
  }
  
  .modern-coi-modal {
    max-width: 95vw;
  }
}

@media (max-width: 768px) {
  .modern-coi-modal {
    max-width: 95vw;
    margin: 1rem;
  }
  
  .modern-coi-header {
    padding: 1.5rem;
  }
  
  .header-content h2 {
    font-size: 1.5rem;
  }
  
  .upload-form-container {
    padding: 1rem;
  }
  
  .extracted-data-content {
    padding: 1rem;
  }
  
  .entity-cards-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .split-pane-layout {
    grid-template-rows: 300px 1fr;
  }
  
  .document-panel {
    max-height: 300px;
  }
}

/* Document Intelligence Style Matching */
.project-compliance {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 0;
}

/* Breadcrumb Navigation */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  padding: 0 2rem;
}

.breadcrumb-item {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.breadcrumb-item:hover {
  color: white;
}

.breadcrumb-item.active {
  color: white;
  font-weight: 600;
}

.breadcrumb-separator {
  color: rgba(255, 255, 255, 0.6);
}

/* Project Header */
.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding: 0 2rem;
}

.project-info {
  flex: 1;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.project-metadata {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.project-type,
.client-type {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  display: inline-block;
  backdrop-filter: blur(10px);
  width: fit-content;
}

.project-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin-top: 0.5rem;
}

.project-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  color: white;
}

.btn-outline-primary {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

/* Intelligence Hub */
.intelligence-hub {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Intelligence Overview */
.intelligence-overview {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.overview-header {
  text-align: center;
  margin-bottom: 2rem;
}

.overview-header h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.overview-description {
  color: #718096;
  font-size: 1.1rem;
  margin: 0;
}

/* Compliance Stats */
.compliance-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.total-subjects .stat-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
}

.compliant-count .stat-icon {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
}

.non-compliant-count .stat-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.stat-content h3 {
  font-size: 2rem;
  font-weight: 800;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.stat-content p {
  color: #718096;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.stat-status .status {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.status.active {
  background: #dcfce7;
  color: #166534;
}

.status.pending {
  background: #fef3c7;
  color: #92400e;
}

.status.success {
  background: #dcfce7;
  color: #166534;
}

.status.warning {
  background: #fed7aa;
  color: #c2410c;
}

.status.disabled {
  background: #f3f4f6;
  color: #6b7280;
}

/* Intelligence Card */
.intelligence-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-description {
  color: #718096;
  margin: 0;
  font-size: 1rem;
}

/* Management Controls */
.management-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.search-filter-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.search-input {
  padding: 0.5rem 0.75rem 0.5rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  width: 250px;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.selection-count {
  font-size: 0.9rem;
  color: #374151;
  font-weight: 600;
}

.add-subject-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

/* Results Summary */
.results-summary {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.summary-text {
  font-size: 0.9rem;
  color: #1e40af;
  font-weight: 500;
}

/* Modern Table */
.subjects-table-container {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.table-wrapper {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.modern-table thead {
  background: #f9fafb;
}

.modern-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  position: relative;
}

.modern-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.modern-table th.sortable:hover {
  background: #f3f4f6;
}

.modern-table th i {
  margin-left: 0.5rem;
  font-size: 0.8rem;
}

.modern-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.subject-row:hover {
  background: #f9fafb;
}

.subject-row.selected {
  background: #eff6ff;
}

.select-column {
  width: 50px;
  text-align: center;
}

.subject-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.subject-icon {
  color: #6b7280;
}

.name {
  font-weight: 600;
  color: #111827;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-compliant {
  background: #dcfce7;
  color: #166534;
}

.status-non_compliant {
  background: #fef2f2;
  color: #dc2626;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-reviewing {
  background: #dbeafe;
  color: #1d4ed8;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 1.5rem;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
}

.pagination-info {
  font-size: 0.9rem;
  color: #6b7280;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 1rem;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-size-controls {
  font-size: 0.9rem;
  color: #6b7280;
}

.page-size-controls select {
  margin: 0 0.5rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.9rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
}

.empty-icon {
  font-size: 4rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
}

.empty-state p {
  font-size: 1rem;
  margin: 0 0 2rem 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* Tools Grid */
.tools-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.tool-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  text-align: center;
}

.tool-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.tool-card h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.tool-card p {
  color: #718096;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .compliance-stats {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .tools-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .management-controls {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .controls-right {
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  .project-compliance {
    padding: 1rem 0;
  }
  
  .project-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .intelligence-hub {
    padding: 0 1rem;
  }
  
  .intelligence-overview,
  .intelligence-card {
    padding: 1.5rem;
  }
  
  .search-input {
    width: 200px;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}

/* ===== AGENT INTELLIGENCE STYLES ===== */

.agent-evaluation-content {
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.agent-recommendation-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.recommendation-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.action-label {
  font-weight: 600;
  opacity: 0.9;
}

.action-value {
  font-weight: 700;
  font-size: 1.1rem;
}

.urgency-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: auto;
}

.urgency-critical {
  background: #dc2626;
  color: white;
}

.urgency-high {
  background: #ea580c;
  color: white;
}

.urgency-medium {
  background: #d97706;
  color: white;
}

.urgency-low {
  background: #16a34a;
  color: white;
}

.agent-reasoning {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.reasoning-text {
  margin: 0;
  line-height: 1.6;
  color: #374151;
}

.confidence-metrics {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.confidence-bar {
  background: #f1f5f9;
  border-radius: 8px;
  height: 20px;
  overflow: hidden;
  margin: 0.5rem 0;
  position: relative;
}

.confidence-fill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.5s ease;
}

.confidence-very-high {
  background: linear-gradient(90deg, #10b981, #059669);
}

.confidence-high {
  background: linear-gradient(90deg, #3b82f6, #2563eb);
}

.confidence-medium {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.confidence-low {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.confidence-unknown {
  background: linear-gradient(90deg, #6b7280, #4b5563);
}

.confidence-percentage {
  font-weight: 600;
  color: #374151;
}

.resolution-timeline {
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.timeline-label {
  font-weight: 500;
}

.timeline-value {
  font-weight: 600;
  color: #374151;
}

.specific-actions {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.actions-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 4px;
  border-left: 3px solid #3b82f6;
}

.action-item:last-child {
  margin-bottom: 0;
}

.action-name {
  font-weight: 600;
  color: #374151;
  margin-right: 0.5rem;
}

.action-description {
  color: #6b7280;
  line-height: 1.5;
}

.agent-risk-factors {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.risk-factors-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.risk-factor-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #fef3c7;
  border-radius: 4px;
  border-left: 3px solid #f59e0b;
  color: #92400e;
}

.agent-metadata {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.metadata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.metadata-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metadata-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metadata-value {
  font-weight: 600;
  color: #374151;
}

.agent-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .recommendation-summary {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .urgency-badge {
    margin-left: 0;
  }
  
  .metadata-grid {
    grid-template-columns: 1fr;
  }
  
  .action-item {
    flex-direction: column;
    gap: 0.25rem;
  }
}

/* END AGENT INTELLIGENCE STYLES */

/* HITL Override Modal Styles */
.override-modal {
  max-width: 600px;
}

.override-buttons {
  display: flex;
  gap: 0.25rem;
}

.override-btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.override-details .requirement-summary {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #ffc107;
}

.override-form .form-group {
  margin-bottom: 1rem;
}

.override-options {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.radio-option:hover {
  background-color: #f8f9fa;
}

.radio-option input[type="radio"] {
  margin: 0;
}

.radio-option input[type="radio"]:checked + .result-badge {
  box-shadow: 0 0 0 2px #007bff;
}

.current-result {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.evidence-summary {
  font-size: 0.9rem;
  color: #6c757d;
}

/* Result Actions Styles */
.result-actions {
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

.feedback-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: nowrap;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.feedback-btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.35rem;
  border: 1px solid #dee2e6;
  background: white;
  color: #6c757d;
  transition: all 0.2s;
  border-radius: 3px;
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.feedback-btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.feedback-btn.active {
  background: #007bff;
  border-color: #007bff;
  color: white;
}

.feedback-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Correction Summary Styles */
.correction-summary {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.correction-summary .badge-sm {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
}

/* Enhanced feedback button styles for partial feedback */
.feedback-btn.active[title*="Partial"] {
  background: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.feedback-btn[title*="Partial"]:hover {
  background: #e0a800;
  border-color: #d39e00;
}

/* END HITL Override Modal Styles */

/* Requirement Result Header Row */
.result-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.req-code-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
}

.quick-actions {
  display: flex;
  gap: 0.375rem;
  align-items: center;
}

.btn-icon {
  padding: 0.375rem 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.btn-icon:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-edit {
  color: #3b82f6;
  border-color: #3b82f6;
}

.btn-edit:hover {
  background: #eff6ff;
  border-color: #2563eb;
}

.btn-enhance {
  color: #8b5cf6;
  border-color: #8b5cf6;
}

.btn-enhance:hover {
  background: #f5f3ff;
  border-color: #7c3aed;
}

/* Classification Display Styles */
.classification-progress {
  margin-top: 1rem;
  padding: 1rem;
  background: #e7f3ff;
  border-left: 4px solid #2196f3;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.classification-progress i {
  color: #2196f3;
  font-size: 1.25rem;
}

.classification-result {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-left: 4px solid #4caf50;
  border-radius: 4px;
}

.classification-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #2e7d32;
}

.classification-header i {
  font-size: 1.25rem;
}

.classification-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.doc-type-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-right: 0.5rem;
}

.confidence-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.confidence-high {
  background: #d4edda;
  color: #155724;
}

.confidence-medium {
  background: #fff3cd;
  color: #856404;
}

.confidence-low {
  background: #f8d7da;
  color: #721c24;
}

.coverage-info {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #495057;
}

/* Policy Context Options for Temporal Analysis */
.policy-context-options {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.policy-context-options .form-group {
  margin-bottom: 0;
}

.policy-context-options .form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.policy-context-options .form-label i {
  color: #6c757d;
}

.policy-context-options .help-tooltip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #e9ecef;
  border-radius: 50%;
  font-size: 12px;
  color: #6c757d;
  cursor: help;
  font-weight: normal;
}

.reference-date-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.reference-date-input input[type="date"] {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.875rem;
  flex: 1;
  max-width: 200px;
}

.reference-date-input input[type="date"]:focus {
  border-color: #2563eb;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.reference-date-input .clear-date {
  padding: 0.375rem 0.5rem;
  font-size: 0.75rem;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
}

.reference-date-input .clear-date:hover {
  background: #f8f9fa;
  color: #495057;
}

.policy-context-options .form-text {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  color: #6c757d;
}

.selection-summary {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e7f3ff;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #0d47a1;
  font-weight: 500;
}

/* === PR1: Notification transparency panel === */
.notification-panel {
  margin: 1rem 0 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.notification-panel--sent {
  border-color: #86efac;
  background: #f0fdf4;
}

.notification-panel--info {
  border-color: #bae6fd;
  background: #f0f9ff;
}

.notification-panel--pending {
  border-color: #fcd34d;
  background: #fffbeb;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.notification-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
}

.notification-title i {
  color: #475569;
}

.notification-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
}

.notification-status.sent {
  background: #dcfce7;
  color: #166534;
}

.notification-status.info {
  background: #e0f2fe;
  color: #075985;
}

.notification-status.pending {
  background: #fef3c7;
  color: #92400e;
}

.notification-recipient {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.625rem 0.875rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #1e293b;
}

.notification-recipient.no-recipient {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.recipient-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.recipient-email {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace;
  background: #f1f5f9;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  color: #1e293b;
}

.recipient-source {
  font-size: 0.75rem;
  color: #64748b;
}

.recipient-contact {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.notification-reasons {
  font-size: 0.8125rem;
  color: #475569;
  margin-bottom: 0.75rem;
}

.notification-reasons ul {
  margin: 0.25rem 0 0 1.25rem;
  padding: 0;
}

.notification-reasons li {
  margin: 0.125rem 0;
}

.notification-history {
  font-size: 0.8125rem;
  color: #475569;
  margin-bottom: 0.75rem;
}

.notification-history ul {
  margin: 0.25rem 0 0 1.25rem;
  padding: 0;
  list-style: none;
}

.notification-history li {
  margin: 0.25rem 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
}

.notification-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.notification-actions .btn-sm {
  padding: 0.4rem 0.875rem;
  font-size: 0.8125rem;
}

.notification-actions .btn-outline {
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.notification-actions .btn-outline:hover:not(:disabled) {
  background: #f1f5f9;
}

.notification-actions .btn-primary:disabled,
.notification-actions .btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notification-history .text-success { color: #10b981; }
.notification-history .text-danger { color: #ef4444; }

/* PR2: Send drawer (override To/CC, pick contact) */
.send-drawer {
  margin-top: 0.75rem;
  padding: 1rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}

.send-drawer .drawer-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #1e293b;
  font-size: 0.9375rem;
}

.send-drawer .drawer-header i { color: #3b82f6; }

.send-drawer .drawer-row {
  margin-bottom: 0.75rem;
}

.send-drawer .drawer-row label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.25rem;
}

.send-drawer .drawer-row .req { color: #ef4444; }

.send-drawer .form-control {
  width: 100%;
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: white;
}

.send-drawer .form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.send-drawer .cc-input-row {
  display: flex;
  gap: 0.5rem;
}

.send-drawer .cc-input-row .form-control { flex: 1; }

.send-drawer .cc-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
}

.send-drawer .cc-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: #e0f2fe;
  color: #0c4a6e;
  border-radius: 999px;
  font-size: 0.8125rem;
}

.send-drawer .cc-remove {
  background: transparent;
  border: 0;
  cursor: pointer;
  color: #0369a1;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.send-drawer .cc-remove:hover { color: #0c4a6e; }

.send-drawer .cc-hint {
  display: block;
  margin-top: 0.375rem;
  color: #64748b;
  font-size: 0.75rem;
}

.send-drawer .drawer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.history-trigger {
  display: inline-block;
  padding: 0 0.375rem;
  border-radius: 3px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.history-trigger--auto {
  background: #fef3c7;
  color: #92400e;
}

.history-trigger--manual {
  background: #dbeafe;
  color: #1e40af;
}

</style> 